import * as THREE from 'three';
import { createTerrainOps } from './world-terrain.js';
import { createStructureOps } from './world-structures.js';
import { WORLD_GEN_PROFILE } from './config/world-gen-profile.js';
import { createFluidState } from './world/fluid-state.js';
import { createFluidVisuals } from './world/fluid-visuals.js';
import { createFluidSimulation } from './world/fluid-sim.js';
import { rand, lerp, smooth, clamp } from './utils/math-helpers.js';
import {
  hash2i as _hash2i,
  hash3i as _hash3i,
  valueNoise2 as _valueNoise2,
  valueNoise3 as _valueNoise3,
  fbm2 as _fbm2,
} from './world/noise.js';

export function createWorld(scene, renderer, options = {}) {
  const DEFAULT_BUJI_TEXTURE_DIR = '../assets/textures/buji/';
  const DEFAULT_INDUSTRIAL_TEXTURE_DIR = '../assets/textures/minimal_industrial/';
  const DEFAULT_ECO_TEXTURE_DIR = '../assets/textures/eco/';
  const columnPrefetch = options?.columnPrefetch || null;
  const onCactusConsumeDrop = typeof options?.onCactusConsumeDrop === 'function'
    ? options.onCactusConsumeDrop
    : null;
  const rawTextureStyle = options?.textureStyle ?? 'classic';
  const bujiTextureBaseUrl = typeof options?.bujiTextureBaseUrl === 'string' && options.bujiTextureBaseUrl.trim()
    ? options.bujiTextureBaseUrl
    : new URL(DEFAULT_BUJI_TEXTURE_DIR, import.meta.url).href;
  const industrialTextureBaseUrl = typeof options?.industrialTextureBaseUrl === 'string' && options.industrialTextureBaseUrl.trim()
    ? options.industrialTextureBaseUrl
    : new URL(DEFAULT_INDUSTRIAL_TEXTURE_DIR, import.meta.url).href;
  const ecoTextureBaseUrl = typeof options?.ecoTextureBaseUrl === 'string' && options.ecoTextureBaseUrl.trim()
    ? options.ecoTextureBaseUrl
    : new URL(DEFAULT_ECO_TEXTURE_DIR, import.meta.url).href;
  const CHUNK_SIZE = 16;
  const VIEW_DISTANCE = 2;
  const MIN_VIEW_DISTANCE = 1;
  const MAX_VIEW_DISTANCE = 16;
  const CHUNK_VISIBILITY_ALWAYS_RING = 1;
  const CHUNK_VISIBILITY_NEAR_DISTANCE = CHUNK_SIZE * 1.85;
  const CHUNK_VISIBILITY_RETAIN_SECONDS = 0.18;
  const CHUNK_VISIBILITY_RETAIN_MARGIN = CHUNK_SIZE * 0.75;
  const CHUNK_VISIBILITY_HYSTERESIS_DOT = 0.16;
  const DETAIL_VISIBILITY_ALWAYS_DISTANCE = CHUNK_SIZE * 0.95;
  const DETAIL_VISIBILITY_RETAIN_SECONDS = 0.12;
  const DETAIL_VISIBILITY_RETAIN_MARGIN = 2.5;
  const DETAIL_VISIBILITY_HYSTERESIS_DOT = 0.2;
  const INITIAL_RADIUS = 1;
  const CRITICAL_CHUNK_RADIUS = 1;
  const STREAM_SURFACE_PRIORITY_RADIUS = 2;
  const CRITICAL_BUILD_BUDGET_SCALE = 1.35;
  const CRITICAL_BUILD_BUDGET_MIN_EXTRA = 8;
  const CRITICAL_BUILD_BUDGET_MAX_EXTRA = 12;
  const MAX_CHUNKS_PER_FRAME = Math.max(1, Math.floor(Number(WORLD_GEN_PROFILE.maxChunksPerFrame) || 3));
  const CHUNK_BUILD_COLUMNS_PER_FRAME = Math.max(8, Math.floor(Number(WORLD_GEN_PROFILE.chunkBuildColumnsPerFrame) || 24));
  const SURFACE_COLUMN_DEPTH = Math.max(2, Math.floor(Number(WORLD_GEN_PROFILE.surfaceColumnDepth) || 8));
  const FULL_COLUMN_DEPTH = Math.max(SURFACE_COLUMN_DEPTH + 2, Math.floor(Number(WORLD_GEN_PROFILE.fullColumnDepth) || 30));
  const STREAM_EDGE_MARGIN = 6;
  const STREAM_DIR_EPSILON = 0.015;
  const WORLD_MIN_Y = Math.floor(Number(WORLD_GEN_PROFILE.worldMinY) || -22);
  const WATER_LEVEL = Math.floor(Number(WORLD_GEN_PROFILE.waterLevel) || 8);
  const STRUCTURE_DENSITY_COMPENSATION = Math.max(0.35, Number(WORLD_GEN_PROFILE.structureDensityCompensation) || 1);
  const BASELINE_SETTLEMENT_CELL_SIZE = 96;
  const BASELINE_POI_CELL_SIZE = 72;
  const BASELINE_SHIPWRECK_CELL_SIZE = 112;
  const BASELINE_SKY_FEATURE_CELL_SIZE = 88;
  function scaleStructureSpawnChance(baseChance, baselineCellSize, currentCellSize) {
    const base = clamp(Number(baseChance) || 0, 0.001, 0.999);
    const baseline = Math.max(8, Number(baselineCellSize) || 8);
    const current = Math.max(8, Number(currentCellSize) || baseline);
    const areaScale = Math.max(0.2, (current / baseline) ** 2);
    const effectiveTries = Math.max(0.2, areaScale * STRUCTURE_DENSITY_COMPENSATION);
    return clamp(1 - ((1 - base) ** effectiveTries), 0.02, 0.985);
  }
  const SETTLEMENT_CELL_SIZE = Math.max(24, Math.floor(Number(WORLD_GEN_PROFILE.settlementCellSize) || 192));
  const SETTLEMENT_BASE_CHANCE = scaleStructureSpawnChance(0.28, BASELINE_SETTLEMENT_CELL_SIZE, SETTLEMENT_CELL_SIZE);
  const POI_CELL_SIZE = Math.max(24, Math.floor(Number(WORLD_GEN_PROFILE.poiCellSize) || 144));
  const POI_BASE_CHANCE = scaleStructureSpawnChance(0.42, BASELINE_POI_CELL_SIZE, POI_CELL_SIZE);
  const SHIPWRECK_CELL_SIZE = Math.max(24, Math.floor(Number(WORLD_GEN_PROFILE.shipwreckCellSize) || 224));
  const SHIPWRECK_BASE_CHANCE = scaleStructureSpawnChance(0.22, BASELINE_SHIPWRECK_CELL_SIZE, SHIPWRECK_CELL_SIZE);
  const SKY_FEATURE_CELL_SIZE = Math.max(24, Math.floor(Number(WORLD_GEN_PROFILE.skyFeatureCellSize) || 176));
  const SKY_FEATURE_BASE_CHANCE = scaleStructureSpawnChance(0.28, BASELINE_SKY_FEATURE_CELL_SIZE, SKY_FEATURE_CELL_SIZE);
  const NETHER_X_OFFSET = 200000;
  const END_X_OFFSET = 400000;
  const NETHER_X_THRESHOLD = NETHER_X_OFFSET * 0.5;
  const END_X_THRESHOLD = (NETHER_X_OFFSET + END_X_OFFSET) * 0.5;
  const WORLD_SEED = Number.isFinite(Number(options?.worldSeed))
    ? (Math.trunc(Number(options.worldSeed)) | 0)
    : (Math.trunc(Number(WORLD_GEN_PROFILE.worldSeed) || 0) | 0);

  const WATER_SOURCE_LEVEL = 7;
  const WATER_FALL_LEVEL = 8;
  const WATER_MIN_FLOW_LEVEL = 1;
  const WATER_TICK_INTERVAL = 0.28;
  const WATER_MAX_TICKS_PER_FRAME = 1;
  const WATER_DIRTY_BUDGET_PER_TICK = 256;
  const WATER_SEED_SCAN_BUDGET_PER_FRAME = 72;
  const LAVA_SOURCE_LEVEL = 4;
  const LAVA_MIN_FLOW_LEVEL = 1;
  const LAVA_MAX_LATERAL_SPREAD = 3;
  const LAVA_TICK_INTERVAL = 0.6;
  const LAVA_MAX_TICKS_PER_FRAME = 1;
  const LAVA_DIRTY_BUDGET_PER_TICK = 128;
  const LAVA_SEED_SCAN_BUDGET_PER_FRAME = 44;
  const FLUID_SEED_EDGE_MARGIN = 1;
  const WATER_VISUAL_LERP_SPEED = 10;
  const WATER_VISUAL_UPDATE_BUDGET_PER_FLUID_TICK = 220;
  const KELP_OCEAN_CHANCE = 0.22;
  const KELP_WETLAND_CHANCE = 0.3;
  const KELP_MAX_HEIGHT_SHALLOW = 3;
  const KELP_MAX_HEIGHT_DEEP = 2;
  const WATER_LEVEL_HEIGHT_BY_LEVEL = new Map([
    [8, 1.0],
    [7, 0.875],
    [6, 0.75],
    [5, 0.625],
    [4, 0.5],
    [3, 0.375],
    [2, 0.25],
    [1, 0.125]
  ]);
  const LAVA_LEVEL_HEIGHT_BY_LEVEL = new Map([
    [4, 1.0],
    [3, 0.78],
    [2, 0.56],
    [1, 0.34]
  ]);
  const WATER_DIRTY_OFFSETS = [
    [0, 0, 0],
    [1, 0, 0],
    [-1, 0, 0],
    [0, 1, 0],
    [0, -1, 0],
    [0, 0, 1],
    [0, 0, -1]
  ];
  const WATER_LATERAL_OFFSETS = [
    [1, 0, 0],
    [-1, 0, 0],
    [0, 0, 1],
    [0, 0, -1]
  ];
  const blockGeo = new THREE.BoxGeometry(1, 1, 1);
  function createWaterLevelGeo(height = 1) {
    const h = clamp(Number(height) || 1, 0.18, 1);
    if (h >= 0.999) return blockGeo;
    const geo = new THREE.BoxGeometry(1, h, 1);
    geo.translate(0, (h - 1) * 0.5, 0);
    return geo;
  }
  const WATER_LEVEL_GEOS = new Map(
    Array.from(WATER_LEVEL_HEIGHT_BY_LEVEL.entries(), ([level, height]) => [level, createWaterLevelGeo(height)])
  );
  const LAVA_LEVEL_GEOS = new Map(
    Array.from(LAVA_LEVEL_HEIGHT_BY_LEVEL.entries(), ([level, height]) => [level, createWaterLevelGeo(height)])
  );
  const dropGeo = new THREE.BoxGeometry(0.25, 0.25, 0.25);
  const dropXpGeo = new THREE.SphereGeometry(0.04, 8, 6);
  const torchGeo = new THREE.CylinderGeometry(0.08, 0.12, 0.72, 8);
  torchGeo.translate(0, -0.14, 0);
  const doorClosedGeo = new THREE.BoxGeometry(0.14, 1.0, 1.0);
  doorClosedGeo.translate(0.43, 0, 0);
  const doorOpenGeo = new THREE.BoxGeometry(1.0, 1.0, 0.14);
  doorOpenGeo.translate(0, 0, 0.43);
  const trapdoorClosedGeo = new THREE.BoxGeometry(1.0, 0.14, 1.0);
  trapdoorClosedGeo.translate(0, -0.43, 0);
  const trapdoorOpenGeo = new THREE.BoxGeometry(1.0, 1.0, 0.14);
  trapdoorOpenGeo.translate(0, 0, 0.43);
  const fenceGeo = new THREE.BoxGeometry(0.4, 1.0, 0.4);
  const farmlandGeo = new THREE.BoxGeometry(1.0, 0.88, 1.0);
  farmlandGeo.translate(0, -0.06, 0);
  const cropGeoStage1 = new THREE.BoxGeometry(0.72, 0.28, 0.72);
  cropGeoStage1.translate(0, -0.36, 0);
  const cropGeoStage2 = new THREE.BoxGeometry(0.76, 0.46, 0.76);
  cropGeoStage2.translate(0, -0.27, 0);
  const cropGeoStage3 = new THREE.BoxGeometry(0.8, 0.66, 0.8);
  cropGeoStage3.translate(0, -0.17, 0);
  const cropGeoStage4 = new THREE.BoxGeometry(0.84, 0.86, 0.84);
  cropGeoStage4.translate(0, -0.07, 0);
  const saplingGeo = new THREE.BoxGeometry(0.42, 0.7, 0.42);
  saplingGeo.translate(0, -0.15, 0);
  const berryBushGeo = new THREE.BoxGeometry(0.76, 0.66, 0.76);
  berryBushGeo.translate(0, -0.17, 0);
  const blueberryBushGeo = new THREE.BoxGeometry(0.72, 0.62, 0.72);
  blueberryBushGeo.translate(0, -0.19, 0);
  const leverGeo = new THREE.BoxGeometry(0.3, 0.3, 0.3);
  leverGeo.translate(0, -0.35, 0);
  const wireGeo = new THREE.BoxGeometry(0.98, 0.08, 0.98);
  wireGeo.translate(0, -0.46, 0);
  const fireGeo = new THREE.BoxGeometry(0.78, 0.9, 0.78);
  fireGeo.translate(0, -0.05, 0);
  const solidBlocks = new Map();
  const culledBlocks = new Map();
  const instancedBlockBucketsByChunk = new Map();
  const instancedBlockIndexByKey = new Map();
  const instancedBlockMetaByKey = new Map();
  const instancedMeshBucket = new WeakMap();
  const saplingKeys = new Set();
  const cactusKeys = new Set();
  const objects = [];
  const objectSet = new Set();
  const objectIndexByUuid = new Map();
  const dynamicRaycastObjects = new Set();
  const raycastScratch = [];
  const dynamicRaycastScratch = [];
  const drops = [];
  const loadedChunks = new Map();
  const chunkQueue = [];
  const queuedChunkKeys = new Set();
  const chunkBuildQueue = [];
  const chunkBuildQueuedKeys = new Set();
  const pendingCullChunkQueue = [];
  const pendingCullChunkKeys = new Set();
  const manualPlaced = new Map();
  const manualByChunk = new Map();
  const manualRemoved = new Set();
  const settlementCache = new Map();
  const poiCache = new Map();
  const shipwreckCache = new Map();
  const skyFeatureCache = new Map();
  const pendingLeafDecay = new Set();
  const fireExpireAt = new Map();
  const fallingBlocks = [];

  // Keep dynamic lights capped so torch-heavy bases don't tank frame time.
  const MAX_ACTIVE_POINT_LIGHTS = 24;
  const activeLightMeshes = [];
  const deferredLightMeshes = [];
  let lightBudgetDirty = true;
  const _lightDistScratch = new THREE.Vector3();

  const LEAF_DECAY_RADIUS = 4;
  const LEAF_DECAY_BATCH = 12;
  const LEAF_DECAY_INTERVAL = 0.32;
  const LEAF_SAPLING_DROP_CHANCE = 0.1;
  const LEAF_APPLE_DROP_CHANCE = 0.04;
  const FIRE_SPREAD_INTERVAL = 0.34;
  const FIRE_SPREAD_IGNITE_CHANCE = 0.32;
  const FIRE_SPREAD_CONSUME_CHANCE = 0.14;
  const LAVA_IGNITE_SCAN_PER_SPREAD = 64;
  const LAVA_IGNITE_CHANCE = 0.24;
  const FLAMMABLE_BLOCK_IDS = new Set([
    3, 4, 6, 11, 14, 15, 16, 17, 18, 19, 20,
    50, 51, 52, 53, 65, 66, 67, 68,
    78, 79, 80, 81, 82, 83, 84, 85, 86,
    87, 88, 89, 90, 91, 92, 93, 94, 95
  ]);
  const GRAVITY_BLOCK_IDS = new Set([26, 27]);
  const INSTANCED_CUSTOM_GEOMETRY_BLOCK_IDS = new Set([
    12,
    20,
    49,
    50, 51, 52, 53,
    58, 59,
    60, 61,
    64,
    65, 66, 67,
    76
  ]);
  const RUNTIME_POINT_LIGHT_BLOCK_IDS = new Set([
    12,
    38,
    63,
    64,
    69
  ]);
  const INSTANCED_NON_SOLID_BLOCK_IDS = new Set([
    12,
    43, 46,
    50, 51, 52, 53,
    58, 59,
    60, 61,
    64,
    73,
    65, 66, 67,
    76
  ]);

  const DROP_HALF_SIZE = 0.125;
  const DROP_HALF_SIZE_XP = 0.04;
  const DROP_GROUND_OFFSET = 0.5 + DROP_HALF_SIZE;
  const DROP_GRAVITY = 19.5;
  const DROP_TERMINAL_VEL = 18;
  const STATIC_INSTANCE_MIN_CAPACITY = 48;
  const MAX_ACTIVE_DROPS = 480;
  const DROP_POOL_LIMIT = 320;
  const DROP_STACK_LIMITS = Object.freeze({
    block: 12,
    item: 16,
    xp: 24
  });
  const DROP_STACK_MERGE_RADIUS_SQ = 0.58 * 0.58;
  const DROP_MERGE_SCAN_LIMIT = 96;
  const DROP_SLEEP_DISTANCE_SQ = 18 * 18;
  const DROP_WAKE_DISTANCE_SQ = 9 * 9;
  const DROP_SLEEP_VERTICAL_RANGE = 5.5;
  const DROP_SLEEP_VELOCITY_EPSILON = 0.06;
  const DROP_SLEEP_SPIN_EPSILON = 0.2;
  const MAX_TEXTURE_ANISOTROPY = 4;
  const CHUNK_RAYCAST_REPAIR_COOLDOWN_MS = 260;
  const SAPLING_BLOCK_ID = 65;
  const CACTUS_BLOCK_ID = 72;
  const PLAYER_DISCARD_DROP_SOURCE = 'player_discard';
  const DETAIL_CULL_DISTANCE_BY_BLOCK_ID = new Map([
    [4, 38],
    [12, 20],
    [16, 28],
    [17, 26],
    [18, 28],
    [19, 26],
    [20, 26],
    [49, 22],
    [50, 20],
    [51, 20],
    [52, 20],
    [53, 20],
    [58, 18],
    [59, 18],
    [60, 18],
    [61, 18],
    [64, 16],
    [65, 20],
    [66, 22],
    [67, 22],
    [73, 18],
    [76, 18]
  ]);
  const INSTANCED_PROXY_EXTRA_FIELDS = [
    'treeRootX',
    'treeRootY',
    'treeRootZ',
    'treeTrunkTopY',
    'treeTrunkRadius',
    'treeTrunkBlockId',
    'treeSpecies',
    'leafDecayEligible'
  ];
  const MANUAL_BLOCK_OPTION_FIELDS = [
    'leafVariant',
    'leafDecayEligible',
    'treeRootX',
    'treeRootY',
    'treeRootZ',
    'treeTrunkTopY',
    'treeTrunkRadius',
    'treeTrunkBlockId',
    'treeSpecies'
  ];
  const SAPLING_GROWTH_INTERVAL = 0.95;
  const SAPLING_GROWTH_BATCH = 6;
  const SAPLING_GROWTH_MIN_SECONDS = 35;
  const SAPLING_GROWTH_MAX_SECONDS = 80;
  const SAPLING_RESYNC_INTERVAL = 2.4;
  const SAPLING_RESYNC_RADIUS = 9;
  const SAPLING_RESYNC_Y_DOWN = 4;
  const SAPLING_RESYNC_Y_UP = 12;
  const CACTUS_GROWTH_INTERVAL = 0.6;
  const CACTUS_GROWTH_BATCH = 8;
  const CACTUS_GROWTH_MIN_SECONDS = 70;
  const CACTUS_GROWTH_MAX_SECONDS = 130;
  const CACTUS_TRASH_TO_GROW = 3;
  const CACTUS_MAX_HEIGHT = 3;
  const CACTUS_TRASH_RADIUS = 1.18;
  const CACTUS_TRASH_VERTICAL_MARGIN = 0.95;
  const CACTUS_MAX_TRASH_PER_TICK = 3;
  const textureAnisotropy = Math.max(1, Math.min(renderer.capabilities.getMaxAnisotropy(), MAX_TEXTURE_ANISOTROPY));
  const LAVA_IGNITE_OFFSETS = [
    [1, 0, 0], [-1, 0, 0],
    [0, 1, 0], [0, -1, 0],
    [0, 0, 1], [0, 0, -1]
  ];
  const FIRE_SPREAD_BASE_OFFSETS = [
    [0, -1, 0],
    [1, -1, 0],
    [-1, -1, 0],
    [0, -1, 1],
    [0, -1, -1],
    [0, 0, 0],
    [1, 0, 0],
    [-1, 0, 0],
    [0, 0, 1],
    [0, 0, -1]
  ];

  const dropTarget = new THREE.Vector3();
  const instancedMatrixScratch = new THREE.Matrix4();
  const instancedPositionScratch = new THREE.Vector3();
  const instancedInteriorPointScratch = new THREE.Vector3();
  const dropPool = [];
  let leafDecayTimer = 0;
  let saplingGrowthTimer = 0;
  let saplingGrowthCursor = 0;
  const saplingGrowthProgress = new Map();
  let saplingResyncTimer = 0;
  let cactusGrowthTimer = 0;
  let cactusGrowthCursor = 0;
  const cactusGrowthProgress = new Map();
  const cactusTrashProgress = new Map();
  const fluidState = createFluidState({
    viewDistance: VIEW_DISTANCE,
    waterSeedScanBudgetPerFrame: WATER_SEED_SCAN_BUDGET_PER_FRAME,
    lavaSeedScanBudgetPerFrame: LAVA_SEED_SCAN_BUDGET_PER_FRAME,
    waterDirtyBudgetPerTick: WATER_DIRTY_BUDGET_PER_TICK,
    lavaDirtyBudgetPerTick: LAVA_DIRTY_BUDGET_PER_TICK,
    waterVisualUpdateBudgetPerFluidTick: WATER_VISUAL_UPDATE_BUDGET_PER_FLUID_TICK
  });
  const {
    waterMap,
    lavaMap,
    lavaSourceSet,
    pendingWaterSeedKeys,
    pendingLavaSeedKeys,
    pendingWaterSeedSet,
    pendingLavaSeedSet,
    animatingWaterMeshes
  } = fluidState;
  let fireSpreadAccumulator = 0;
  let lavaIgniteCursor = 0;
  const initialViewDistanceRaw = Number(options?.viewDistance);
  let activeViewDistance = Number.isFinite(initialViewDistanceRaw)
    ? clamp(Math.floor(initialViewDistanceRaw), MIN_VIEW_DISTANCE, MAX_VIEW_DISTANCE)
    : VIEW_DISTANCE;
  let activeUnloadDistance = activeViewDistance + 1;
  let lastPrefetchCacheChunks = Number.NaN;
  let lastPrefetchInFlight = Number.NaN;

  let centerChunkX = Number.NaN;
  let centerChunkZ = Number.NaN;
  let lastStreamSampleX = Number.NaN;
  let lastStreamSampleZ = Number.NaN;
  let chunkVisibilityTimer = 0;
  let lastVisibilitySampleX = Number.NaN;
  let lastVisibilitySampleZ = Number.NaN;
  let lastVisibilityForwardX = Number.NaN;
  let lastVisibilityForwardZ = Number.NaN;

  const _BK_Y_RANGE = 1024;
  const _BK_Z_RANGE = 1000001;
  const _BK_Y_OFF = 512;
  const _BK_XZ_OFF = 500001;
  const _CK_RANGE = 1000001;
  const _CK_OFF = 500001;

  function blockKey(x, y, z) {
    return ((x + _BK_XZ_OFF) * _BK_Z_RANGE + (z + _BK_XZ_OFF)) * _BK_Y_RANGE + (y + _BK_Y_OFF);
  }

  function blockKeyReverse(key) {
    const yOff = key % _BK_Y_RANGE;
    const rem = (key - yOff) / _BK_Y_RANGE;
    const zOff = rem % _BK_Z_RANGE;
    const xOff = (rem - zOff) / _BK_Z_RANGE;
    return { x: xOff - _BK_XZ_OFF, y: yOff - _BK_Y_OFF, z: zOff - _BK_XZ_OFF };
  }

  function chunkKey(cx, cz) {
    return (cx + _CK_OFF) * _CK_RANGE + (cz + _CK_OFF);
  }

  function worldToBlock(v) {
    return Math.floor(v + 0.5);
  }

  function worldToChunk(v) {
    return Math.floor(v / CHUNK_SIZE);
  }

  function normalizeViewDistance(rawValue) {
    const parsed = Math.floor(Number(rawValue));
    if (!Number.isFinite(parsed)) return activeViewDistance;
    return clamp(parsed, MIN_VIEW_DISTANCE, MAX_VIEW_DISTANCE);
  }

  function getDimensionAtCoord(x, _z = 0) {
    if (x >= END_X_THRESHOLD) return 'end';
    if (x >= NETHER_X_THRESHOLD) return 'nether';
    return 'overworld';
  }

  function toDimensionX(localX, dimension) {
    if (dimension === 'nether') return localX + NETHER_X_OFFSET;
    if (dimension === 'end') return localX + END_X_OFFSET;
    return localX;
  }

  function toLocalX(worldX, dimension) {
    if (dimension === 'nether') return worldX - NETHER_X_OFFSET;
    if (dimension === 'end') return worldX - END_X_OFFSET;
    return worldX;
  }

  function hash2i(x, z, seed = 0) { return _hash2i(x, z, seed, WORLD_SEED); }
  function hash3i(x, y, z, seed = 0) { return _hash3i(x, y, z, seed, WORLD_SEED); }

  function setBoundedCache(cache, key, value, maxSize = 4096) {
    if (cache.has(key)) cache.delete(key);
    cache.set(key, value);
    if (cache.size <= maxSize) return;
    const firstKey = cache.keys().next().value;
    if (firstKey !== undefined) cache.delete(firstKey);
  }

  function valueNoise2(x, z, freq, seed = 0) { return _valueNoise2(x, z, freq, seed, WORLD_SEED); }
  function valueNoise3(x, y, z, freq, seed = 0) { return _valueNoise3(x, y, z, freq, seed, WORLD_SEED); }
  function fbm2(x, z, opts) { return _fbm2(x, z, opts, WORLD_SEED); }

  const {
    columnNoise,
    netherColumnNoise,
    endColumnNoise,
    chooseStoneType,
    shouldCarveCave,
    shouldCarveSurfaceRavine,
    shouldCarveNetherCave,
    shouldCarveEndVoid
  } = createTerrainOps({
    WORLD_MIN_Y,
    WATER_LEVEL,
    clamp,
    smooth,
    fbm2,
    valueNoise2,
    valueNoise3,
    toLocalX,
    ecosystemScale: Number(WORLD_GEN_PROFILE.ecosystemScale) || 1,
    terrainVerticalScale: Number(WORLD_GEN_PROFILE.terrainVerticalScale) || 1,
    undergroundDepthScale: Number(WORLD_GEN_PROFILE.undergroundDepthScale) || 1
  });

  function createDetailedTexture(baseColorHex, opts = {}) {
    const { size = 192, grain = 3600, grainStrength = 0.24, streaks = 0 } = opts;
    const c = document.createElement('canvas');
    c.width = size;
    c.height = size;
    const ctx = c.getContext('2d');
    ctx.fillStyle = baseColorHex;
    ctx.fillRect(0, 0, size, size);

    for (let i = 0; i < grain; i++) {
      const x = Math.random() * size;
      const y = Math.random() * size;
      const dark = Math.random() < 0.55;
      const alpha = Math.random() * grainStrength;
      ctx.fillStyle = dark ? `rgba(0,0,0,${alpha})` : `rgba(255,255,255,${alpha * 0.75})`;
      const w = Math.floor(rand(1, 4));
      const h = Math.floor(rand(1, 4));
      ctx.fillRect(x, y, w, h);
    }

    for (let i = 0; i < streaks; i++) {
      ctx.strokeStyle = `rgba(0,0,0,${rand(0.05, 0.15)})`;
      ctx.lineWidth = rand(1, 2.2);
      const y = rand(0, size);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(size, y + rand(-9, 9));
      ctx.stroke();
    }

    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.anisotropy = textureAnisotropy;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = true;
    return tex;
  }

  const TEXTURE_STYLE_CLASSIC = 'classic';
  const TEXTURE_STYLE_BUJI = 'buji';
  const TEXTURE_STYLE_INDUSTRIAL = 'industrial';
  const TEXTURE_STYLE_ECO = 'eco';
  const TEXTURE_STYLE_OPTIONS = Object.freeze([TEXTURE_STYLE_CLASSIC, TEXTURE_STYLE_BUJI, TEXTURE_STYLE_INDUSTRIAL, TEXTURE_STYLE_ECO]);
  const THEMED_TEXTURE_FILE_BY_BLOCK_ID = new Map([
    [1, 'grass.png'],
    [2, 'dirt.png'],
    [3, 'log.png'],
    [5, 'stone.png'],
    [6, 'planks.png'],
    [78, 'log.png'],
    [79, 'log.png'],
    [80, 'log.png'],
    [81, 'log.png'],
    [82, 'log.png'],
    [83, 'log.png'],
    [84, 'log.png'],
    [85, 'log.png'],
    [86, 'log.png'],
    [87, 'planks.png'],
    [88, 'planks.png'],
    [89, 'planks.png'],
    [90, 'planks.png'],
    [91, 'planks.png'],
    [92, 'planks.png'],
    [93, 'planks.png'],
    [94, 'planks.png'],
    [95, 'planks.png'],
    [7, 'water.png'],
    [8, 'iron_ore.png'],
    [9, 'coal_ore.png'],
    [10, 'diamond_ore.png'],
    [11, 'crafting_table.png'],
    [13, 'furnace.png'],
    [14, 'chest.png'],
    [15, 'bed.png'],
    [16, 'oak_door.png'],
    [17, 'oak_door_open.png'],
    [18, 'trapdoor.png'],
    [19, 'trapdoor_open.png'],
    [20, 'fence.png'],
    [21, 'sandstone.png'],
    [22, 'bricks.png'],
    [23, 'glass.png'],
    [24, 'white_concrete.png'],
    [25, 'asphalt.png'],
    [26, 'sand.png'],
    [27, 'gravel.png'],
    [28, 'cobblestone.png'],
    [29, 'stone_bricks.png'],
    [30, 'smooth_stone.png'],
    [31, 'obsidian.png'],
    [32, 'mossy_cobblestone.png'],
    [33, 'chiseled_stone_bricks.png'],
    [34, 'cracked_stone_bricks.png'],
    [35, 'coal_block.png'],
    [36, 'iron_block.png'],
    [37, 'diamond_block.png'],
    [38, 'glowstone.png'],
    [39, 'quartz_block.png'],
    [40, 'netherrack.png'],
    [41, 'soul_sand.png'],
    [42, 'nether_bricks.png'],
    [43, 'nether_portal.png'],
    [44, 'end_stone.png'],
    [45, 'purpur_block.png'],
    [46, 'end_portal.png'],
    [47, 'gold_ore.png'],
    [48, 'gold_block.png'],
    [49, 'farmland.png'],
    [62, 'redstone_lamp.png'],
    [63, 'redstone_lamp_on.png'],
    [68, 'hay_bale.png'],
    [69, 'lava.png'],
    [70, 'snow_block.png'],
    [71, 'ice.png'],
    [72, 'cactus.png'],
    [73, 'kelp.png'],
    [74, 'basalt.png'],
    [75, 'volcanic_rock.png'],
    [77, 'bricks.png']
  ]);
  const textureBaseUrlByStyle = new Map([
    [TEXTURE_STYLE_BUJI, bujiTextureBaseUrl],
    [TEXTURE_STYLE_INDUSTRIAL, industrialTextureBaseUrl],
    [TEXTURE_STYLE_ECO, ecoTextureBaseUrl]
  ]);
  const texturePackMapByStyle = new Map();
  const texturePackLoadPromiseByStyle = new Map();
  const classicTextureMapByBlockId = new Map();
  let activeTextureStyle = TEXTURE_STYLE_CLASSIC;
  let requestedTextureStyle = TEXTURE_STYLE_CLASSIC;

  function normalizeTextureStyle(styleId) {
    const style = String(styleId || '').trim().toLowerCase();
    if (!style) return TEXTURE_STYLE_BUJI;
    if (style === TEXTURE_STYLE_CLASSIC || style === 'vanilla' || style === 'default' || style === 'none' || style === 'off') {
      return TEXTURE_STYLE_CLASSIC;
    }
    if (style === TEXTURE_STYLE_BUJI || style === 'unrestrained' || style === 'buji' || style === '不羈' || style === '不羁') {
      return TEXTURE_STYLE_BUJI;
    }
    if (style === TEXTURE_STYLE_INDUSTRIAL || style === 'minimal_industrial' || style === 'industrial_minimal' || style === '極簡工業') {
      return TEXTURE_STYLE_INDUSTRIAL;
    }
    if (style === TEXTURE_STYLE_ECO || style === 'eco_vivid' || style === 'biome' || style === 'biome_vivid' || style === '生態' || style === '繽紛') {
      return TEXTURE_STYLE_ECO;
    }
    return TEXTURE_STYLE_CLASSIC;
  }

  function configureBlockTextureMap(tex) {
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.anisotropy = textureAnisotropy;
    tex.minFilter = THREE.NearestMipmapNearestFilter;
    tex.magFilter = THREE.NearestFilter;
    tex.generateMipmaps = true;
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;
    return tex;
  }

  function resolveTextureFileUrl(fileName, baseUrl, styleId = TEXTURE_STYLE_BUJI) {
    try {
      return new URL(fileName, baseUrl).href;
    } catch {
      const fallbackBase = textureBaseUrlByStyle.get(styleId) || new URL(DEFAULT_BUJI_TEXTURE_DIR, import.meta.url).href;
      return new URL(fileName, fallbackBase).href;
    }
  }

  function loadTexturePackMap(styleId) {
    if (styleId !== TEXTURE_STYLE_BUJI && styleId !== TEXTURE_STYLE_INDUSTRIAL && styleId !== TEXTURE_STYLE_ECO) {
      return Promise.resolve(new Map());
    }
    const baseUrl = textureBaseUrlByStyle.get(styleId);
    if (!baseUrl) return Promise.resolve(new Map());

    const loader = new THREE.TextureLoader();
    const loadTasks = [];
    for (const [id, fileName] of THEMED_TEXTURE_FILE_BY_BLOCK_ID.entries()) {
      const textureUrl = resolveTextureFileUrl(fileName, baseUrl, styleId);
      loadTasks.push(new Promise((resolve) => {
        loader.load(
          textureUrl,
          (loadedTexture) => {
            resolve([id, configureBlockTextureMap(loadedTexture)]);
          },
          undefined,
          () => resolve([id, null])
        );
      }));
    }

    return Promise.all(loadTasks).then((entries) => {
      const mapById = new Map();
      for (const [id, tex] of entries) {
        if (tex) mapById.set(id, tex);
      }
      return mapById;
    });
  }

  function ensureTextureStyleLoaded(styleId) {
    const style = normalizeTextureStyle(styleId);
    if (style === TEXTURE_STYLE_CLASSIC) return Promise.resolve(new Map());
    if (texturePackMapByStyle.has(style)) return Promise.resolve(texturePackMapByStyle.get(style));
    const loading = texturePackLoadPromiseByStyle.get(style);
    if (loading) return loading;

    const loadPromise = loadTexturePackMap(style)
      .then((loadedMap) => {
        if (loadedMap.size > 0) {
          texturePackMapByStyle.set(style, loadedMap);
        } else {
          texturePackMapByStyle.delete(style);
        }
        return loadedMap;
      })
      .catch(() => {
        texturePackMapByStyle.delete(style);
        return new Map();
      })
      .finally(() => {
        texturePackLoadPromiseByStyle.delete(style);
      });

    texturePackLoadPromiseByStyle.set(style, loadPromise);
    return loadPromise;
  }

  function applyTextureStyle(styleId) {
    const style = normalizeTextureStyle(styleId);
    const textureMapByBlockId = style === TEXTURE_STYLE_CLASSIC ? null : texturePackMapByStyle.get(style);

    for (const [id, defaultMap] of classicTextureMapByBlockId.entries()) {
      const mat = blockData[id]?.mat;
      if (!mat || !('map' in mat)) continue;
      const nextMap = textureMapByBlockId?.get(id) || defaultMap;
      if (mat.map === nextMap) continue;
      mat.map = nextMap;
      mat.needsUpdate = true;
    }

    activeTextureStyle = style;
    return activeTextureStyle;
  }

  function setTextureStyleInternal(styleId) {
    const style = normalizeTextureStyle(styleId);
    requestedTextureStyle = style;
    if (style === TEXTURE_STYLE_CLASSIC) {
      applyTextureStyle(style);
      return Promise.resolve({ requested: requestedTextureStyle, active: activeTextureStyle });
    }

    return ensureTextureStyleLoaded(style)
      .then((loadedMap) => {
        if (requestedTextureStyle === style) {
          if (loadedMap.size > 0) {
            applyTextureStyle(style);
          } else {
            requestedTextureStyle = TEXTURE_STYLE_CLASSIC;
            applyTextureStyle(TEXTURE_STYLE_CLASSIC);
          }
        }
        return { requested: requestedTextureStyle, active: activeTextureStyle };
      })
      .catch(() => {
        requestedTextureStyle = TEXTURE_STYLE_CLASSIC;
        applyTextureStyle(TEXTURE_STYLE_CLASSIC);
        return { requested: requestedTextureStyle, active: activeTextureStyle };
      });
  }

  function createLeafTexture(opts = {}) {
    const {
      size = 64,
      blobs = 18,
      shadows = 180,
      cutouts = 10
    } = opts;

    const c = document.createElement('canvas');
    c.width = size;
    c.height = size;
    const ctx = c.getContext('2d');

    ctx.fillStyle = '#2d7a1c';
    ctx.fillRect(0, 0, size, size);

    for (let i = 0; i < blobs; i++) {
      const x = Math.random() * size;
      const y = Math.random() * size;
      const r = rand(3.2, 8.8);
      ctx.fillStyle = Math.random() > 0.5 ? '#1a5e0e' : '#3d9e2a';
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }

    for (let i = 0; i < shadows; i++) {
      const alpha = rand(0.03, 0.2);
      const px = Math.floor(Math.random() * size);
      const py = Math.floor(Math.random() * size);
      const d = Math.random() < 0.18 ? 2 : 1;
      ctx.fillStyle = `rgba(0,0,0,${alpha})`;
      ctx.fillRect(px, py, d, d);
    }

    for (let i = 0; i < cutouts; i++) {
      const d = Math.random() < 0.82 ? 2 : 3;
      const px = Math.floor(Math.random() * (size - d));
      const py = Math.floor(Math.random() * (size - d));
      ctx.clearRect(px, py, d, d);
    }

    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.anisotropy = Math.min(textureAnisotropy, 2);
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.generateMipmaps = true;
    return tex;
  }

  const leavesMap = createLeafTexture();
  const leafTintPalette = [
    '#c7f7ad', '#a9eb88', '#8cdc66', '#70c84f', '#54af3b',
    '#3f8f45', '#2f7040', '#55756d',
    '#e76845', '#e7a24a', '#f0d56f', '#c76a3d'
  ];
  const leavesMatVariants = leafTintPalette.map((tint) => new THREE.MeshStandardMaterial({
    map: leavesMap,
    color: new THREE.Color(tint),
    roughness: 0.82,
    metalness: 0.0,
    alphaTest: 0.34,
    side: THREE.FrontSide
  }));
  const leavesMat = leavesMatVariants[0];
  const LEAF_VARIANT_POOL_BY_THEME = Object.freeze({
    plains: [0, 1, 2, 3, 4],
    tropical: [1, 2, 3, 4, 5],
    mist: [2, 3, 4, 5, 6],
    autumn: [8, 9, 10, 11],
    alpine: [5, 6, 7]
  });
  const TREE_SPECIES_PROFILES = Object.freeze({
    camphor: Object.freeze({
      id: 'camphor',
      canopyStyle: 'round',
      minHeight: 4,
      maxHeight: 6,
      canopyRadius: 2,
      canopyHeight: 2,
      spacingRadius: 2,
      leafTheme: 'plains'
    }),
    banyan: Object.freeze({
      id: 'banyan',
      canopyStyle: 'spread',
      minHeight: 6,
      maxHeight: 8,
      canopyRadius: 3,
      canopyHeight: 2,
      spacingRadius: 3,
      aerialRoots: true,
      leafTheme: 'tropical'
    }),
    nanmu: Object.freeze({
      id: 'nanmu',
      canopyStyle: 'column',
      minHeight: 5,
      maxHeight: 7,
      canopyRadius: 2,
      canopyHeight: 3,
      spacingRadius: 2,
      leafTheme: 'tropical'
    }),
    birch: Object.freeze({
      id: 'birch',
      canopyStyle: 'light_round',
      minHeight: 5,
      maxHeight: 7,
      canopyRadius: 2,
      canopyHeight: 2,
      spacingRadius: 2,
      leafTheme: 'plains'
    }),
    cypress: Object.freeze({
      id: 'cypress',
      canopyStyle: 'conifer',
      minHeight: 8,
      maxHeight: 12,
      canopyRadius: 3,
      canopyHeight: 6,
      spacingRadius: 3,
      trunkRadius: 1,
      leafTheme: 'mist'
    }),
    maple: Object.freeze({
      id: 'maple',
      canopyStyle: 'round',
      minHeight: 5,
      maxHeight: 7,
      canopyRadius: 2,
      canopyHeight: 2,
      spacingRadius: 2,
      leafTheme: 'autumn'
    }),
    conifer: Object.freeze({
      id: 'conifer',
      canopyStyle: 'conifer',
      minHeight: 7,
      maxHeight: 11,
      canopyRadius: 3,
      canopyHeight: 6,
      spacingRadius: 3,
      leafTheme: 'alpine'
    }),
    juniper: Object.freeze({
      id: 'juniper',
      canopyStyle: 'sparse',
      minHeight: 4,
      maxHeight: 6,
      canopyRadius: 2,
      canopyHeight: 2,
      spacingRadius: 2,
      leanTrunk: true,
      leafTheme: 'alpine'
    }),
    fir: Object.freeze({
      id: 'fir',
      canopyStyle: 'spire',
      minHeight: 8,
      maxHeight: 13,
      canopyRadius: 2,
      canopyHeight: 7,
      spacingRadius: 3,
      leafTheme: 'alpine'
    })
  });
  const TREE_LOG_BLOCK_ID_BY_SPECIES = Object.freeze({
    camphor: 78,
    banyan: 79,
    nanmu: 80,
    birch: 81,
    cypress: 82,
    maple: 83,
    conifer: 84,
    juniper: 85,
    fir: 86
  });
  const TREE_LOG_BLOCK_ID_SET = new Set([3, ...Object.values(TREE_LOG_BLOCK_ID_BY_SPECIES)]);
  const TREE_SOIL_BLOCK_ID_SET = new Set([1, 2, 49]);
  const TREE_SPAWN_DENSITY_SCALE = 0.88;
  const FOREST_BERRY_ROLL_BONUS = 0.01;
  const WETLAND_BLUEBERRY_ROLL_BONUS = 0.01;
  const WETLAND_BERRY_ROLL_BONUS = 0.004;
  const LOWLAND_BERRY_ROLL_BONUS = 0.005;

  function isTreeLogBlockId(blockId) {
    return TREE_LOG_BLOCK_ID_SET.has(Number(blockId));
  }

  function isTreeSoilBlockId(blockId) {
    return TREE_SOIL_BLOCK_ID_SET.has(Number(blockId));
  }

  function createWoodLogDef(name, colorHex, hardness = 1.15) {
    return {
      name,
      color: colorHex,
      hardness,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({
        map: createDetailedTexture(colorHex, { grainStrength: 0.22, streaks: 8 }),
        roughness: 0.82,
        metalness: 0.02
      })
    };
  }

  function createWoodPlankDef(name, colorHex, hardness = 1.0) {
    return {
      name,
      color: colorHex,
      hardness,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({
        map: createDetailedTexture(colorHex, { grainStrength: 0.18, streaks: 6 }),
        roughness: 0.83,
        metalness: 0.01
      })
    };
  }
  const blockData = {
    1: {
      name: 'Grass',
      color: '#5cad58',
      hardness: 0.35,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#5cae56', { grainStrength: 0.26 }), roughness: 0.85, metalness: 0.02 })
    },
    2: {
      name: 'Dirt',
      color: '#8c5d31',
      hardness: 0.45,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#8b5a2b', { grainStrength: 0.34 }), roughness: 0.94, metalness: 0.0 })
    },
    3: {
      name: 'Log',
      color: '#6a4525',
      hardness: 1.15,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#6a4525', { grainStrength: 0.22, streaks: 8 }), roughness: 0.82, metalness: 0.02 })
    },
    4: {
      name: 'Leaves',
      color: '#2f8f33',
      hardness: 0.15,
      solid: true,
      raycast: true,
      showInHotbar: true,
      mat: leavesMat
    },
    5: {
      name: 'Stone',
      color: '#7f7f7f',
      hardness: 2.45,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#7e7e7e', { grainStrength: 0.42 }), roughness: 0.74, metalness: 0.03 })
    },
    6: {
      name: 'Planks',
      color: '#cf9d62',
      hardness: 1.0,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#cf9d62', { grainStrength: 0.18, streaks: 6 }), roughness: 0.83, metalness: 0.01 })
    },
    78: createWoodLogDef('Camphor Log', '#7d5c38'),
    79: createWoodLogDef('Banyan Log', '#69462d'),
    80: createWoodLogDef('Nanmu Log', '#806247'),
    81: createWoodLogDef('Birch Log', '#d2c7ad'),
    82: createWoodLogDef('Cypress Log', '#75442f', 1.2),
    83: createWoodLogDef('Maple Log', '#8e5a3a'),
    84: createWoodLogDef('Conifer Log', '#5f4a35', 1.2),
    85: createWoodLogDef('Juniper Log', '#7a7564', 1.2),
    86: createWoodLogDef('Fir Log', '#5a4836', 1.22),
    87: createWoodPlankDef('Camphor Planks', '#c8a77f'),
    88: createWoodPlankDef('Banyan Planks', '#bd9368'),
    89: createWoodPlankDef('Nanmu Planks', '#d2b188'),
    90: createWoodPlankDef('Birch Planks', '#e6ddb9'),
    91: createWoodPlankDef('Cypress Planks', '#ba8564'),
    92: createWoodPlankDef('Maple Planks', '#d39a72'),
    93: createWoodPlankDef('Conifer Planks', '#b59277'),
    94: createWoodPlankDef('Juniper Planks', '#c9c0ab'),
    95: createWoodPlankDef('Fir Planks', '#b89d83'),
    7: {
      name: 'Water',
      color: '#4a88dd',
      hardness: 0.06,
      solid: false,
      replaceable: true,
      mineable: false,
      raycast: false,
      showInHotbar: false,
      mat: new THREE.MeshStandardMaterial({
        map: createDetailedTexture('#4f8fe8', { grain: 2400, grainStrength: 0.1 }),
        roughness: 0.28,
        metalness: 0.0,
        transparent: true,
        opacity: 0.62,
        depthWrite: false
      })
    },
    8: {
      name: 'Iron Ore',
      color: '#9f8d7f',
      hardness: 2.9,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#9f8d7f', { grainStrength: 0.36, streaks: 3 }), roughness: 0.76, metalness: 0.12 })
    },
    9: {
      name: 'Coal Ore',
      color: '#4f4f54',
      hardness: 2.7,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#57575c', { grainStrength: 0.4, streaks: 2 }), roughness: 0.79, metalness: 0.08 })
    },
    10: {
      name: 'Diamond Ore',
      color: '#49c7da',
      hardness: 3.2,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#4ad1e8', { grainStrength: 0.36, streaks: 2 }), roughness: 0.75, metalness: 0.1 })
    },
    11: {
      name: 'Crafting Table',
      color: '#a0713f',
      hardness: 1.7,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#9a6b39', { grainStrength: 0.28, streaks: 7 }), roughness: 0.82, metalness: 0.04 })
    },
    12: {
      name: 'Torch',
      color: '#f1b955',
      hardness: 0.12,
      solid: false,
      showInHotbar: true,
      geo: torchGeo,
      mat: new THREE.MeshStandardMaterial({
        color: 0xdeb86a,
        roughness: 0.62,
        metalness: 0.02,
        emissive: 0xffa742,
        emissiveIntensity: 0.85
      }),
      light: { color: 0xffd392, intensity: 2.8, distance: 26, decay: 1.2 }
    },
    64: {
      name: 'Fire',
      color: '#ff8a3d',
      hardness: 0.0,
      solid: false,
      replaceable: true,
      mineable: true,
      showInHotbar: false,
      geo: fireGeo,
      mat: new THREE.MeshStandardMaterial({
        color: 0xff8a3d,
        roughness: 0.22,
        metalness: 0.0,
        transparent: true,
        opacity: 0.76,
        emissive: 0xff5a21,
        emissiveIntensity: 0.82,
        depthWrite: false
      }),
      light: { color: 0xffa15f, intensity: 0.6, distance: 7 }
    },
    69: {
      name: 'Lava',
      color: '#ff6a2a',
      hardness: 0.0,
      solid: false,
      replaceable: true,
      mineable: false,
      raycast: false,
      showInHotbar: false,
      mat: new THREE.MeshStandardMaterial({
        map: createDetailedTexture('#ff6a2a', { grain: 1500, grainStrength: 0.16, streaks: 2 }),
        roughness: 0.16,
        metalness: 0.0,
        transparent: false,
        opacity: 1.0,
        emissive: 0xff5a1f,
        emissiveIntensity: 0.88,
        depthWrite: true
      }),
      light: { color: 0xff6030, intensity: 0.2, distance: 12, decay: 1.6 }
    },
    14: {
      name: 'Chest',
      color: '#936233',
      hardness: 1.9,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#916131', { grainStrength: 0.22, streaks: 8 }), roughness: 0.82, metalness: 0.03 })
    },
    15: {
      name: 'Bed',
      color: '#a13f3f',
      hardness: 0.7,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#a44747', { grainStrength: 0.2, streaks: 3 }), roughness: 0.84, metalness: 0.01 })
    },
    16: {
      name: 'Oak Door',
      color: '#9b6c3a',
      hardness: 1.45,
      solid: true,
      showInHotbar: true,
      geo: doorClosedGeo,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#9a6a39', { grainStrength: 0.22, streaks: 7 }), roughness: 0.84, metalness: 0.02 })
    },
    17: {
      name: 'Oak Door (Open)',
      color: '#9b6c3a',
      hardness: 1.45,
      solid: false,
      showInHotbar: false,
      geo: doorOpenGeo,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#9a6a39', { grainStrength: 0.22, streaks: 7 }), roughness: 0.84, metalness: 0.02 })
    },
    18: {
      name: 'Trapdoor',
      color: '#8f6131',
      hardness: 1.35,
      solid: true,
      showInHotbar: true,
      geo: trapdoorClosedGeo,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#8f6131', { grainStrength: 0.22, streaks: 6 }), roughness: 0.84, metalness: 0.02 })
    },
    19: {
      name: 'Trapdoor (Open)',
      color: '#8f6131',
      hardness: 1.35,
      solid: false,
      showInHotbar: false,
      geo: trapdoorOpenGeo,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#8f6131', { grainStrength: 0.22, streaks: 6 }), roughness: 0.84, metalness: 0.02 })
    },
    20: {
      name: 'Fence',
      color: '#936333',
      hardness: 1.6,
      solid: true,
      showInHotbar: true,
      geo: fenceGeo,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#936333', { grainStrength: 0.24, streaks: 7 }), roughness: 0.84, metalness: 0.02 })
    },
    21: {
      name: 'Sandstone',
      color: '#dcc18b',
      hardness: 2.1,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#dcc18b', { grainStrength: 0.26, streaks: 3 }), roughness: 0.85, metalness: 0.01 })
    },
    22: {
      name: 'Bricks',
      color: '#b3604a',
      hardness: 2.3,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#b55f49', { grainStrength: 0.28, streaks: 5 }), roughness: 0.82, metalness: 0.02 })
    },
    23: {
      name: 'Glass',
      color: '#b9e7f4',
      hardness: 0.45,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({
        map: createDetailedTexture('#b9e7f4', { grain: 1100, grainStrength: 0.08 }),
        roughness: 0.18,
        metalness: 0.0,
        transparent: true,
        opacity: 0.48
      })
    },
    24: {
      name: 'White Concrete',
      color: '#e6e6e3',
      hardness: 2.7,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#e4e4e0', { grainStrength: 0.18, streaks: 1 }), roughness: 0.86, metalness: 0.0 })
    },
    25: {
      name: 'Asphalt',
      color: '#303236',
      hardness: 2.9,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#303236', { grainStrength: 0.34, streaks: 2 }), roughness: 0.88, metalness: 0.01 })
    },
    26: {
      name: 'Sand',
      color: '#d9ca8e',
      hardness: 0.55,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#d9ca8e', { grainStrength: 0.24, streaks: 1 }), roughness: 0.92, metalness: 0.0 })
    },
    27: {
      name: 'Gravel',
      color: '#8d8d93',
      hardness: 0.85,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#8c8d92', { grainStrength: 0.34, streaks: 2 }), roughness: 0.9, metalness: 0.01 })
    },
    28: {
      name: 'Cobblestone',
      color: '#737377',
      hardness: 2.35,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#727277', { grainStrength: 0.38, streaks: 3 }), roughness: 0.82, metalness: 0.02 })
    },
    29: {
      name: 'Stone Bricks',
      color: '#8a8c90',
      hardness: 2.6,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#8a8c90', { grainStrength: 0.3, streaks: 2 }), roughness: 0.82, metalness: 0.02 })
    },
    30: {
      name: 'Smooth Stone',
      color: '#95979d',
      hardness: 2.5,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#95979d', { grainStrength: 0.22, streaks: 1 }), roughness: 0.8, metalness: 0.02 })
    },
    31: {
      name: 'Obsidian',
      color: '#2a2440',
      hardness: 8.8,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#2a2440', { grainStrength: 0.2, streaks: 4 }), roughness: 0.56, metalness: 0.06 })
    },
    32: {
      name: 'Mossy Cobblestone',
      color: '#6d765f',
      hardness: 2.3,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#6d765f', { grainStrength: 0.34, streaks: 2 }), roughness: 0.86, metalness: 0.01 })
    },
    33: {
      name: 'Chiseled Stone Bricks',
      color: '#84888d',
      hardness: 2.7,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#85898e', { grainStrength: 0.26, streaks: 2 }), roughness: 0.82, metalness: 0.02 })
    },
    34: {
      name: 'Cracked Stone Bricks',
      color: '#72767b',
      hardness: 2.65,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#72767b', { grainStrength: 0.34, streaks: 3 }), roughness: 0.83, metalness: 0.02 })
    },
    35: {
      name: 'Coal Block',
      color: '#2e3035',
      hardness: 2.9,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#2d3035', { grainStrength: 0.3, streaks: 2 }), roughness: 0.78, metalness: 0.04 })
    },
    36: {
      name: 'Iron Block',
      color: '#c6c0b4',
      hardness: 5.0,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#c6c0b4', { grainStrength: 0.2, streaks: 1 }), roughness: 0.44, metalness: 0.24 })
    },
    37: {
      name: 'Diamond Block',
      color: '#64dff6',
      hardness: 5.2,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#64dff6', { grainStrength: 0.16, streaks: 1 }), roughness: 0.34, metalness: 0.22 })
    },
    38: {
      name: 'Glowstone',
      color: '#d2a15e',
      hardness: 0.45,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({
        map: createDetailedTexture('#d2a15e', { grainStrength: 0.2, streaks: 2 }),
        roughness: 0.52,
        metalness: 0.02,
        emissive: 0xffa64d,
        emissiveIntensity: 0.4
      }),
      light: { color: 0xffc27c, intensity: 0.78, distance: 9 }
    },
    39: {
      name: 'Quartz Block',
      color: '#f3eee7',
      hardness: 2.4,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#f3eee7', { grainStrength: 0.14, streaks: 1 }), roughness: 0.72, metalness: 0.03 })
    },
    40: {
      name: 'Netherrack',
      color: '#7a2e26',
      hardness: 0.85,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#7a2e26', { grainStrength: 0.36, streaks: 3 }), roughness: 0.9, metalness: 0.01 })
    },
    41: {
      name: 'Soul Sand',
      color: '#5b463a',
      hardness: 0.75,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#5b463a', { grainStrength: 0.34, streaks: 2 }), roughness: 0.92, metalness: 0.0 })
    },
    42: {
      name: 'Nether Bricks',
      color: '#381f23',
      hardness: 2.2,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#381f23', { grainStrength: 0.28, streaks: 3 }), roughness: 0.84, metalness: 0.02 })
    },
    43: {
      name: 'Nether Portal',
      color: '#8a3df5',
      hardness: 999,
      solid: false,
      showInHotbar: false,
      replaceable: true,
      mineable: false,
      raycast: false,
      mat: new THREE.MeshStandardMaterial({
        map: createDetailedTexture('#8a3df5', { grain: 1400, grainStrength: 0.12, streaks: 2 }),
        roughness: 0.16,
        metalness: 0.0,
        transparent: true,
        opacity: 0.62,
        emissive: 0x5b2ec6,
        emissiveIntensity: 0.55,
        depthWrite: false
      }),
      light: { color: 0x9d7bff, intensity: 0.45, distance: 8 }
    },
    44: {
      name: 'End Stone',
      color: '#d7d3a6',
      hardness: 2.8,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#d7d3a6', { grainStrength: 0.28, streaks: 2 }), roughness: 0.88, metalness: 0.01 })
    },
    45: {
      name: 'Purpur Block',
      color: '#9b7aa4',
      hardness: 2.6,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#9b7aa4', { grainStrength: 0.24, streaks: 2 }), roughness: 0.82, metalness: 0.02 })
    },
    47: {
      name: 'Gold Ore',
      color: '#b59d53',
      hardness: 3.05,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#b59d53', { grainStrength: 0.34, streaks: 3 }), roughness: 0.78, metalness: 0.12 })
    },
    48: {
      name: 'Gold Block',
      color: '#e2bf4a',
      hardness: 5.05,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#e2bf4a', { grainStrength: 0.2, streaks: 1 }), roughness: 0.46, metalness: 0.28 })
    },
    49: {
      name: 'Farmland',
      color: '#795634',
      hardness: 0.6,
      solid: true,
      showInHotbar: false,
      geo: farmlandGeo,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#7a5a39', { grainStrength: 0.32, streaks: 2 }), roughness: 0.91, metalness: 0.0 })
    },
    50: {
      name: 'Wheat Crop (Stage 1)',
      color: '#84b84d',
      hardness: 0.0,
      solid: false,
      replaceable: true,
      showInHotbar: false,
      geo: cropGeoStage1,
      mat: new THREE.MeshStandardMaterial({ color: 0x7fb54c, roughness: 0.86, metalness: 0.0 })
    },
    51: {
      name: 'Wheat Crop (Stage 2)',
      color: '#8dbc4f',
      hardness: 0.0,
      solid: false,
      replaceable: true,
      showInHotbar: false,
      geo: cropGeoStage2,
      mat: new THREE.MeshStandardMaterial({ color: 0x8dbd53, roughness: 0.84, metalness: 0.0 })
    },
    52: {
      name: 'Wheat Crop (Stage 3)',
      color: '#aac45d',
      hardness: 0.0,
      solid: false,
      replaceable: true,
      showInHotbar: false,
      geo: cropGeoStage3,
      mat: new THREE.MeshStandardMaterial({ color: 0xa9c55f, roughness: 0.83, metalness: 0.0 })
    },
    53: {
      name: 'Wheat Crop (Mature)',
      color: '#d5ba5c',
      hardness: 0.0,
      solid: false,
      replaceable: true,
      showInHotbar: false,
      geo: cropGeoStage4,
      mat: new THREE.MeshStandardMaterial({ color: 0xd8be61, roughness: 0.8, metalness: 0.0 })
    },
    65: {
      name: 'Oak Sapling',
      color: '#58ad4d',
      hardness: 0.0,
      solid: false,
      replaceable: true,
      showInHotbar: true,
      geo: saplingGeo,
      mat: new THREE.MeshStandardMaterial({ color: 0x5ab04f, roughness: 0.86, metalness: 0.0 })
    },
    66: {
      name: 'Berry Bush',
      color: '#b73f58',
      hardness: 0.0,
      solid: false,
      replaceable: true,
      showInHotbar: false,
      geo: berryBushGeo,
      mat: new THREE.MeshStandardMaterial({ color: 0xb9435b, roughness: 0.86, metalness: 0.0 })
    },
    67: {
      name: 'Blueberry Bush',
      color: '#4f63c6',
      hardness: 0.0,
      solid: false,
      replaceable: true,
      showInHotbar: false,
      geo: blueberryBushGeo,
      mat: new THREE.MeshStandardMaterial({ color: 0x4f64c9, roughness: 0.86, metalness: 0.0 })
    },
    68: {
      name: 'Hay Bale',
      color: '#d0b560',
      hardness: 0.62,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#cfb55f', { grainStrength: 0.28, streaks: 10 }), roughness: 0.9, metalness: 0.0 })
    },
    58: {
      name: 'Lever',
      color: '#7f7f7f',
      hardness: 0.5,
      solid: false,
      showInHotbar: true,
      geo: leverGeo,
      mat: new THREE.MeshStandardMaterial({ color: 0x9a9a9a, roughness: 0.6, metalness: 0.14 })
    },
    59: {
      name: 'Lever (On)',
      color: '#9f9f9f',
      hardness: 0.5,
      solid: false,
      showInHotbar: false,
      geo: leverGeo,
      mat: new THREE.MeshStandardMaterial({ color: 0xb3b3b3, roughness: 0.56, metalness: 0.16, emissive: 0xff5c3e, emissiveIntensity: 0.42 })
    },
    60: {
      name: 'Redstone Wire',
      color: '#702727',
      hardness: 0.08,
      solid: false,
      showInHotbar: true,
      geo: wireGeo,
      mat: new THREE.MeshStandardMaterial({ color: 0x6a2222, roughness: 0.72, metalness: 0.0 })
    },
    61: {
      name: 'Redstone Wire (Powered)',
      color: '#c94040',
      hardness: 0.08,
      solid: false,
      showInHotbar: false,
      geo: wireGeo,
      mat: new THREE.MeshStandardMaterial({ color: 0xbd3737, roughness: 0.64, metalness: 0.0, emissive: 0xff4f4f, emissiveIntensity: 0.52 })
    },
    62: {
      name: 'Redstone Lamp',
      color: '#6a4f2c',
      hardness: 1.3,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#6b4f2b', { grainStrength: 0.22, streaks: 1 }), roughness: 0.62, metalness: 0.06 })
    },
    63: {
      name: 'Redstone Lamp (On)',
      color: '#c08d4a',
      hardness: 1.3,
      solid: true,
      showInHotbar: false,
      mat: new THREE.MeshStandardMaterial({
        map: createDetailedTexture('#c08d4a', { grainStrength: 0.2, streaks: 1 }),
        roughness: 0.48,
        metalness: 0.04,
        emissive: 0xffb75e,
        emissiveIntensity: 0.62
      }),
      light: { color: 0xffc77c, intensity: 1.1, distance: 8, decay: 2.0 }
    },
    46: {
      name: 'End Portal',
      color: '#4b0b73',
      hardness: 999,
      solid: false,
      showInHotbar: false,
      replaceable: true,
      mineable: false,
      raycast: false,
      mat: new THREE.MeshStandardMaterial({
        map: createDetailedTexture('#4b0b73', { grain: 1300, grainStrength: 0.14, streaks: 2 }),
        roughness: 0.2,
        metalness: 0.0,
        transparent: true,
        opacity: 0.66,
        emissive: 0x6f2cc2,
        emissiveIntensity: 0.5,
        depthWrite: false
      }),
      light: { color: 0xa976ff, intensity: 0.52, distance: 9 }
    },
    13: {
      name: 'Furnace',
      color: '#727780',
      hardness: 3.6,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#6e747d', { grainStrength: 0.36, streaks: 2 }), roughness: 0.72, metalness: 0.08 })
    },
    70: {
      name: 'Snow Block',
      color: '#f2f8fc',
      hardness: 0.5,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#f9fbfc', { grainStrength: 0.1 }), roughness: 0.8, metalness: 0.0 })
    },
    71: {
      name: 'Ice',
      color: '#8cbfe6',
      hardness: 1.0,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({
        map: createDetailedTexture('#9dd2ed', { grainStrength: 0.15 }),
        roughness: 0.1,
        metalness: 0.05,
        transparent: true,
        opacity: 0.82,
        depthWrite: false
      })
    },
    72: {
      name: 'Cactus',
      color: '#136b19',
      hardness: 0.4,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#1b7e22', { grainStrength: 0.35, streaks: 4 }), roughness: 0.9, metalness: 0.05 })
    },
    73: {
      name: 'Kelp',
      color: '#2a7c30',
      hardness: 0.2,
      solid: false,
      replaceable: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#3b9e4a', { grainStrength: 0.2, streaks: 10 }), roughness: 0.6, metalness: 0.0, transparent: true, opacity: 0.9 })
    },
    74: {
      name: 'Basalt',
      color: '#4d5159',
      hardness: 2.2,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#4b4f57', { grainStrength: 0.35, streaks: 9 }), roughness: 0.9, metalness: 0.05 })
    },
    75: {
      name: 'Volcanic Rock',
      color: '#2b2a2a',
      hardness: 3.5,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#2a2929', { grainStrength: 0.45, streaks: 4 }), roughness: 0.95, metalness: 0.0 })
    },
    77: {
      name: 'Red Brick',
      color: '#9f473a',
      hardness: 2.8,
      solid: true,
      showInHotbar: true,
      mat: new THREE.MeshStandardMaterial({ map: createDetailedTexture('#9f473a', { grainStrength: 0.34, streaks: 6 }), roughness: 0.84, metalness: 0.01 })
    },
    76: {
      name: 'Aloe',
      color: '#4ea95f',
      hardness: 0.0,
      solid: false,
      replaceable: true,
      showInHotbar: false,
      geo: saplingGeo,
      mat: new THREE.MeshStandardMaterial({ color: 0x58b86a, roughness: 0.84, metalness: 0.0 })
    }
  };

  for (const [idText, def] of Object.entries(blockData)) {
    const id = Number(idText);
    const mat = def?.mat;
    if (!Number.isFinite(id) || !mat || !('map' in mat) || !mat.map) continue;
    classicTextureMapByBlockId.set(id, mat.map);
  }
  requestedTextureStyle = normalizeTextureStyle(rawTextureStyle);
  applyTextureStyle(TEXTURE_STYLE_CLASSIC);
  void setTextureStyleInternal(requestedTextureStyle);

  function maybeGenerateRavineHazard(x, surfaceY, z, ck, info) {
    const terrain = info?.terrain;
    if (terrain !== 'canyon' && terrain !== 'ravine') return;

    const cutStrength = Math.max(Number(info?.canyon || 0), Number(info?.ravine || 0));
    if (cutStrength <= 0.34) return;

    const axisNoiseFreq = terrain === 'canyon' ? 0.05 : 0.074;
    const axisNoise = Math.abs(valueNoise2(x + 47, z - 31, axisNoiseFreq, terrain === 'canyon' ? 7703 : 7709));
    const width = terrain === 'canyon'
      ? (0.16 + (1 - cutStrength) * 0.07)
      : (0.1 + (1 - cutStrength) * 0.05);
    if (axisNoise > width * 0.86) return;

    const depth = Math.floor((terrain === 'canyon' ? 8 : 6) + cutStrength * (terrain === 'canyon' ? 15 : 10));
    const floorY = surfaceY - depth;
    const fluidY = floorY + 1;
    if (fluidY <= WORLD_MIN_Y + 1) return;

    const submergedRavine = surfaceY <= WATER_LEVEL - 1;
    if (submergedRavine) {
      const tunnelBaseY = floorY + 1;
      const tunnelOuterWidth = width * (terrain === 'canyon' ? 0.74 : 0.68);
      const tunnelInnerWidth = tunnelOuterWidth * 0.46;
      if (axisNoise <= tunnelOuterWidth) {
        const scanTopY = Math.max(WATER_LEVEL + 10, surfaceY + 18);
        const firstWaterY = sealFirstWaterAboveAsGlass(x, z, tunnelBaseY + 1, scanTopY, ck);

        if (axisNoise <= tunnelInnerWidth) {
          if (canUseNaturalAt(x, tunnelBaseY, z)) {
            setGeneratedBlockAt(x, tunnelBaseY, z, 5, ck);
          }

          let tunnelTopY = Number.isFinite(firstWaterY) ? firstWaterY : null;
          if (!Number.isFinite(tunnelTopY)) {
            tunnelTopY = Math.min(WATER_LEVEL, tunnelBaseY + (terrain === 'canyon' ? 6 : 5));
          }
          tunnelTopY = Math.max(tunnelBaseY + 2, tunnelTopY);

          for (let y = tunnelBaseY + 1; y <= tunnelTopY - 1; y++) {
            clearGeneratedBlockAt(x, y, z);
            convertAdjacentWaterToGlass(x, y, z);
          }
          setGeneratedBlockAt(x, tunnelTopY, z, 23, ck);
          convertAdjacentWaterToGlass(x, tunnelTopY, z);
          convertAdjacentWaterToGlass(x, tunnelTopY - 1, z);
        } else {
          const shellTopY = Math.max(tunnelBaseY, Number.isFinite(firstWaterY) ? firstWaterY : WATER_LEVEL);
          for (let y = tunnelBaseY; y <= shellTopY; y++) {
            setGeneratedBlockAt(x, y, z, 23, ck);
          }
          convertAdjacentWaterToGlass(x, shellTopY, z);
        }
        return;
      }
    }

    const hotField = (valueNoise2(x - 17, z + 29, 0.085, 7727) + 1) * 0.5;
    const pocketField = (valueNoise2(x + 13, z - 9, 0.19, 7739) + 1) * 0.5;
    const wetField = (valueNoise2(x + 21, z + 7, 0.11, 7751) + 1) * 0.5;

    let fluidId = null;
    let shellId = null;
    if (surfaceY <= WATER_LEVEL + 1 || wetField > 0.83) {
      fluidId = 7;
    } else if (floorY <= 20 && hotField > 0.63 && pocketField > 0.6) {
      fluidId = 69;
      shellId = 75;
    } else if (terrain === 'canyon' && floorY <= 16 && hotField > 0.56 && wetField < 0.48) {
      fluidId = 69;
      shellId = 74;
    } else if (wetField > 0.88 && pocketField > 0.66) {
      fluidId = 7;
    }

    if (!fluidId) return;
    const placed = createBlock(x, fluidY, z, fluidId, { generated: true, chunkKey: ck, allowReplace: true });
    if (!placed) return;
    if (fluidId === 7) {
      sealFluidWithBarrier(x, fluidY, z, fluidId, ck, { corners: true, floor: true });
    } else if (shellId) {
      if (canUseNaturalAt(x, floorY, z)) {
        setGeneratedBlockAt(x, floorY, z, shellId, ck);
      }
      if (canUseNaturalAt(x, floorY - 1, z)) {
        setGeneratedBlockAt(x, floorY - 1, z, shellId, ck);
      }
      for (let ly = fluidY; ly <= fluidY + 2; ly++) {
        if (canUseNaturalAt(x, ly, z) && getBlockIdAt(x, ly, z) !== fluidId && getBlockIdAt(x, ly, z) !== 0) {
           if (hash2i(x, z, 7759 + ly) > 0.3) {
             setGeneratedBlockAt(x, ly, z, shellId, ck);
           }
        }
      }
    }
  }

  function maybeGenerateVolcanicVent(x, surfaceY, z, ck, info) {
    if (info?.terrain !== 'volcano') return;
    if (surfaceY <= WATER_LEVEL + 4) return;

    const heatField = (valueNoise2(x - 11, z + 29, 0.082, 8411) + 1) * 0.5;
    const crackField = (valueNoise2(x + 19, z - 13, 0.14, 8423) + 1) * 0.5;
    const ashField = (valueNoise2(x - 37, z + 7, 0.1, 8437) + 1) * 0.5;

    if (canUseNaturalAt(x, surfaceY, z) && heatField > 0.52) {
      const crustId = heatField > 0.78 ? 75 : 74;
      setGeneratedBlockAt(x, surfaceY, z, crustId, ck);
    }

    if (heatField > 0.86 && crackField > 0.62) {
      const ventY = surfaceY + 1;
      if (canUseNaturalAt(x, ventY, z) && (getBlockIdAt(x, ventY, z) == null || getBlockIdAt(x, ventY, z) === 0)) {
        const placed = createBlock(x, ventY, z, 69, { generated: true, chunkKey: ck, allowReplace: true });
        if (placed && hash2i(x, z, 8449) > 0.58 && canUseNaturalAt(x, ventY + 1, z)) {
          createBlock(x, ventY + 1, z, 69, { generated: true, chunkKey: ck, allowReplace: true });
        }
      }
      return;
    }

    if (ashField > 0.78 && crackField < 0.44 && canUseNaturalAt(x, surfaceY + 1, z) && hash2i(x, z, 8461) > 0.74) {
      setGeneratedBlockAt(x, surfaceY + 1, z, 76, ck);
    }
  }

  function sanitizeManualPlacementOptions(opts = null, blockId = null) {
    if (!opts || typeof opts !== 'object') return null;
    const sanitized = {};
    for (const field of MANUAL_BLOCK_OPTION_FIELDS) {
      const value = opts[field];
      if (value === undefined || value === null) continue;
      if (field === 'treeSpecies') {
        if (typeof value === 'string' && value) sanitized[field] = value;
        continue;
      }
      if (field === 'leafDecayEligible') {
        if (value === true) sanitized[field] = true;
        continue;
      }
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) continue;
      sanitized[field] = Math.floor(numeric);
    }
    const resolvedBlockId = Number.isFinite(Number(blockId))
      ? Math.floor(Number(blockId))
      : Math.floor(Number(opts?.id));
    if (
      resolvedBlockId === 4
      && sanitized.leafDecayEligible !== true
      && Number.isFinite(sanitized.treeRootX)
      && Number.isFinite(sanitized.treeRootY)
      && Number.isFinite(sanitized.treeRootZ)
    ) {
      sanitized.leafDecayEligible = true;
    }
    return Object.keys(sanitized).length > 0 ? sanitized : null;
  }

  function registerManualPlacement(x, y, z, id, opts = null) {
    const k = blockKey(x, y, z);
    const ck = chunkKey(worldToChunk(x), worldToChunk(z));
    const sanitizedOpts = sanitizeManualPlacementOptions(opts, id);
    manualPlaced.set(k, { x, y, z, id, chunkKey: ck, opts: sanitizedOpts });
    if (!manualByChunk.has(ck)) manualByChunk.set(ck, new Set());
    manualByChunk.get(ck).add(k);
    manualRemoved.delete(k);
  }

  function unregisterManualPlacement(key) {
    const existing = manualPlaced.get(key);
    if (!existing) return;
    manualPlaced.delete(key);
    const set = manualByChunk.get(existing.chunkKey);
    if (!set) return;
    set.delete(key);
    if (set.size === 0) manualByChunk.delete(existing.chunkKey);
  }

  function getBlockRecordAtKey(key) {
    const mesh = solidBlocks.get(key);
    if (mesh) {
      return {
        id: mesh.userData.id,
        solid: !!mesh.userData.solid,
        replaceable: !!mesh.userData.replaceable,
        generated: !!mesh.userData.generated,
        chunkKey: mesh.userData.chunkKey || null,
        mesh
      };
    }

    const culled = culledBlocks.get(key);
    if (culled) {
      const def = blockData[culled.id];
      return {
        id: culled.id,
        solid: !!def?.solid,
        replaceable: !!def?.replaceable,
        generated: !!culled.generated,
        chunkKey: culled.chunkKey || null,
        mesh: null
      };
    }

    return null;
  }

  function getBlockAt(x, y, z) {
    return solidBlocks.get(blockKey(worldToBlock(x), worldToBlock(y), worldToBlock(z))) || null;
  }

  function getRenderableBlockAt(x, y, z) {
    const bx = worldToBlock(x);
    const by = worldToBlock(y);
    const bz = worldToBlock(z);
    const key = blockKey(bx, by, bz);
    const direct = solidBlocks.get(key);
    if (direct) return direct;
    if (culledBlocks.has(key)) return null;

    const ck = chunkKey(worldToChunk(bx), worldToChunk(bz));
    const slot = resolveInstancedSlotByKey(key, { chunkKey: ck, localOnly: true });
    if (!slot?.bucket) return null;
    return recoverInstancedProxyForKey(key, slot.bucket, slot.bucket.ck);
  }

  function isSolidAt(x, y, z) {
    const key = blockKey(worldToBlock(x), worldToBlock(y), worldToBlock(z));
    const record = getBlockRecordAtKey(key);
    return !!record?.solid;
  }

  function getPrefetchedColumnInfoAt(gx, gz, expectedDimension = 'overworld') {
    if (!columnPrefetch || typeof columnPrefetch.getColumnInfo !== 'function') return null;
    const info = columnPrefetch.getColumnInfo(gx, gz);
    if (!info) return null;
    if (info.dimension && info.dimension !== expectedDimension) return null;
    return info;
  }

  function estimateSurfaceHeightAt(gx, gz) {
    const dimension = getDimensionAtCoord(gx, gz);
    if (dimension === 'nether') {
      const prefetched = getPrefetchedColumnInfoAt(gx, gz, 'nether');
      if (prefetched) return prefetched.height;
      return netherColumnNoise(gx, gz).height;
    }
    if (dimension === 'end') {
      const info = getPrefetchedColumnInfoAt(gx, gz, 'end') || endColumnNoise(gx, gz);
      if (!info.hasLand) return Number.NEGATIVE_INFINITY;
      return info.height;
    }
    const prefetched = getPrefetchedColumnInfoAt(gx, gz, 'overworld');
    if (prefetched) return prefetched.height;
    return columnNoise(gx, gz).height;
  }

  function isSolidForCollisionAt(x, y, z) {
    const gx = worldToBlock(x);
    const gy = worldToBlock(y);
    const gz = worldToBlock(z);
    const key = blockKey(gx, gy, gz);
    const record = getBlockRecordAtKey(key);
    if (record) return !!record.solid;

    const ck = chunkKey(worldToChunk(gx), worldToChunk(gz));
    const chunk = loadedChunks.get(ck);
    if (chunk?.ready) return false;

    const surfaceY = estimateSurfaceHeightAt(gx, gz);
    if (!Number.isFinite(surfaceY)) return false;
    if (gy < WORLD_MIN_Y || gy > surfaceY) return false;
    return true;
  }

  function getBlockIdAt(x, y, z) {
    const key = blockKey(worldToBlock(x), worldToBlock(y), worldToBlock(z));
    const record = getBlockRecordAtKey(key);
    return record ? record.id : null;
  }

  function isWaterAt(x, y, z) {
    return getBlockIdAt(x, y, z) === 7;
  }

  function isLavaAt(x, y, z) {
    return getBlockIdAt(x, y, z) === 69;
  }

  function hasTorchSupport(x, y, z) {
    if (isSolidAt(x, y - 1, z)) return true;
    if (isSolidAt(x + 1, y, z)) return true;
    if (isSolidAt(x - 1, y, z)) return true;
    if (isSolidAt(x, y, z + 1)) return true;
    if (isSolidAt(x, y, z - 1)) return true;
    return false;
  }

  function canPlaceAt(x, y, z) {
    const key = blockKey(worldToBlock(x), worldToBlock(y), worldToBlock(z));
    const existing = getBlockRecordAtKey(key);
    return !existing || !!existing.replaceable;
  }

  function parseBlockKey(key) {
    if (typeof key === 'number') return blockKeyReverse(key);
    const [x, y, z] = key.split(',').map(Number);
    return { x, y, z };
  }

  function isInstancedBlockProxy(block) {
    return !!block?.userData?.instancedProxy;
  }

  function cloneInstancedProxyUserData(userData = {}, key = null, chunkKeyHint = null) {
    const cloned = {
      id: userData.id,
      hardness: userData.hardness,
      solid: !!userData.solid,
      mineable: userData.mineable !== false,
      replaceable: !!userData.replaceable,
      raycast: userData.raycast !== false,
      generated: !!userData.generated,
      chunkKey: userData.chunkKey || chunkKeyHint || null,
      instancedProxy: true,
      blockKey: key
    };
    for (const field of INSTANCED_PROXY_EXTRA_FIELDS) {
      if (userData[field] !== undefined) cloned[field] = userData[field];
    }
    return cloned;
  }

  function getDetailCullDistance(typeId) {
    const distance = DETAIL_CULL_DISTANCE_BY_BLOCK_ID.get(Number(typeId));
    return Number.isFinite(distance) && distance > 0 ? distance : 0;
  }

  function shouldUseInstancedBlock(typeId, def, opts = {}) {
    const baseTypeId = parseInstancedTypeId(typeId);
    if (!def || !Number.isFinite(baseTypeId)) return false;
    if (!def.solid && !INSTANCED_NON_SOLID_BLOCK_IDS.has(baseTypeId)) return false;
    if (def.geo && def.geo !== blockGeo && !INSTANCED_CUSTOM_GEOMETRY_BLOCK_IDS.has(baseTypeId)) return false;
    if (def.light && shouldUseRuntimePointLight(baseTypeId)) return false;
    if (baseTypeId === 7 || baseTypeId === 69) return false;
    if (GRAVITY_BLOCK_IDS.has(baseTypeId)) return false;
    return true;
  }

  function shouldUseRuntimePointLight(typeId) {
    return RUNTIME_POINT_LIGHT_BLOCK_IDS.has(Number(typeId));
  }

  function getChunkInstancedBuckets(ck, create = false) {
    let buckets = instancedBlockBucketsByChunk.get(ck);
    if (!buckets && create) {
      buckets = new Map();
      instancedBlockBucketsByChunk.set(ck, buckets);
    }
    return buckets || null;
  }

  function createInstancedBucket(ck, typeId, def) {
    const chunk = loadedChunks.get(ck);
    if (!chunk) return null;
    const baseTypeId = Number.isFinite(typeId)
      ? Math.floor(typeId)
      : Number.parseInt(String(typeId), 10);
    const detailCullDistance = getDetailCullDistance(baseTypeId);
    const detailAnchorX = (chunk.cx * CHUNK_SIZE) + CHUNK_SIZE * 0.5;
    const detailAnchorZ = (chunk.cz * CHUNK_SIZE) + CHUNK_SIZE * 0.5;
    const raycastEnabled = def?.raycast !== false;

    const mesh = new THREE.InstancedMesh(def.geo || blockGeo, def.mat, STATIC_INSTANCE_MIN_CAPACITY);
    mesh.count = 0;
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    mesh.frustumCulled = false;
    mesh.matrixAutoUpdate = false;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.userData = {
      instancedBlockBucket: true,
      chunkKey: ck,
      typeId,
      baseTypeId,
      detailCullDistance,
      detailAnchorX,
      detailAnchorZ,
      detailVisibleRetainUntil: 0,
      raycast: raycastEnabled
    };

    if (chunk.group) {
      chunk.group.add(mesh);
      chunk.group.visible = chunk.visible !== false;
    } else {
      scene.add(mesh);
    }
    if (raycastEnabled) {
      addRaycastObject(mesh);
      chunk.raycastObjects.add(mesh);
    }
    if (detailCullDistance > 0) {
      chunk.detailMeshes.add(mesh);
    }

    const bucket = {
      ck,
      typeId,
      mesh,
      capacity: STATIC_INSTANCE_MIN_CAPACITY,
      count: 0,
      keys: []
    };
    instancedMeshBucket.set(mesh, bucket);
    return bucket;
  }

  function swapInstancedBucketMesh(bucket, nextMesh) {
    if (!bucket || !nextMesh) return;
    const prevMesh = bucket.mesh;
    const chunk = loadedChunks.get(bucket.ck);

    if (chunk) {
      if (chunk.group) {
        chunk.group.remove(prevMesh);
        chunk.group.add(nextMesh);
      } else {
        scene.remove(prevMesh);
        scene.add(nextMesh);
      }
      if (prevMesh.userData?.raycast !== false) chunk.raycastObjects.delete(prevMesh);
      if (nextMesh.userData?.raycast !== false) chunk.raycastObjects.add(nextMesh);
      if (chunk.detailMeshes?.has(prevMesh)) {
        chunk.detailMeshes.delete(prevMesh);
        chunk.detailMeshes.add(nextMesh);
      }
    } else {
      removeMeshFromWorldParent(prevMesh);
      scene.add(nextMesh);
    }
    if (prevMesh.userData?.raycast !== false) removeRaycastObject(prevMesh);
    if (nextMesh.userData?.raycast !== false) addRaycastObject(nextMesh);
    instancedMeshBucket.delete(prevMesh);
    instancedMeshBucket.set(nextMesh, bucket);
    prevMesh.dispose?.();
    bucket.mesh = nextMesh;
  }

  function ensureInstancedBucketCapacity(bucket, minCapacity) {
    if (!bucket || bucket.capacity >= minCapacity) return bucket;

    let nextCapacity = bucket.capacity;
    while (nextCapacity < minCapacity) nextCapacity *= 2;

    const prevMesh = bucket.mesh;
    const nextMesh = new THREE.InstancedMesh(prevMesh.geometry, prevMesh.material, nextCapacity);
    nextMesh.count = bucket.count;
    nextMesh.castShadow = prevMesh.castShadow;
    nextMesh.receiveShadow = prevMesh.receiveShadow;
    nextMesh.visible = prevMesh.visible;
    nextMesh.frustumCulled = false;
    nextMesh.matrixAutoUpdate = prevMesh.matrixAutoUpdate;
    nextMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    nextMesh.userData = {
      ...prevMesh.userData
    };

    for (let i = 0; i < bucket.count; i++) {
      prevMesh.getMatrixAt(i, instancedMatrixScratch);
      nextMesh.setMatrixAt(i, instancedMatrixScratch);
    }
    nextMesh.instanceMatrix.needsUpdate = true;

    bucket.capacity = nextCapacity;
    swapInstancedBucketMesh(bucket, nextMesh);
    return bucket;
  }

  function resolveInstancedSlotByKey(key, opts = {}) {
    const cached = instancedBlockIndexByKey.get(key);
    if (cached?.bucket) {
      const bucket = cached.bucket;
      const index = Math.floor(Number(cached.index));
      if (Number.isFinite(index) && index >= 0 && index < bucket.count && bucket.keys[index] === key) {
        return { bucket, index };
      }
      instancedBlockIndexByKey.delete(key);
    }

    const resolveFromBucketMap = (bucketMap) => {
      if (!bucketMap) return null;
      for (const bucket of bucketMap.values()) {
        if (!bucket || bucket.count <= 0) continue;
        const index = bucket.keys.indexOf(key);
        if (index < 0 || index >= bucket.count) continue;
        const slot = { bucket, index };
        instancedBlockIndexByKey.set(key, slot);
        return slot;
      }
      return null;
    };

    const hintedChunkKey = opts?.chunkKey;
    if (hintedChunkKey !== undefined && hintedChunkKey !== null) {
      const hinted = resolveFromBucketMap(instancedBlockBucketsByChunk.get(hintedChunkKey));
      if (hinted) return hinted;
      if (opts?.localOnly) return null;
    }

    for (const chunkBuckets of instancedBlockBucketsByChunk.values()) {
      const slot = resolveFromBucketMap(chunkBuckets);
      if (slot) return slot;
    }
    return null;
  }

  function removeInstancedBlockByKey(key) {
    const slot = resolveInstancedSlotByKey(key);
    if (!slot) return false;

    const bucket = slot.bucket;
    const index = slot.index;
    if (!bucket || index < 0 || index >= bucket.count) {
      instancedBlockIndexByKey.delete(key);
      return false;
    }

    const mesh = bucket.mesh;
    const lastIndex = bucket.count - 1;
    if (index !== lastIndex) {
      mesh.getMatrixAt(lastIndex, instancedMatrixScratch);
      mesh.setMatrixAt(index, instancedMatrixScratch);
      const movedKey = bucket.keys[lastIndex];
      bucket.keys[index] = movedKey;
      if (movedKey) instancedBlockIndexByKey.set(movedKey, { bucket, index });
    }

    bucket.keys[lastIndex] = undefined;
    bucket.count = lastIndex;
    mesh.count = bucket.count;
    mesh.instanceMatrix.needsUpdate = true;
    instancedBlockIndexByKey.delete(key);
    instancedBlockMetaByKey.delete(key);

    if (bucket.count > 0) return true;

    const chunk = loadedChunks.get(bucket.ck);
    if (chunk) {
      if (mesh.userData?.raycast !== false) chunk.raycastObjects.delete(mesh);
      if (mesh.userData?.detailCullDistance > 0) {
        chunk.detailMeshes?.delete(mesh);
      }
      if (chunk.group) chunk.group.remove(mesh);
      else scene.remove(mesh);
    } else {
      removeMeshFromWorldParent(mesh);
    }

    if (mesh.userData?.raycast !== false) removeRaycastObject(mesh);
    instancedMeshBucket.delete(mesh);
    const chunkBuckets = instancedBlockBucketsByChunk.get(bucket.ck);
    if (chunkBuckets) {
      chunkBuckets.delete(bucket.typeId);
      if (chunkBuckets.size === 0) instancedBlockBucketsByChunk.delete(bucket.ck);
    }
    mesh.dispose?.();
    return true;
  }

  function getInstancedBucketKey(typeId, opts = {}) {
    if (typeId === 4) {
      const variant = Math.abs(opts?.leafVariant || 0) % leavesMatVariants.length;
      return `4.${variant}`;
    }
    return typeId;
  }

  function getInstancedMaterial(typeId, opts = {}) {
    if (typeId === 4) {
      const variant = Math.abs(opts?.leafVariant || 0) % leavesMatVariants.length;
      return leavesMatVariants[variant] || leavesMat;
    }
    return blockData[typeId]?.mat;
  }

  function addInstancedBlockToWorld(block, key, opts = {}) {
    if (!block || key === undefined || key === null) return null;
    const typeId = block.userData.id;
    const def = blockData[typeId];
    if (!def) return null;

    const ck = block.userData.chunkKey || chunkKey(worldToChunk(block.position.x), worldToChunk(block.position.z));
    const chunk = loadedChunks.get(ck);
    if (!chunk) return null;
    const proxyUserData = cloneInstancedProxyUserData(block.userData, key, ck);
    block.userData = proxyUserData;

    const bucketKey = getInstancedBucketKey(typeId, opts);
    const buckets = getChunkInstancedBuckets(ck, true);
    let bucket = buckets.get(bucketKey);
    if (!bucket) {
      const mat = getInstancedMaterial(typeId, opts);
      const defOverride = mat !== def.mat ? { ...def, mat } : def;
      bucket = createInstancedBucket(ck, bucketKey, defOverride);
      if (!bucket) return null;
      buckets.set(bucketKey, bucket);
    }

    ensureInstancedBucketCapacity(bucket, bucket.count + 1);

    const index = bucket.count;
    bucket.count += 1;
    bucket.keys[index] = key;
    bucket.mesh.count = bucket.count;
    instancedMatrixScratch.makeTranslation(block.position.x, block.position.y, block.position.z);
    bucket.mesh.setMatrixAt(index, instancedMatrixScratch);
    bucket.mesh.instanceMatrix.needsUpdate = true;
    instancedBlockIndexByKey.set(key, { bucket, index });
    instancedBlockMetaByKey.set(key, { ...proxyUserData });

    solidBlocks.set(key, block);
    chunk.blockKeys.add(key);
    culledBlocks.delete(key);
    return block;
  }

  function parseInstancedTypeId(rawTypeId) {
    if (Number.isFinite(rawTypeId)) return Math.floor(rawTypeId);
    if (typeof rawTypeId !== 'string' || rawTypeId.length === 0) return null;
    const parsed = Number.parseInt(rawTypeId, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function recoverInstancedProxyForKey(key, bucket = null, chunkKeyHint = null) {
    const pos = parseBlockKey(key);
    if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y) || !Number.isFinite(pos.z)) return null;

    const storedUserData = instancedBlockMetaByKey.get(key) || null;
    const typeId = parseInstancedTypeId(storedUserData?.id ?? bucket?.typeId ?? bucket?.mesh?.userData?.typeId);
    if (!Number.isFinite(typeId)) return null;
    const def = blockData[typeId];
    if (!def) return null;

    const resolvedChunkKey = chunkKeyHint
      || bucket?.ck
      || bucket?.mesh?.userData?.chunkKey
      || chunkKey(worldToChunk(pos.x), worldToChunk(pos.z));
    const proxyUserData = storedUserData
      ? {
        ...storedUserData,
        chunkKey: storedUserData.chunkKey || resolvedChunkKey || null,
        instancedProxy: true,
        blockKey: key
      }
      : {
        id: typeId,
        hardness: def.hardness,
        solid: !!def.solid,
        mineable: def.mineable !== false,
        replaceable: !!def.replaceable,
        raycast: def.raycast !== false,
        generated: true,
        chunkKey: resolvedChunkKey || null,
        instancedProxy: true,
        blockKey: key
      };
    const proxy = {
      position: new THREE.Vector3(pos.x, pos.y, pos.z),
      userData: proxyUserData
    };

    solidBlocks.set(key, proxy);
    const chunk = resolvedChunkKey ? loadedChunks.get(resolvedChunkKey) : null;
    if (chunk) chunk.blockKeys.add(key);
    culledBlocks.delete(key);
    return proxy;
  }

  function recoverInstancedProxyAtWorldPosition(x, y, z, bucket = null, chunkKeyHint = null) {
    const gx = worldToBlock(x);
    const gy = worldToBlock(y);
    const gz = worldToBlock(z);
    const key = blockKey(gx, gy, gz);
    const mapped = solidBlocks.get(key);
    if (mapped) return mapped;
    if (culledBlocks.has(key)) return null;

    const hintedChunkKey = chunkKeyHint
      ?? bucket?.ck
      ?? bucket?.mesh?.userData?.chunkKey
      ?? chunkKey(worldToChunk(gx), worldToChunk(gz));
    const slot = resolveInstancedSlotByKey(key, { chunkKey: hintedChunkKey, localOnly: true });
    if (!slot?.bucket) return null;
    return recoverInstancedProxyForKey(key, slot.bucket, slot.bucket.ck);
  }

  function recoverInstancedProxyFromRaycastHit(hit, bucket = null) {
    const rawObject = hit?.object;
    const resolvedBucket = bucket || instancedMeshBucket.get(rawObject);
    if (!resolvedBucket) return null;

    const chunkKeyHint = rawObject?.userData?.chunkKey || resolvedBucket.ck || null;
    const instanceId = Number(hit?.instanceId);
    if (Number.isFinite(instanceId)) {
      const index = Math.floor(instanceId);
      if (index >= 0 && index < resolvedBucket.count) {
        const key = resolvedBucket.keys[index];
        if (key !== undefined && key !== null) {
          const mapped = solidBlocks.get(key);
          if (mapped) return mapped;
          const recovered = recoverInstancedProxyForKey(key, resolvedBucket, chunkKeyHint);
          if (recovered) return recovered;
        }

        resolvedBucket.mesh.getMatrixAt(index, instancedMatrixScratch);
        instancedPositionScratch.setFromMatrixPosition(instancedMatrixScratch);
        const matrixRecovered = recoverInstancedProxyAtWorldPosition(
          instancedPositionScratch.x,
          instancedPositionScratch.y,
          instancedPositionScratch.z,
          resolvedBucket,
          chunkKeyHint
        );
        if (matrixRecovered) return matrixRecovered;
      }
    }

    if (hit?.point) {
      const faceNormal = hit?.face?.normal;
      if (faceNormal) {
        instancedInteriorPointScratch.copy(hit.point).addScaledVector(faceNormal, -0.02);
        const inwardRecovered = recoverInstancedProxyAtWorldPosition(
          instancedInteriorPointScratch.x,
          instancedInteriorPointScratch.y,
          instancedInteriorPointScratch.z,
          resolvedBucket,
          chunkKeyHint
        );
        if (inwardRecovered) return inwardRecovered;
      }

      const pointRecovered = recoverInstancedProxyAtWorldPosition(
        hit.point.x,
        hit.point.y,
        hit.point.z,
        resolvedBucket,
        chunkKeyHint
      );
      if (pointRecovered) return pointRecovered;
    }

    return null;
  }

  function resolveRaycastHitObject(hit) {
    const rawObject = hit?.object;
    if (!rawObject) return null;
    if (!rawObject.userData?.instancedBlockBucket) return rawObject;

    const bucket = instancedMeshBucket.get(rawObject);
    if (!bucket) return null;
    return recoverInstancedProxyFromRaycastHit(hit, bucket);
  }

  function isChunkReadyAt(x, z) {
    const ck = chunkKey(worldToChunk(x), worldToChunk(z));
    const chunk = loadedChunks.get(ck);
    return !!chunk?.ready;
  }

  function isWaterLevel(level) {
    return Number.isFinite(level)
      && Math.floor(level) >= WATER_MIN_FLOW_LEVEL
      && Math.floor(level) <= WATER_FALL_LEVEL;
  }

  const fluidVisuals = createFluidVisuals({
    state: fluidState,
    clamp,
    worldToBlock,
    blockGeo,
    lavaLevelGeos: {
      geometryByLevel: LAVA_LEVEL_GEOS,
      heightByLevel: LAVA_LEVEL_HEIGHT_BY_LEVEL
    },
    waterLevelHeightByLevel: WATER_LEVEL_HEIGHT_BY_LEVEL,
    waterSourceLevel: WATER_SOURCE_LEVEL,
    waterMinFlowLevel: WATER_MIN_FLOW_LEVEL,
    lavaSourceLevel: LAVA_SOURCE_LEVEL,
    lavaMinFlowLevel: LAVA_MIN_FLOW_LEVEL,
    waterVisualLerpSpeed: WATER_VISUAL_LERP_SPEED,
    waterVisualUpdateBudgetPerFluidTick: WATER_VISUAL_UPDATE_BUDGET_PER_FLUID_TICK
  });

  const fluidSim = createFluidSimulation({
    state: fluidState,
    config: {
      waterSourceLevel: WATER_SOURCE_LEVEL,
      waterFallLevel: WATER_FALL_LEVEL,
      waterMinFlowLevel: WATER_MIN_FLOW_LEVEL,
      waterTickInterval: WATER_TICK_INTERVAL,
      waterMaxTicksPerFrame: WATER_MAX_TICKS_PER_FRAME,
      waterDirtyBudgetPerTick: WATER_DIRTY_BUDGET_PER_TICK,
      waterSeedScanBudgetPerFrame: WATER_SEED_SCAN_BUDGET_PER_FRAME,
      lavaSourceLevel: LAVA_SOURCE_LEVEL,
      lavaMinFlowLevel: LAVA_MIN_FLOW_LEVEL,
      lavaMaxLateralSpread: LAVA_MAX_LATERAL_SPREAD,
      lavaTickInterval: LAVA_TICK_INTERVAL,
      lavaMaxTicksPerFrame: LAVA_MAX_TICKS_PER_FRAME,
      lavaDirtyBudgetPerTick: LAVA_DIRTY_BUDGET_PER_TICK,
      lavaSeedScanBudgetPerFrame: LAVA_SEED_SCAN_BUDGET_PER_FRAME,
      fluidSeedEdgeMargin: FLUID_SEED_EDGE_MARGIN,
      waterVisualUpdateBudgetPerFluidTick: WATER_VISUAL_UPDATE_BUDGET_PER_FLUID_TICK,
      lavaIgniteScanPerSpread: LAVA_IGNITE_SCAN_PER_SPREAD,
      lavaIgniteChance: LAVA_IGNITE_CHANCE,
      worldMinY: WORLD_MIN_Y,
      chunkSize: CHUNK_SIZE,
      viewDistance: VIEW_DISTANCE,
      waterDirtyOffsets: WATER_DIRTY_OFFSETS,
      waterLateralOffsets: WATER_LATERAL_OFFSETS
    },
    helpers: {
      blockKey,
      worldToBlock,
      worldToChunk,
      parseBlockKey,
      chunkKey,
      getBlockIdAt,
      getBlockAt,
      getBlockRecordAtKey,
      createBlock,
      removeBlock,
      isChunkReadyAt,
      hasWaterContact,
      hasLavaContact,
      tryIgniteFromLavaAt,
      getActiveViewDistance: () => activeViewDistance,
      loadedChunks,
      solidBlocks,
      blockData,
      onLavaHardened() {
        if (typeof window !== 'undefined' && window.sounds?.sizzle) {
          window.sounds.sizzle();
        }
      }
    },
    visuals: fluidVisuals
  });

  function normalizeWaterLevel(levelRaw) {
    return fluidVisuals.normalizeWaterLevel(levelRaw);
  }

  function getWaterHeightForLevel(level = WATER_SOURCE_LEVEL) {
    return fluidVisuals.getWaterHeightForLevel(level);
  }

  function getLavaHeightForLevel(level = LAVA_SOURCE_LEVEL) {
    return fluidVisuals.getLavaHeightForLevel(level);
  }

  function getWaterGeometryForLevel(level = WATER_SOURCE_LEVEL) {
    return fluidVisuals.getWaterGeometryForLevel(level);
  }

  function getLavaGeometryForLevel(level = LAVA_SOURCE_LEVEL) {
    return fluidVisuals.getLavaGeometryForLevel(level);
  }

  function applyWaterVisualHeight(mesh, level = WATER_SOURCE_LEVEL, immediate = true) {
    return fluidVisuals.applyWaterVisualHeight(mesh, level, immediate);
  }

  function applyLavaVisualHeight(mesh, level = LAVA_SOURCE_LEVEL) {
    return fluidVisuals.applyLavaVisualHeight(mesh, level);
  }

  function applyLavaLightState(mesh, enabled) {
    return fluidVisuals.applyLavaLightState(mesh, enabled);
  }

  function updateWaterVisualAnimations(delta = 0.016, opts = {}) {
    return fluidVisuals.updateWaterVisualAnimations(delta, opts);
  }

  function getWaterLevelAt(x, y, z) {
    return fluidSim.getWaterLevelAt(x, y, z);
  }

  function getLavaLevelAt(x, y, z) {
    return fluidSim.getLavaLevelAt(x, y, z);
  }

  function isLavaSourceAt(x, y, z) {
    return fluidSim.isLavaSourceAt(x, y, z);
  }

  function queueWaterUpdateAt(x, y, z, delay = 0) {
    return fluidSim.queueWaterUpdateAt(x, y, z, delay);
  }

  function queueWaterUpdateAround(x, y, z, delay = 0) {
    return fluidSim.queueWaterUpdateAround(x, y, z, delay);
  }

  function queueLavaUpdateAt(x, y, z, delay = 0) {
    return fluidSim.queueLavaUpdateAt(x, y, z, delay);
  }

  function queueLavaUpdateAround(x, y, z, delay = 0) {
    return fluidSim.queueLavaUpdateAround(x, y, z, delay);
  }

  function enqueueWaterSeedKey(key) {
    return fluidSim.enqueueWaterSeedKey(key);
  }

  function enqueueLavaSeedKey(key) {
    return fluidSim.enqueueLavaSeedKey(key);
  }

  function queueFluidSeedsForChunkAndReadyNeighbors(cx, cz) {
    return fluidSim.queueFluidSeedsForChunkAndReadyNeighbors(cx, cz);
  }

  function updateWaterFlow(delta = 0.016, opts = {}) {
    return fluidSim.updateWaterFlow(delta, opts);
  }

  function sealFluidWithBarrier(x, y, z, fluidId, ck, opts = {}) {
    const barrierId = Number(opts.barrierId) || (fluidId === 7 ? 23 : 5);
    const includeCorners = !!opts.corners;
    const includeFloor = !!opts.floor;
    const offsets = includeCorners
      ? [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]]
      : [[1, 0], [-1, 0], [0, 1], [0, -1]];

    for (const [dx, dz] of offsets) {
      const nx = x + dx;
      const nz = z + dz;
      const id = getBlockIdAt(nx, y, nz);
      const replaceable = id === null || id === 7 || id === 69 || !!blockData[id]?.replaceable;
      if (!replaceable) continue;
      createBlock(nx, y, nz, barrierId, { generated: true, chunkKey: ck, allowReplace: true });
    }

    if (!includeFloor) return;
    const belowId = getBlockIdAt(x, y - 1, z);
    const floorReplaceable = belowId === null || belowId === 7 || belowId === 69 || !!blockData[belowId]?.replaceable;
    if (floorReplaceable) {
      createBlock(x, y - 1, z, barrierId, { generated: true, chunkKey: ck, allowReplace: true });
    }
  }

  function convertAdjacentWaterToGlass(x, y, z) {
    const dirs = [
      [1, 0, 0], [-1, 0, 0],
      [0, 1, 0], [0, -1, 0],
      [0, 0, 1], [0, 0, -1]
    ];

    for (const [dx, dy, dz] of dirs) {
      const nx = x + dx;
      const ny = y + dy;
      const nz = z + dz;
      if (getBlockIdAt(nx, ny, nz) !== 7) continue;
      const nck = chunkKey(worldToChunk(nx), worldToChunk(nz));
      setGeneratedBlockAt(nx, ny, nz, 23, nck);
    }
  }

  function sealFirstWaterAboveAsGlass(x, z, fromY, toY, ck = null) {
    const scanFrom = Math.floor(Math.min(fromY, toY));
    const scanTo = Math.floor(Math.max(fromY, toY));
    const useChunkKey = ck || chunkKey(worldToChunk(x), worldToChunk(z));
    for (let y = scanFrom; y <= scanTo; y++) {
      if (getBlockIdAt(x, y, z) !== 7) continue;
      setGeneratedBlockAt(x, y, z, 23, useChunkKey);
      return y;
    }
    return null;
  }

  function isLeafDecayEligible(mesh) {
    return !!mesh
      && mesh.userData.id === 4
      && (mesh.userData.generated || mesh.userData.leafDecayEligible === true);
  }

  function queueLeafForDecay(x, y, z) {
    const key = blockKey(x, y, z);
    const mesh = solidBlocks.get(key);
    if (!isLeafDecayEligible(mesh)) return;
    pendingLeafDecay.add(key);
  }

  function queueNearbyLeavesForDecay(x, y, z, radius = LEAF_DECAY_RADIUS) {
    for (let lx = x - radius; lx <= x + radius; lx++) {
      for (let ly = y - radius; ly <= y + radius; ly++) {
        for (let lz = z - radius; lz <= z + radius; lz++) {
          queueLeafForDecay(lx, ly, lz);
        }
      }
    }
  }

  function hasNearbyLog(x, y, z, radius = LEAF_DECAY_RADIUS) {
    for (let lx = x - radius; lx <= x + radius; lx++) {
      for (let ly = y - radius; ly <= y + radius; ly++) {
        for (let lz = z - radius; lz <= z + radius; lz++) {
          if (isTreeLogBlockId(getBlockIdAt(lx, ly, lz))) return true;
        }
      }
    }
    return false;
  }

  function hasTrackedTreeLog(leaf) {
    const rootX = Number(leaf?.userData?.treeRootX);
    const rootY = Number(leaf?.userData?.treeRootY);
    const rootZ = Number(leaf?.userData?.treeRootZ);
    if (!Number.isFinite(rootX) || !Number.isFinite(rootY) || !Number.isFinite(rootZ)) return null;

    const preferredTrunkId = Number(leaf?.userData?.treeTrunkBlockId);
    const trunkRadius = Math.max(0, Math.floor(Number(leaf?.userData?.treeTrunkRadius) || 0));
    const trunkTopRaw = Number(leaf?.userData?.treeTrunkTopY);
    const trunkTop = Number.isFinite(trunkTopRaw) ? trunkTopRaw : (rootY + 8);
    const minY = Math.floor(Math.min(rootY, trunkTop));
    const maxY = Math.floor(Math.max(rootY, trunkTop) + 1);
    for (let y = minY; y <= maxY; y++) {
      for (let dx = -trunkRadius; dx <= trunkRadius; dx++) {
        for (let dz = -trunkRadius; dz <= trunkRadius; dz++) {
          const blockId = getBlockIdAt(rootX + dx, y, rootZ + dz);
          if (!isTreeLogBlockId(blockId)) continue;
          if (Number.isFinite(preferredTrunkId) && preferredTrunkId > 0 && blockId !== preferredTrunkId) continue;
          return true;
        }
      }
    }
    return false;
  }

  function processLeafDecayBatch(maxCount = LEAF_DECAY_BATCH) {
    if (pendingLeafDecay.size === 0) return;

    let processed = 0;
    const keys = Array.from(pendingLeafDecay);
    for (const key of keys) {
      pendingLeafDecay.delete(key);
      const leaf = solidBlocks.get(key);
      if (!isLeafDecayEligible(leaf)) continue;

      const pos = parseBlockKey(key);
      const trackedLog = hasTrackedTreeLog(leaf);
      if (trackedLog === true) continue;
      if (trackedLog === null && hasNearbyLog(pos.x, pos.y, pos.z)) continue;

      removeBlock(leaf, { trackModification: true, skipTorchCleanup: true });
      spawnDrop(pos.x, pos.y + 0.16, pos.z, '4', { kind: 'item', color: 0x2f8f33 });
      if (Math.random() < LEAF_SAPLING_DROP_CHANCE) {
        spawnDrop(pos.x, pos.y + 0.16, pos.z, 65, { kind: 'block' });
      }
      if (Math.random() < LEAF_APPLE_DROP_CHANCE) {
        spawnDrop(pos.x, pos.y + 0.16, pos.z, 'apple', { kind: 'item', color: 0xcf3f37 });
      }
      queueNearbyLeavesForDecay(pos.x, pos.y, pos.z, 1);

      processed += 1;
      if (processed >= maxCount) break;
    }
  }

  const OCCLUSION_NEIGHBORS = [
    [1, 0, 0],
    [-1, 0, 0],
    [0, 1, 0],
    [0, -1, 0],
    [0, 0, 1],
    [0, 0, -1]
  ];
  const OCCLUSION_REFRESH_OFFSETS = [
    [0, 0, 0],
    [1, 0, 0],
    [-1, 0, 0],
    [0, 1, 0],
    [0, -1, 0],
    [0, 0, 1],
    [0, 0, -1]
  ];

  function addRaycastObject(mesh) {
    if (!mesh || objectSet.has(mesh)) return;
    objects.push(mesh);
    objectSet.add(mesh);
    objectIndexByUuid.set(mesh.uuid, objects.length - 1);
  }

  function removeRaycastObject(mesh) {
    if (!mesh) return;
    const idx = objectIndexByUuid.get(mesh.uuid);
    if (idx === undefined) {
      objectSet.delete(mesh);
      return;
    }
    const last = objects[objects.length - 1];
    objects.pop();
    objectIndexByUuid.delete(mesh.uuid);
    objectSet.delete(mesh);
    if (last && last !== mesh) {
      objects[idx] = last;
      objectIndexByUuid.set(last.uuid, idx);
    }
  }

  function registerDynamicRaycastObject(mesh) {
    if (!mesh || dynamicRaycastObjects.has(mesh)) return;
    dynamicRaycastObjects.add(mesh);
    addRaycastObject(mesh);
  }

  function unregisterDynamicRaycastObject(mesh) {
    if (!mesh) return;
    dynamicRaycastObjects.delete(mesh);
    removeRaycastObject(mesh);
  }

  function removeMeshFromWorldParent(mesh) {
    if (!mesh) return;
    if (mesh.parent) mesh.parent.remove(mesh);
    else scene.remove(mesh);
  }

  function isOpaqueOccluderId(id) {
    const def = blockData[id];
    if (!def || !def.solid || def.replaceable || def.raycast === false) return false;
    if (def.geo || def.light) return false;
    if (GRAVITY_BLOCK_IDS.has(Number(id))) return false;
    const mat = def.mat;
    if (mat?.transparent) return false;
    if (Number.isFinite(mat?.opacity) && mat.opacity < 0.999) return false;
    if (Number.isFinite(mat?.alphaTest) && mat.alphaTest > 0.0001) return false;
    return true;
  }

  function isCullingCandidateMesh(mesh) {
    if (!mesh?.userData?.generated) return false;
    return isOpaqueOccluderId(mesh.userData.id);
  }

  function isFullyOccludedAt(x, y, z, id) {
    if (!isOpaqueOccluderId(id)) return false;
    for (const [dx, dy, dz] of OCCLUSION_NEIGHBORS) {
      const neighborId = getBlockIdAt(x + dx, y + dy, z + dz);
      if (!isOpaqueOccluderId(neighborId)) return false;
    }
    return true;
  }

  function detachMeshForCulling(mesh, key, chunk = null) {
    if (!mesh || !key) return false;
    const instancedProxy = isInstancedBlockProxy(mesh);
    const record = {
      id: mesh.userData.id,
      generated: !!mesh.userData.generated,
      chunkKey: mesh.userData.chunkKey || null
    };
    solidBlocks.delete(key);
    culledBlocks.set(key, record);
    if (!instancedProxy) {
      removeRaycastObject(mesh);
      if (chunk) chunk.raycastObjects.delete(mesh);
      removeMeshFromWorldParent(mesh);
    } else {
      removeInstancedBlockByKey(key);
    }
    if (chunk) chunk.culledCount = (chunk.culledCount || 0) + 1;
    return true;
  }

  function restoreCulledBlockAt(x, y, z) {
    const gx = worldToBlock(x);
    const gy = worldToBlock(y);
    const gz = worldToBlock(z);
    const key = blockKey(gx, gy, gz);
    const record = culledBlocks.get(key);
    if (!record) return null;
    if (isFullyOccludedAt(gx, gy, gz, record.id)) return null;

    culledBlocks.delete(key);
    const restored = createBlock(gx, gy, gz, record.id, {
      generated: !!record.generated,
      chunkKey: record.chunkKey,
      allowReplace: false
    });
    if (!restored) {
      culledBlocks.set(key, record);
      return null;
    }

    const chunk = record.chunkKey ? loadedChunks.get(record.chunkKey) : null;
    if (chunk) chunk.culledCount = Math.max(0, (chunk.culledCount || 0) - 1);
    return restored;
  }

  function maybeCullVisibleBlockAt(x, y, z) {
    const gx = worldToBlock(x);
    const gy = worldToBlock(y);
    const gz = worldToBlock(z);
    const key = blockKey(gx, gy, gz);
    const mesh = solidBlocks.get(key);
    if (!mesh || !isCullingCandidateMesh(mesh)) return false;
    if (!isFullyOccludedAt(gx, gy, gz, mesh.userData.id)) return false;
    const chunk = mesh.userData.chunkKey ? loadedChunks.get(mesh.userData.chunkKey) : null;
    return detachMeshForCulling(mesh, key, chunk);
  }

  function refreshOcclusionAround(x, y, z) {
    const gx = worldToBlock(x);
    const gy = worldToBlock(y);
    const gz = worldToBlock(z);

    for (const [dx, dy, dz] of OCCLUSION_REFRESH_OFFSETS) {
      restoreCulledBlockAt(gx + dx, gy + dy, gz + dz);
    }
    for (const [dx, dy, dz] of OCCLUSION_REFRESH_OFFSETS) {
      maybeCullVisibleBlockAt(gx + dx, gy + dy, gz + dz);
    }
  }

  function cullChunkInterior(ck) {
    const chunk = loadedChunks.get(ck);
    if (!chunk) return;
    const keys = Array.from(chunk.blockKeys);
    for (const key of keys) {
      const mesh = solidBlocks.get(key);
      if (!mesh || !isCullingCandidateMesh(mesh)) continue;
      const pos = parseBlockKey(key);
      if (!isFullyOccludedAt(pos.x, pos.y, pos.z, mesh.userData.id)) continue;
      detachMeshForCulling(mesh, key, chunk);
    }
  }

  function enqueueChunkInteriorCull(ck) {
    if (pendingCullChunkKeys.has(ck)) return;
    pendingCullChunkKeys.add(ck);
    pendingCullChunkQueue.push(ck);
  }

  function enqueueChunkNeighborhoodCull(cx, cz) {
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        enqueueChunkInteriorCull(chunkKey(cx + dx, cz + dz));
      }
    }
  }

  function processPendingChunkCulls(maxChunks = 2) {
    let budget = Math.max(0, Math.floor(maxChunks));
    while (budget > 0 && pendingCullChunkQueue.length > 0) {
      const ck = pendingCullChunkQueue.shift();
      pendingCullChunkKeys.delete(ck);
      cullChunkInterior(ck);
      budget -= 1;
    }
  }

  function updatePrefetchTuningForViewDistance(viewDistance) {
    if (!columnPrefetch || typeof columnPrefetch.setRuntimeTuning !== 'function') return;
    const vd = clamp(Math.floor(Number(viewDistance) || activeViewDistance), MIN_VIEW_DISTANCE, MAX_VIEW_DISTANCE);
    const prefetchRadius = vd + 1;
    const side = (prefetchRadius * 2) + 1;
    const tunedCache = clamp(Math.ceil(side * side * 1.65), 72, 320);
    const tunedInFlight = prefetchRadius >= 8
      ? 6
      : prefetchRadius >= 6
        ? 5
        : prefetchRadius >= 4
          ? 4
          : 3;
    if (tunedCache === lastPrefetchCacheChunks && tunedInFlight === lastPrefetchInFlight) return;
    lastPrefetchCacheChunks = tunedCache;
    lastPrefetchInFlight = tunedInFlight;
    columnPrefetch.setRuntimeTuning({
      maxCacheChunks: tunedCache,
      maxInFlight: tunedInFlight
    });
  }

  function addMeshToWorld(mesh) {
    const x = worldToBlock(mesh.position.x);
    const y = worldToBlock(mesh.position.y);
    const z = worldToBlock(mesh.position.z);
    const key = blockKey(x, y, z);
    solidBlocks.set(key, mesh);
    if (mesh.userData.raycast !== false) {
      addRaycastObject(mesh);
    }
    const chunk = mesh.userData.chunkKey ? loadedChunks.get(mesh.userData.chunkKey) : null;
    if (chunk?.group) {
      chunk.group.add(mesh);
      chunk.group.visible = chunk.visible !== false;
    } else {
      scene.add(mesh);
    }
    if (chunk) {
      chunk.blockKeys.add(key);
      culledBlocks.delete(key);
      if (mesh.userData.detailCullDistance > 0) {
        chunk.detailMeshes.add(mesh);
      }
      if (mesh.userData.raycast !== false) {
        chunk.raycastObjects.add(mesh);
      }
    }
  }

  function createBlock(x, y, z, typeId, opts = {}) {
    const gx = worldToBlock(x);
    const gy = worldToBlock(y);
    const gz = worldToBlock(z);
    const key = blockKey(gx, gy, gz);
    const def = blockData[typeId];
    if (!def) return null;

    const existing = solidBlocks.get(key);
    const existingCulled = culledBlocks.get(key);
    const existingId = existing ? existing.userData.id : (existingCulled ? existingCulled.id : null);
    if (typeId === 12 && (existingId === 7 || existingId === 69)) return null;
    if (typeId === 12 && !hasTorchSupport(gx, gy, gz)) return null;
    if (typeId === 64 && !isSolidAt(gx, gy - 1, gz)) return null;
    if ((typeId === 66 || typeId === 67) && !isSolidAt(gx, gy - 1, gz)) return null;
    if (typeId === 65 && !isTreeSoilBlockId(getBlockIdAt(gx, gy - 1, gz))) return null;
    if ((typeId === 58 || typeId === 59 || typeId === 60 || typeId === 61) && !isSolidAt(gx, gy - 1, gz)) return null;
    if ((typeId === 50 || typeId === 51 || typeId === 52 || typeId === 53) && getBlockIdAt(gx, gy - 1, gz) !== 49) return null;
    if (typeId === 49 && !isSolidAt(gx, gy - 1, gz)) return null;

    if (existing || existingCulled) {
      const existingReplaceable = existing
        ? !!existing.userData.replaceable
        : !!blockData[existingCulled.id]?.replaceable;
      if (!existingReplaceable || !opts.allowReplace) return null;

      if (existing) {
        removeBlock(existing, { trackModification: false, skipOcclusionRefresh: true });
      } else if (existingCulled) {
        culledBlocks.delete(key);
        const chunk = existingCulled.chunkKey ? loadedChunks.get(existingCulled.chunkKey) : null;
        if (chunk) chunk.culledCount = Math.max(0, (chunk.culledCount || 0) - 1);
      }
    }

    let waterSource = false;
    let waterAnchoredSource = false;
    let waterLevel = WATER_SOURCE_LEVEL;
    if (typeId === 7) {
      const rawWaterLevel = Number(opts.waterLevel);
      if (Number.isFinite(rawWaterLevel)) {
        waterLevel = normalizeWaterLevel(rawWaterLevel);
      } else if (opts.waterSource === false) {
        waterLevel = 6;
      } else {
        waterLevel = WATER_SOURCE_LEVEL;
      }
      waterSource = waterLevel === WATER_SOURCE_LEVEL;
      waterAnchoredSource = false;
    }
    let lavaSource = false;
    let lavaLevel = LAVA_SOURCE_LEVEL;
    if (typeId === 69) {
      const rawLavaLevel = Number(opts.lavaLevel);
      lavaLevel = Number.isFinite(rawLavaLevel)
        ? clamp(Math.floor(rawLavaLevel), LAVA_MIN_FLOW_LEVEL, LAVA_SOURCE_LEVEL)
        : LAVA_SOURCE_LEVEL;
      if (typeof opts.lavaSource === 'boolean') lavaSource = opts.lavaSource;
      else lavaSource = !Number.isFinite(rawLavaLevel);
      if (lavaSource) lavaLevel = LAVA_SOURCE_LEVEL;
    }

    if (shouldUseInstancedBlock(typeId, def, opts)) {
      const proxy = {
        position: new THREE.Vector3(gx, gy, gz),
        userData: {
          id: typeId,
          hardness: def.hardness,
          solid: !!def.solid,
          mineable: def.mineable !== false,
          replaceable: !!def.replaceable,
          raycast: def.raycast !== false,
          generated: !!opts.generated,
          chunkKey: opts.chunkKey || null,
          instancedProxy: true,
          blockKey: key
        }
      };
      if (typeId === 4) {
        proxy.userData.leafDecayEligible = opts.leafDecayEligible === true || !!opts.generated;
      }
      if (typeId === 4 && Number.isFinite(opts.treeRootX)) {
        proxy.userData.treeRootX = worldToBlock(opts.treeRootX);
        proxy.userData.treeRootY = worldToBlock(opts.treeRootY);
        proxy.userData.treeRootZ = worldToBlock(opts.treeRootZ);
        if (Number.isFinite(opts.treeTrunkTopY)) {
          proxy.userData.treeTrunkTopY = worldToBlock(opts.treeTrunkTopY);
        }
        if (Number.isFinite(opts.treeTrunkRadius)) {
          proxy.userData.treeTrunkRadius = Math.max(0, Math.floor(Number(opts.treeTrunkRadius) || 0));
        }
        if (Number.isFinite(opts.treeTrunkBlockId)) {
          proxy.userData.treeTrunkBlockId = Math.floor(Number(opts.treeTrunkBlockId) || 0);
        }
        if (typeof opts.treeSpecies === 'string' && opts.treeSpecies) {
          proxy.userData.treeSpecies = opts.treeSpecies;
        }
      }
      waterMap.delete(key);
      lavaMap.delete(key);
      lavaSourceSet.delete(key);
      const instanced = addInstancedBlockToWorld(proxy, key, opts);
      if (instanced) {
        if (typeId === SAPLING_BLOCK_ID) saplingKeys.add(key);
        if (typeId === CACTUS_BLOCK_ID) cactusKeys.add(key);
        return instanced;
      }
    }

    const leafVariant = typeId === 4 ? Math.abs(opts.leafVariant || 0) % leavesMatVariants.length : 0;
    const material = (typeId === 4 && leavesMatVariants[leafVariant]) ? leavesMatVariants[leafVariant] : def.mat;
    const geometry = typeId === 7
      ? getWaterGeometryForLevel(waterLevel)
      : (typeId === 69 ? getLavaGeometryForLevel(lavaLevel) : (def.geo || blockGeo));
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(gx, gy, gz);
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    mesh.frustumCulled = true;
    mesh.matrixAutoUpdate = false;
    mesh.userData = {
      id: typeId,
      hardness: def.hardness,
      solid: !!def.solid,
      mineable: def.mineable !== false,
      replaceable: !!def.replaceable,
      raycast: def.raycast !== false,
      generated: !!opts.generated,
      detailCullDistance: getDetailCullDistance(typeId),
      chunkKey: opts.chunkKey || null
    };
    if (typeId === 4) {
      mesh.userData.leafDecayEligible = opts.leafDecayEligible === true || !!opts.generated;
    }
    if (typeId === 7) {
      mesh.userData.waterSource = waterSource;
      mesh.userData.waterAnchoredSource = waterAnchoredSource;
      mesh.userData.waterLevel = waterLevel;
      mesh.userData.waterBaseY = gy;
      mesh.userData.waterVisualHeight = getWaterHeightForLevel(waterLevel);
      mesh.userData.waterVisualTargetHeight = mesh.userData.waterVisualHeight;
      waterMap.set(key, waterLevel);
      lavaMap.delete(key);
      lavaSourceSet.delete(key);
      applyWaterVisualHeight(mesh, waterLevel, true);
    } else if (typeId === 69) {
      mesh.userData.lavaLevel = lavaLevel;
      mesh.userData.lavaSource = lavaSource;
      mesh.userData.lavaVisualHeight = getLavaHeightForLevel(lavaLevel);
      lavaMap.set(key, lavaLevel);
      if (lavaSource) lavaSourceSet.add(key);
      else lavaSourceSet.delete(key);
      waterMap.delete(key);
      applyLavaVisualHeight(mesh, lavaLevel);
      applyLavaLightState(mesh, lavaSource);
    } else {
      waterMap.delete(key);
      lavaMap.delete(key);
      lavaSourceSet.delete(key);
    }
    if (Number.isFinite(opts.treeRootX) && Number.isFinite(opts.treeRootY) && Number.isFinite(opts.treeRootZ)) {
      mesh.userData.treeRootX = worldToBlock(opts.treeRootX);
      mesh.userData.treeRootY = worldToBlock(opts.treeRootY);
      mesh.userData.treeRootZ = worldToBlock(opts.treeRootZ);
      if (Number.isFinite(opts.treeTrunkTopY)) {
        mesh.userData.treeTrunkTopY = worldToBlock(opts.treeTrunkTopY);
      }
      if (Number.isFinite(opts.treeTrunkRadius)) {
        mesh.userData.treeTrunkRadius = Math.max(0, Math.floor(Number(opts.treeTrunkRadius) || 0));
      }
      if (Number.isFinite(opts.treeTrunkBlockId)) {
        mesh.userData.treeTrunkBlockId = Math.floor(Number(opts.treeTrunkBlockId) || 0);
      }
      if (typeof opts.treeSpecies === 'string' && opts.treeSpecies) {
        mesh.userData.treeSpecies = opts.treeSpecies;
      }
    }

    if (typeId === 64) {
      const duration = Number.isFinite(opts.fireDuration)
        ? Math.max(0.35, opts.fireDuration)
        : (5.4 + Math.random() * 2.8);
      fireExpireAt.set(key, performance.now() / 1000 + duration);
    }

    if (def.light && shouldUseRuntimePointLight(typeId)) {
      if (activeLightMeshes.length < MAX_ACTIVE_POINT_LIGHTS) {
        const torchLight = new THREE.PointLight(
          def.light.color,
          def.light.intensity,
          def.light.distance,
          Number.isFinite(Number(def.light.decay)) ? Number(def.light.decay) : 2
        );
        torchLight.position.set(0, 0.18, 0);
        mesh.add(torchLight);
        mesh.userData.hasActiveLight = true;
        activeLightMeshes.push(mesh);
      } else {
        mesh.userData.hasActiveLight = false;
        mesh.userData.deferredLight = def.light;
        deferredLightMeshes.push(mesh);
      }
      lightBudgetDirty = true;
    }

    mesh.updateMatrix();
    addMeshToWorld(mesh);
    if (typeId === SAPLING_BLOCK_ID) saplingKeys.add(key);
    if (typeId === CACTUS_BLOCK_ID) cactusKeys.add(key);
    return mesh;
  }

  function cleanupUnsupportedTorchesAround(x, y, z, trackModification) {
    const checks = [
      [x, y, z],
      [x, y + 1, z],
      [x + 1, y, z],
      [x - 1, y, z],
      [x, y, z + 1],
      [x, y, z - 1]
    ];

    for (const [tx, ty, tz] of checks) {
      const maybeTorch = getBlockAt(tx, ty, tz);
      if (!maybeTorch || maybeTorch.userData.id !== 12) continue;
      if (hasTorchSupport(tx, ty, tz)) continue;
      spawnDrop(tx, ty + 0.16, tz, 12, { kind: 'block' });
      removeBlock(maybeTorch, { trackModification, skipTorchCleanup: true });
    }
  }

  function removeBlock(mesh, opts = {}) {
    if (!mesh) return;
    const instancedProxy = isInstancedBlockProxy(mesh);
    const {
      trackModification = false,
      skipTorchCleanup = false,
      skipOcclusionRefresh = false
    } = opts;
    const x = worldToBlock(mesh.position.x);
    const y = worldToBlock(mesh.position.y);
    const z = worldToBlock(mesh.position.z);
    const key = blockKey(x, y, z);
    const removedId = mesh.userData.id;
    solidBlocks.delete(key);
    if (removedId === SAPLING_BLOCK_ID) saplingKeys.delete(key);
    if (removedId === CACTUS_BLOCK_ID) {
      cactusKeys.delete(key);
      cactusGrowthProgress.delete(key);
      cactusTrashProgress.delete(key);
    }
    if (removedId === 64) fireExpireAt.delete(key);
    if (removedId === 7) {
      waterMap.delete(key);
      animatingWaterMeshes.delete(mesh);
    }
    if (removedId === 69) {
      lavaMap.delete(key);
      lavaSourceSet.delete(key);
    }

    if (!instancedProxy) removeRaycastObject(mesh);

    if (mesh.userData.hasActiveLight) {
      const idx = activeLightMeshes.indexOf(mesh);
      if (idx >= 0) activeLightMeshes.splice(idx, 1);
      lightBudgetDirty = true;
    } else if (mesh.userData.deferredLight) {
      const idx = deferredLightMeshes.indexOf(mesh);
      if (idx >= 0) deferredLightMeshes.splice(idx, 1);
    }

    if (mesh.userData.chunkKey) {
      const chunk = loadedChunks.get(mesh.userData.chunkKey);
      if (chunk) {
        chunk.blockKeys.delete(key);
        if (mesh.userData.detailCullDistance > 0) {
          chunk.detailMeshes.delete(mesh);
        }
        if (!instancedProxy) chunk.raycastObjects.delete(mesh);
      }
    }

    if (trackModification) {
      if (mesh.userData.generated) {
        manualRemoved.add(key);
      } else {
        unregisterManualPlacement(key);
      }
    }

    if (trackModification && isTreeLogBlockId(removedId)) {
      queueNearbyLeavesForDecay(x, y, z);
    }

    if (trackModification && (removedId === 7 || hasWaterContact(x, y, z))) {
      queueWaterUpdateAround(x, y, z);
    }
    if (trackModification && (removedId === 69 || hasLavaContact(x, y, z))) {
      queueLavaUpdateAround(x, y, z);
    }

    if (instancedProxy) {
      removeInstancedBlockByKey(key);
    } else {
      removeMeshFromWorldParent(mesh);
    }

    if (!skipOcclusionRefresh) refreshOcclusionAround(x, y, z);

    if (!skipTorchCleanup && trackModification) {
      cleanupUnsupportedTorchesAround(x, y, z, trackModification);
    }
  }

  function refreshLightBudget(playerPos) {
    if (!lightBudgetDirty && activeLightMeshes.length <= MAX_ACTIVE_POINT_LIGHTS) return;
    lightBudgetDirty = false;

    for (let i = activeLightMeshes.length - 1; i >= 0; i--) {
      const m = activeLightMeshes[i];
      if (!m || !m.parent) {
        activeLightMeshes.splice(i, 1);
      }
    }
    for (let i = deferredLightMeshes.length - 1; i >= 0; i--) {
      const m = deferredLightMeshes[i];
      if (!m || !m.parent) {
        deferredLightMeshes.splice(i, 1);
      }
    }

    if (!playerPos) return;
    const px = playerPos.x;
    const pz = playerPos.z;

    const allLights = [];
    for (const m of activeLightMeshes) {
      const dx = m.position.x - px;
      const dz = m.position.z - pz;
      allLights.push({ mesh: m, distSq: dx * dx + dz * dz, active: true });
    }
    for (const m of deferredLightMeshes) {
      const dx = m.position.x - px;
      const dz = m.position.z - pz;
      allLights.push({ mesh: m, distSq: dx * dx + dz * dz, active: false });
    }

    allLights.sort((a, b) => a.distSq - b.distSq);

    activeLightMeshes.length = 0;
    deferredLightMeshes.length = 0;

    for (let i = 0; i < allLights.length; i++) {
      const entry = allLights[i];
      const m = entry.mesh;
      if (i < MAX_ACTIVE_POINT_LIGHTS) {
        if (!entry.active && m.userData.deferredLight) {
          const lightDef = m.userData.deferredLight;
          const torchLight = new THREE.PointLight(
            lightDef.color,
            lightDef.intensity,
            lightDef.distance,
            Number.isFinite(Number(lightDef.decay)) ? Number(lightDef.decay) : 2
          );
          torchLight.position.set(0, 0.18, 0);
          m.add(torchLight);
          m.userData.hasActiveLight = true;
          delete m.userData.deferredLight;
        }
        activeLightMeshes.push(m);
      } else {
        if (entry.active) {
          const lightDef = blockData[m.userData.id]?.light;
          for (let c = m.children.length - 1; c >= 0; c--) {
            if (m.children[c] instanceof THREE.PointLight) {
              m.remove(m.children[c]);
            }
          }
          m.userData.hasActiveLight = false;
          if (lightDef) m.userData.deferredLight = lightDef;
        }
        deferredLightMeshes.push(m);
      }
    }
  }

  function isGravityBlockId(id) {
    return GRAVITY_BLOCK_IDS.has(Number(id));
  }

  function spawnFallingGravityBlock(x, y, z, blockId, chunkRef = null) {
    const def = blockData[blockId];
    if (!def) return;
    const mesh = new THREE.Mesh(def.geo || blockGeo, def.mat);
    mesh.position.set(x, y, z);
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    mesh.frustumCulled = true;
    mesh.matrixAutoUpdate = true;
    scene.add(mesh);
    fallingBlocks.push({
      mesh,
      id: blockId,
      chunkKey: chunkRef || null,
      velocityY: 0
    });
  }

  function finalizeFallingGravityBlock(entry, targetX, targetY, targetZ) {
    const existing = getBlockAt(targetX, targetY, targetZ);
    if (existing) {
      if (existing.userData.solid && !existing.userData.replaceable) {
        const def = blockData[entry.id];
        spawnDrop(targetX, targetY + 0.18, targetZ, String(entry.id), {
          kind: 'item',
          color: def?.color
        });
        return false;
      }
      removeBlock(existing, { trackModification: true });
    }

    const placed = placeBlock(targetX, targetY, targetZ, entry.id);
    if (!placed) {
      const def = blockData[entry.id];
      spawnDrop(targetX, targetY + 0.18, targetZ, String(entry.id), {
        kind: 'item',
        color: def?.color
      });
      return false;
    }
    return true;
  }

  function updateFallingGravityBlocks(delta = 0.016) {
    if (fallingBlocks.length === 0) return;
    const dt = Math.max(0.001, Math.min(0.05, delta));

    for (let i = fallingBlocks.length - 1; i >= 0; i--) {
      const entry = fallingBlocks[i];
      const mesh = entry.mesh;
      if (!mesh) {
        fallingBlocks.splice(i, 1);
        continue;
      }

      entry.velocityY = Math.max(-DROP_TERMINAL_VEL, entry.velocityY - DROP_GRAVITY * dt);
      const currentY = mesh.position.y;
      const nextY = currentY + entry.velocityY * dt;
      const bx = worldToBlock(mesh.position.x);
      const bz = worldToBlock(mesh.position.z);

      let landed = false;
      const travel = nextY - currentY;
      const steps = Math.max(1, Math.ceil(Math.abs(travel) / 0.2));
      let sampleY = currentY;
      const stepDelta = travel / steps;
      for (let s = 0; s < steps; s++) {
        sampleY += stepDelta;
        const supportY = worldToBlock(sampleY - 0.5);
        if (supportY <= WORLD_MIN_Y) {
          const finalY = WORLD_MIN_Y + 1;
          finalizeFallingGravityBlock(entry, bx, finalY, bz);
          landed = true;
          break;
        }
        const below = getBlockAt(bx, supportY, bz);
        if (below && below.userData.solid && !below.userData.replaceable) {
          const finalY = supportY + 1;
          finalizeFallingGravityBlock(entry, bx, finalY, bz);
          landed = true;
          break;
        }
      }

      if (landed) {
        scene.remove(mesh);
        fallingBlocks.splice(i, 1);
        continue;
      }

      mesh.position.y = nextY;
    }
  }

  function settleGravityColumn(x, z, fromY, toY) {
    const gx = worldToBlock(x);
    const gz = worldToBlock(z);
    const minY = Math.floor(Math.min(fromY, toY));
    const maxY = Math.floor(Math.max(fromY, toY));
    let changed = false;

    for (let pass = 0; pass < 6; pass++) {
      let movedInPass = false;

      for (let y = minY; y <= maxY; y++) {
        const block = getBlockAt(gx, y, gz);
        if (!block || !isGravityBlockId(block.userData.id)) continue;

        let targetY = y;
        while (targetY > WORLD_MIN_Y) {
          const below = getBlockAt(gx, targetY - 1, gz);
          if (below && below.userData.solid && !below.userData.replaceable) break;
          targetY -= 1;
        }
        if (targetY === y) continue;

        const movedId = block.userData.id;
        const sourceChunkKey = block.userData.chunkKey || chunkKey(worldToChunk(gx), worldToChunk(gz));
        removeBlock(block, { trackModification: true });
        spawnFallingGravityBlock(gx, y, gz, movedId, sourceChunkKey);
        movedInPass = true;
        changed = true;
      }

      if (!movedInPass) break;
    }

    return changed;
  }

  function settleGravityAround(x, y, z, radius = 0) {
    const gx = worldToBlock(x);
    const gy = worldToBlock(y);
    const gz = worldToBlock(z);
    const r = Math.max(0, Math.floor(radius));
    let changed = false;

    for (let dz = -r; dz <= r; dz++) {
      for (let dx = -r; dx <= r; dx++) {
        if (settleGravityColumn(gx + dx, gz + dz, gy - 1, gy + 24)) changed = true;
      }
    }

    return changed;
  }

  function placeBlock(x, y, z, typeId) {
    const gx = worldToBlock(x);
    const gy = worldToBlock(y);
    const gz = worldToBlock(z);
    const ck = chunkKey(worldToChunk(gx), worldToChunk(gz));
    if (!loadedChunks.has(ck)) loadChunk(worldToChunk(gx), worldToChunk(gz));
    const block = createBlock(gx, gy, gz, typeId, { generated: false, chunkKey: ck, allowReplace: true });
    if (!block) return null;
    registerManualPlacement(gx, gy, gz, typeId);
    const key = blockKey(gx, gy, gz);
    if (typeId === 7) enqueueWaterSeedKey(key);
    if (typeId === 69) enqueueLavaSeedKey(key);
    refreshOcclusionAround(gx, gy, gz);
    if (typeId === 7 || hasWaterContact(gx, gy, gz)) queueWaterUpdateAround(gx, gy, gz);
    if (typeId === 69 || hasLavaContact(gx, gy, gz)) queueLavaUpdateAround(gx, gy, gz);
    return block;
  }

  function colorFromId(id) {
    const str = String(id);
    let hash = 2166136261;
    for (let i = 0; i < str.length; i++) {
      hash ^= str.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }
    const r = 96 + ((hash >>> 0) & 0x7f);
    const g = 96 + ((hash >>> 7) & 0x7f);
    const b = 96 + ((hash >>> 14) & 0x7f);
    return (r << 16) | (g << 8) | b;
  }

  const dropMaterialCache = new Map();

  function getDropGeometry(kind = 'block') {
    return kind === 'xp' ? dropXpGeo : dropGeo;
  }

  function getDropMaterial(dropId, kind = 'block', color = null) {
    if (kind === 'block' && blockData[dropId]) return blockData[dropId].mat;

    if (kind === 'xp') {
      const resolved = color || 0x78f56d;
      const key = `xp:${resolved.toString(16)}`;
      if (!dropMaterialCache.has(key)) {
        dropMaterialCache.set(key, new THREE.MeshStandardMaterial({
          color: resolved,
          emissive: resolved,
          emissiveIntensity: 0.42,
          roughness: 0.34,
          metalness: 0.0
        }));
      }
      return dropMaterialCache.get(key);
    }

    const resolved = color || colorFromId(dropId);
    const key = `item:${dropId}:${resolved.toString(16)}`;
    if (!dropMaterialCache.has(key)) {
      dropMaterialCache.set(key, new THREE.MeshStandardMaterial({
        color: resolved,
        roughness: 0.62,
        metalness: 0.05
      }));
    }
    return dropMaterialCache.get(key);
  }

  function acquireDropMesh(dropId, kind = 'block', color = null) {
    const mesh = dropPool.pop() || new THREE.Mesh(getDropGeometry(kind), getDropMaterial(dropId, kind, color));
    mesh.geometry = getDropGeometry(kind);
    mesh.material = getDropMaterial(dropId, kind, color);
    mesh.visible = true;
    mesh.scale.setScalar(1);
    mesh.rotation.set(0, 0, 0);
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    return mesh;
  }

  function recycleDropMesh(mesh) {
    if (!mesh) return;
    scene.remove(mesh);
    mesh.visible = false;
    mesh.userData = {};
    if (dropPool.length < DROP_POOL_LIMIT) {
      dropPool.push(mesh);
    }
  }

  function resolveDropStackLimit(kind = 'block') {
    return Math.max(1, Math.floor(Number(DROP_STACK_LIMITS[kind]) || DROP_STACK_LIMITS.block));
  }

  function canMergeDropStack(
    userData,
    dropId,
    kind,
    source,
    colorKey,
    baseScale,
    stackLimit
  ) {
    if (!userData || userData.mode === 'magnet') return false;
    if (userData.kind !== kind) return false;
    if (userData.dropId !== dropId) return false;
    if ((userData.source || '') !== source) return false;
    if ((userData.dropColorKey || '') !== colorKey) return false;
    if (Math.abs((Number(userData.baseScale) || 1) - baseScale) > 0.001) return false;
    if ((Number(userData.stackLimit) || resolveDropStackLimit(kind)) !== stackLimit) return false;
    return true;
  }

  function tryMergeNearbyDropStacks(
    x,
    y,
    z,
    dropId,
    count,
    { kind = 'block', source = '', colorKey = '', baseScale = 1, stackLimit = resolveDropStackLimit(kind) } = {}
  ) {
    let remaining = Math.max(0, Math.floor(Number(count) || 0));
    if (remaining <= 0 || drops.length <= 0) return remaining;

    let scanned = 0;
    for (let i = drops.length - 1; i >= 0 && remaining > 0 && scanned < DROP_MERGE_SCAN_LIMIT; i--) {
      scanned += 1;
      const drop = drops[i];
      const userData = drop?.userData || null;
      if (!canMergeDropStack(userData, dropId, kind, source, colorKey, baseScale, stackLimit)) continue;
      const dx = drop.position.x - x;
      const dy = drop.position.y - y;
      const dz = drop.position.z - z;
      if (((dx * dx) + (dy * dy) + (dz * dz)) > DROP_STACK_MERGE_RADIUS_SQ) continue;

      const existingCount = Math.max(1, Math.floor(Number(userData.stackCount) || 1));
      const free = Math.max(0, stackLimit - existingCount);
      if (free <= 0) continue;

      const merged = Math.min(free, remaining);
      userData.stackCount = existingCount + merged;
      userData.sleeping = false;
      remaining -= merged;
    }

    return remaining;
  }

  function spawnDrop(x, y, z, dropId, opts = {}) {
    const kind = opts.kind || 'block';
    const color = opts.color || null;
    const count = Math.max(1, Math.floor(opts.count || 1));
    const baseScale = Number.isFinite(Number(opts.scale))
      ? clamp(Number(opts.scale), 0.18, 2.5)
      : 1;
    const source = typeof opts.source === 'string' ? opts.source : '';
    const pickupCooldown = Math.max(0, Number(opts.pickupCooldown) || 0);
    const spawnSpreadHorizontal = Number.isFinite(Number(opts.spawnSpreadHorizontal))
      ? Math.max(0, Number(opts.spawnSpreadHorizontal))
      : 0.14;
    const spawnSpreadVertical = Number.isFinite(Number(opts.spawnSpreadVertical))
      ? Math.max(0, Number(opts.spawnSpreadVertical))
      : 0.2;
    const hasCustomVx = Number.isFinite(Number(opts.vx));
    const hasCustomVy = Number.isFinite(Number(opts.vy));
    const hasCustomVz = Number.isFinite(Number(opts.vz));
    const colorKey = color === null || color === undefined ? '' : String(color);
    const stackLimit = Math.max(1, Math.floor(Number(opts.stackLimit) || resolveDropStackLimit(kind)));
    let remainingCount = tryMergeNearbyDropStacks(x, y, z, dropId, count, {
      kind,
      source,
      colorKey,
      baseScale,
      stackLimit
    });
    if (remainingCount <= 0) return;

    const availableSlots = Math.max(0, MAX_ACTIVE_DROPS - drops.length);
    const spawnCount = Math.min(Math.ceil(remainingCount / stackLimit), availableSlots);
    if (spawnCount <= 0) return;

    const halfSize = (kind === 'xp' ? DROP_HALF_SIZE_XP : DROP_HALF_SIZE) * baseScale;
    const groundOffset = 0.5 + halfSize;

    for (let i = 0; i < spawnCount; i++) {
      const stackCount = Math.min(stackLimit, remainingCount);
      remainingCount -= stackCount;
      const mesh = acquireDropMesh(dropId, kind, color);
      mesh.scale.setScalar(baseScale);
      mesh.position.set(
        x + (Math.random() * (spawnSpreadHorizontal * 2) - spawnSpreadHorizontal),
        y + (Math.random() * spawnSpreadVertical),
        z + (Math.random() * (spawnSpreadHorizontal * 2) - spawnSpreadHorizontal)
      );
      const baseVx = hasCustomVx ? Number(opts.vx) + (Math.random() * 0.18 - 0.09) : (Math.random() * 2 - 1) * 0.95;
      const baseVz = hasCustomVz ? Number(opts.vz) + (Math.random() * 0.18 - 0.09) : (Math.random() * 2 - 1) * 0.95;
      const baseVy = hasCustomVy ? Number(opts.vy) + (Math.random() * 0.12 - 0.06) : 1.2 + Math.random() * 0.95;
      const initialSpeed = Math.sqrt((baseVx * baseVx) + (baseVy * baseVy) + (baseVz * baseVz));
      const spinScale = THREE.MathUtils.clamp(0.55 + initialSpeed * 0.22, 0.6, 2.4);
      mesh.userData = {
        dropId,
        kind,
        source,
        phase: Math.random() * Math.PI * 2,
        mode: 'idle',
        speed: 0,
        vx: baseVx,
        vz: baseVz,
        vy: baseVy,
        spinX: (Math.random() * 2 - 1) * 4.1 * spinScale,
        spinZ: (Math.random() * 2 - 1) * 4.1 * spinScale,
        grounded: false,
        groundY: mesh.position.y,
        halfSize,
        groundOffset,
        baseScale,
        pickupCooldown,
        stackCount,
        stackLimit,
        dropColorKey: colorKey,
        sleeping: false
      };
      scene.add(mesh);
      drops.push(mesh);
    }
  }

  function updateDrops(time, delta, playerPos, onPickup) {
    for (let i = drops.length - 1; i >= 0; i--) {
      const drop = drops[i];
      const userData = drop.userData;
      const halfSize = Number(userData.halfSize) || DROP_HALF_SIZE;
      const groundOffset = Number(userData.groundOffset) || DROP_GROUND_OFFSET;
      const baseScale = Number(userData.baseScale) || 1;
      const stackCount = Math.max(1, Math.floor(Number(userData.stackCount) || 1));
      userData.pickupCooldown = Math.max(0, (userData.pickupCooldown || 0) - delta);

      const playerDx = playerPos.x - drop.position.x;
      const playerDy = Math.abs(playerPos.y - drop.position.y);
      const playerDz = playerPos.z - drop.position.z;
      const playerDistSq = (playerDx * playerDx) + (playerDz * playerDz);

      if (userData.sleeping) {
        if (playerDy > DROP_SLEEP_VERTICAL_RANGE || playerDistSq > DROP_WAKE_DISTANCE_SQ) continue;
        userData.sleeping = false;
      }

      if (userData.mode === 'idle') {
        drop.rotation.y += 2.5 * delta;
        drop.rotation.x += (userData.spinX || 0) * delta;
        drop.rotation.z += (userData.spinZ || 0) * delta;

        userData.vy = Math.max(-DROP_TERMINAL_VEL, userData.vy - DROP_GRAVITY * delta);

        const damp = userData.grounded ? 8.2 : 1.2;
        const drag = Math.max(0, 1 - damp * delta);
        userData.vx *= drag;
        userData.vz *= drag;
        const spinDamp = userData.grounded ? 6.8 : 1.45;
        const spinDrag = Math.max(0, 1 - spinDamp * delta);
        userData.spinX = (userData.spinX || 0) * spinDrag;
        userData.spinZ = (userData.spinZ || 0) * spinDrag;

        drop.position.x += userData.vx * delta;
        drop.position.z += userData.vz * delta;
        drop.position.y += userData.vy * delta;

        const bx = worldToBlock(drop.position.x);
        const by = worldToBlock(drop.position.y);
        const bz = worldToBlock(drop.position.z);

        if (isSolidAt(bx, by, bz) && userData.vy <= 0) {
          userData.grounded = true;
          userData.groundY = by + groundOffset;
          drop.position.y = userData.groundY;
          userData.vy = Math.abs(userData.vy) > 3 ? -userData.vy * 0.22 : 0;
          userData.vx *= 0.72;
          userData.vz *= 0.72;
        } else {
          const supportY = worldToBlock(drop.position.y - (halfSize + 0.02));
          if (isSolidAt(bx, supportY, bz) && userData.vy <= 0) {
            userData.grounded = true;
            userData.groundY = supportY + groundOffset;
            drop.position.y = userData.groundY;
            userData.vy = 0;
          } else {
            userData.grounded = false;
          }
        }

        if (userData.grounded && userData.vy === 0) {
          drop.position.y = userData.groundY + Math.sin(time * 0.003 + userData.phase) * 0.045;
        }

        const dy = Math.abs(playerPos.y - drop.position.y);
        const dx = playerPos.x - drop.position.x;
        const dz = playerPos.z - drop.position.z;
        const distSq = (dx * dx) + (dz * dz);
        if (userData.pickupCooldown <= 0 && dy < 3.3 && distSq < (4.2 * 4.2)) {
          userData.mode = 'magnet';
          userData.speed = 4.2;
        } else if (
          userData.pickupCooldown <= 0
          && userData.grounded
          && userData.vy === 0
          && dy < DROP_SLEEP_VERTICAL_RANGE
          && distSq > DROP_SLEEP_DISTANCE_SQ
          && Math.abs(userData.vx || 0) <= DROP_SLEEP_VELOCITY_EPSILON
          && Math.abs(userData.vz || 0) <= DROP_SLEEP_VELOCITY_EPSILON
          && Math.abs(userData.spinX || 0) <= DROP_SLEEP_SPIN_EPSILON
          && Math.abs(userData.spinZ || 0) <= DROP_SLEEP_SPIN_EPSILON
        ) {
          userData.sleeping = true;
          userData.vx = 0;
          userData.vz = 0;
          userData.spinX = 0;
          userData.spinZ = 0;
          drop.position.y = userData.groundY;
          continue;
        }
      }

      if (userData.mode === 'magnet') {
        dropTarget.set(playerPos.x, playerPos.y - 0.55, playerPos.z);
        const toPlayer = dropTarget.sub(drop.position);
        const dist = toPlayer.length();
        userData.speed = Math.min(22, userData.speed + delta * 28);
        if (dist > 0.0001) {
          toPlayer.multiplyScalar(1 / dist);
          drop.position.addScaledVector(toPlayer, userData.speed * delta);
        }
        const s = clamp(dist * 0.8, 0.15, 1.0);
        drop.scale.setScalar(s * baseScale);
        if (dist < 0.48) {
          const accepted = onPickup({
            id: userData.dropId,
            kind: userData.kind,
            source: userData.source,
            count: stackCount
          });
          let acceptedCount = 0;
          if (accepted === true) acceptedCount = stackCount;
          else if (Number.isFinite(Number(accepted))) {
            acceptedCount = clamp(Math.floor(Number(accepted) || 0), 0, stackCount);
          }

          if (acceptedCount > 0) {
            if (acceptedCount >= stackCount) {
              recycleDropMesh(drop);
              drops.splice(i, 1);
              continue;
            }

            userData.stackCount = stackCount - acceptedCount;
            userData.mode = 'idle';
            userData.speed = 0;
            userData.pickupCooldown = 0.18;
            drop.scale.setScalar(baseScale);
            continue;
          }

          if (accepted !== false && !Number.isFinite(Number(accepted))) {
            recycleDropMesh(drop);
            drops.splice(i, 1);
            continue;
          }

          userData.mode = 'idle';
          userData.speed = 0;
          userData.pickupCooldown = 0.32;
          const awayX = drop.position.x - playerPos.x;
          const awayZ = drop.position.z - playerPos.z;
          const awayLen = Math.hypot(awayX, awayZ) || 1;
          drop.position.x += (awayX / awayLen) * 0.2;
          drop.position.z += (awayZ / awayLen) * 0.2;
          drop.scale.setScalar(baseScale);
        }
      }
    }
  }

  function hasWaterContact(x, y, z) {
    if (isWaterAt(x, y, z)) return true;
    return (
      isWaterAt(x + 1, y, z) ||
      isWaterAt(x - 1, y, z) ||
      isWaterAt(x, y + 1, z) ||
      isWaterAt(x, y - 1, z) ||
      isWaterAt(x, y, z + 1) ||
      isWaterAt(x, y, z - 1)
    );
  }

  function hasLavaContact(x, y, z) {
    if (isLavaAt(x, y, z)) return true;
    return (
      isLavaAt(x + 1, y, z) ||
      isLavaAt(x - 1, y, z) ||
      isLavaAt(x, y + 1, z) ||
      isLavaAt(x, y - 1, z) ||
      isLavaAt(x, y, z + 1) ||
      isLavaAt(x, y, z - 1)
    );
  }

  function isFlammableBlockId(id) {
    return FLAMMABLE_BLOCK_IDS.has(Number(id));
  }

  function tryIgniteFireAt(x, y, z, durationMin = 4.8, durationMax = 8.6) {
    if (!isChunkReadyAt(x, z)) return false;
    const duration = durationMin + Math.random() * Math.max(0.1, durationMax - durationMin);
    return igniteAt(x, y, z, { duration });
  }

  function consumeFlammableBlockAt(x, y, z) {
    const id = getBlockIdAt(x, y, z);
    if (!isFlammableBlockId(id)) return false;
    const block = getBlockAt(x, y, z);
    if (!block) return false;
    removeBlock(block, { trackModification: true });
    return true;
  }

  function tryIgniteFromLavaAt(x, y, z, chance = LAVA_IGNITE_CHANCE) {
    if (getBlockIdAt(x, y, z) !== 69) return false;
    if (Math.random() > chance) return false;

    const start = Math.floor(Math.random() * LAVA_IGNITE_OFFSETS.length);
    for (let i = 0; i < LAVA_IGNITE_OFFSETS.length; i++) {
      const [dx, dy, dz] = LAVA_IGNITE_OFFSETS[(start + i) % LAVA_IGNITE_OFFSETS.length];
      const bx = x + dx;
      const by = y + dy;
      const bz = z + dz;
      const blockId = getBlockIdAt(bx, by, bz);
      if (!isFlammableBlockId(blockId)) continue;
      const def = blockData[blockId];
      const igniteY = def?.solid ? by + 1 : by;
      if (tryIgniteFireAt(bx, igniteY, bz, 5.2, 9.8)) return true;
    }
    return false;
  }

  function spreadFireFrom(x, y, z) {
    const belowId = getBlockIdAt(x, y - 1, z);
    if (isFlammableBlockId(belowId) && Math.random() < FIRE_SPREAD_CONSUME_CHANCE) {
      consumeFlammableBlockAt(x, y - 1, z);
    }

    for (const [dx, dy, dz] of FIRE_SPREAD_BASE_OFFSETS) {
      const bx = x + dx;
      const by = y + dy;
      const bz = z + dz;
      const blockId = getBlockIdAt(bx, by, bz);
      if (!isFlammableBlockId(blockId)) continue;
      if (Math.random() > FIRE_SPREAD_IGNITE_CHANCE) continue;
      const def = blockData[blockId];
      const igniteY = def?.solid ? by + 1 : by;
      tryIgniteFireAt(bx, igniteY, bz, 3.8, 7.6);
    }
  }

  function processLavaSourceIgnitionSweep() {
    const sourceCount = lavaSourceSet.size;
    if (sourceCount <= 0) return;
    const scanCount = Math.min(LAVA_IGNITE_SCAN_PER_SPREAD, sourceCount);
    if (scanCount <= 0) return;

    const startIndex = ((lavaIgniteCursor % sourceCount) + sourceCount) % sourceCount;
    let scanned = 0;
    let index = 0;
    for (const key of lavaSourceSet) {
      if (index < startIndex) {
        index += 1;
        continue;
      }
      const { x, y, z } = parseBlockKey(key);
      if (isChunkReadyAt(x, z)) {
        tryIgniteFromLavaAt(x, y, z, LAVA_IGNITE_CHANCE);
      }
      scanned += 1;
      index += 1;
      if (scanned >= scanCount) break;
    }

    if (scanned < scanCount && startIndex > 0) {
      index = 0;
      for (const key of lavaSourceSet) {
        if (index >= startIndex || scanned >= scanCount) break;
        const { x, y, z } = parseBlockKey(key);
        if (isChunkReadyAt(x, z)) {
          tryIgniteFromLavaAt(x, y, z, LAVA_IGNITE_CHANCE);
        }
        scanned += 1;
        index += 1;
      }
    }

    lavaIgniteCursor = sourceCount > 0 ? (startIndex + scanned) % sourceCount : 0;
  }

  function igniteAt(x, y, z, opts = {}) {
    const gx = worldToBlock(x);
    const gy = worldToBlock(y);
    const gz = worldToBlock(z);
    const existingId = getBlockIdAt(gx, gy, gz);
    if (existingId === 7 || existingId === 69 || existingId === 43 || existingId === 46 || existingId === 64) return false;
    if (!isSolidAt(gx, gy - 1, gz)) return false;
    if (hasWaterContact(gx, gy, gz)) return false;

    const duration = Number.isFinite(opts.duration)
      ? Math.max(0.35, opts.duration)
      : (5.4 + Math.random() * 2.8);
    const created = createBlock(gx, gy, gz, 64, {
      generated: false,
      allowReplace: true,
      fireDuration: duration
    });
    if (!created) return false;
    return true;
  }

  function updateFire(delta = 0.016, time = performance.now() / 1000) {
    const safeDelta = Math.max(0, Number(delta) || 0);
    fireSpreadAccumulator += safeDelta;
    const spreadSteps = Math.min(2, Math.floor(fireSpreadAccumulator / FIRE_SPREAD_INTERVAL));
    if (spreadSteps > 0) fireSpreadAccumulator -= spreadSteps * FIRE_SPREAD_INTERVAL;
    if (spreadSteps > 0) {
      for (let step = 0; step < spreadSteps; step++) {
        processLavaSourceIgnitionSweep();
      }
    }
    if (fireExpireAt.size === 0) return;

    const toExtinguish = [];
    const activeFireCells = [];

    for (const [key, expireAt] of fireExpireAt.entries()) {
      const block = solidBlocks.get(key);
      if (!block || block.userData?.id !== 64) {
        fireExpireAt.delete(key);
        continue;
      }

      const { x, y, z } = parseBlockKey(key);
      const noSupport = !isSolidAt(x, y - 1, z);
      const touchedWater = hasWaterContact(x, y, z);
      const timedOut = time >= expireAt;
      if (noSupport || touchedWater || timedOut) {
        toExtinguish.push(block);
      } else {
        activeFireCells.push({ x, y, z });
      }
    }

    for (const block of toExtinguish) {
      removeBlock(block, { trackModification: false, skipOcclusionRefresh: true });
    }

    if (spreadSteps <= 0 || activeFireCells.length <= 0) return;
    for (let step = 0; step < spreadSteps; step++) {
      for (const cell of activeFireCells) {
        spreadFireFrom(cell.x, cell.y, cell.z);
      }
    }
  }

  function updateCacti(delta = 0.016) {
    cactusGrowthTimer += Math.max(0, Number(delta) || 0);
    if (cactusGrowthTimer < CACTUS_GROWTH_INTERVAL) return;

    let ticks = Math.floor(cactusGrowthTimer / CACTUS_GROWTH_INTERVAL);
    cactusGrowthTimer -= ticks * CACTUS_GROWTH_INTERVAL;
    ticks = Math.min(ticks, 2);
    for (let i = 0; i < ticks; i++) processCactusGrowthBatch();
  }

  function canUseNaturalAt(x, y, z) {
    const key = blockKey(x, y, z);
    return !manualRemoved.has(key) && !manualPlaced.has(key);
  }

  function exportManualState() {
    const placed = [];
    for (const entry of manualPlaced.values()) {
      const liveBlock = getBlockAt(entry.x, entry.y, entry.z);
      const persistedOpts = entry.opts || sanitizeManualPlacementOptions(liveBlock?.userData, entry.id);
      const snapshotEntry = {
        x: entry.x,
        y: entry.y,
        z: entry.z,
        id: entry.id
      };
      if (persistedOpts && Object.keys(persistedOpts).length > 0) {
        snapshotEntry.opts = { ...persistedOpts };
      }
      placed.push(snapshotEntry);
    }

    const removed = [];
    for (const key of manualRemoved.values()) {
      const pos = parseBlockKey(key);
      removed.push({
        x: pos.x,
        y: pos.y,
        z: pos.z
      });
    }

    return { placed, removed };
  }

  function importManualState(snapshot = null) {
    manualPlaced.clear();
    manualByChunk.clear();
    manualRemoved.clear();

    const placed = Array.isArray(snapshot?.placed) ? snapshot.placed : [];
    for (const raw of placed) {
      const x = worldToBlock(Number(raw?.x));
      const y = worldToBlock(Number(raw?.y));
      const z = worldToBlock(Number(raw?.z));
      const id = Number(raw?.id);
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
      if (!blockData[id]) continue;

      const key = blockKey(x, y, z);
      const ck = chunkKey(worldToChunk(x), worldToChunk(z));
      manualPlaced.set(key, {
        x,
        y,
        z,
        id,
        chunkKey: ck,
        opts: sanitizeManualPlacementOptions(raw?.opts, id)
      });
      if (!manualByChunk.has(ck)) manualByChunk.set(ck, new Set());
      manualByChunk.get(ck).add(key);
      manualRemoved.delete(key);
    }

    const removed = Array.isArray(snapshot?.removed) ? snapshot.removed : [];
    for (const raw of removed) {
      const x = worldToBlock(Number(raw?.x));
      const y = worldToBlock(Number(raw?.y));
      const z = worldToBlock(Number(raw?.z));
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
      const key = blockKey(x, y, z);
      if (manualPlaced.has(key)) continue;
      manualRemoved.add(key);
    }
  }

  function clearRuntimeWorld() {
    for (const drop of drops) recycleDropMesh(drop);
    drops.length = 0;
    for (const falling of fallingBlocks) {
      if (falling?.mesh) scene.remove(falling.mesh);
    }
    fallingBlocks.length = 0;

    for (const mesh of solidBlocks.values()) {
      if (isInstancedBlockProxy(mesh)) continue;
      removeMeshFromWorldParent(mesh);
    }
    for (const chunk of loadedChunks.values()) {
      if (chunk?.group?.parent) chunk.group.parent.remove(chunk.group);
    }
    solidBlocks.clear();
    culledBlocks.clear();
    instancedBlockBucketsByChunk.clear();
    instancedBlockIndexByKey.clear();
    instancedBlockMetaByKey.clear();
    objects.length = 0;
    objectSet.clear();
    objectIndexByUuid.clear();
    raycastScratch.length = 0;
    saplingGrowthProgress.clear();
    saplingKeys.clear();
    cactusGrowthProgress.clear();
    cactusTrashProgress.clear();
    cactusKeys.clear();

    loadedChunks.clear();
    chunkQueue.length = 0;
    queuedChunkKeys.clear();
    chunkBuildQueue.length = 0;
    chunkBuildQueuedKeys.clear();
    pendingLeafDecay.clear();
    fireExpireAt.clear();
    fluidState.reset();
    settlementCache.clear();
    poiCache.clear();
    shipwreckCache.clear();
    skyFeatureCache.clear();

    leafDecayTimer = 0;
    saplingGrowthTimer = 0;
    saplingGrowthCursor = 0;
    saplingResyncTimer = 0;
    cactusGrowthTimer = 0;
    cactusGrowthCursor = 0;
    fireSpreadAccumulator = 0;
    lavaIgniteCursor = 0;
    chunkVisibilityTimer = 0;
    lastPrefetchCacheChunks = Number.NaN;
    lastPrefetchInFlight = Number.NaN;
    pendingCullChunkQueue.length = 0;
    pendingCullChunkKeys.clear();
    centerChunkX = Number.NaN;
    centerChunkZ = Number.NaN;
    lastStreamSampleX = Number.NaN;
    lastStreamSampleZ = Number.NaN;
    lastVisibilitySampleX = Number.NaN;
    lastVisibilitySampleZ = Number.NaN;
    lastVisibilityForwardX = Number.NaN;
    lastVisibilityForwardZ = Number.NaN;
  }

  function reloadTerrain(startPos = { x: 0, z: 0 }) {
    clearRuntimeWorld();
    generateTerrain(startPos);
  }

  function createNaturalBlock(x, y, z, typeId, ck, opts = {}) {
    if (!canUseNaturalAt(x, y, z)) return null;
    return createBlock(x, y, z, typeId, { generated: true, chunkKey: ck, ...opts });
  }

  function setGeneratedBlockAt(x, y, z, typeId, ck) {
    if (!canUseNaturalAt(x, y, z)) return null;
    const existing = getBlockAt(x, y, z);
    if (existing) {
      if (!existing.userData.generated) return null;
      removeBlock(existing, { trackModification: false, skipOcclusionRefresh: true });
    }
    return createBlock(x, y, z, typeId, { generated: true, chunkKey: ck, allowReplace: true });
  }

  function clearGeneratedBlockAt(x, y, z) {
    if (!canUseNaturalAt(x, y, z)) return false;
    const existing = getBlockAt(x, y, z);
    if (!existing) return true;
    if (!existing.userData.generated) return false;
    removeBlock(existing, { trackModification: false, skipOcclusionRefresh: true });
    return true;
  }

  function shapeColumnSurface(x, z, surfaceY, targetY, ck, topId, fillId) {
    const dstY = Math.floor(targetY);
    if (dstY > surfaceY) {
      for (let y = surfaceY + 1; y <= dstY; y++) {
        setGeneratedBlockAt(x, y, z, fillId, ck);
      }
    } else if (dstY < surfaceY) {
      for (let y = surfaceY; y > dstY; y--) {
        clearGeneratedBlockAt(x, y, z);
      }
    }
    setGeneratedBlockAt(x, dstY, z, topId, ck);
    return dstY;
  }

  function clearColumnAbove(x, z, fromY, toY) {
    const from = Math.floor(Math.min(fromY, toY));
    const to = Math.floor(Math.max(fromY, toY));
    for (let y = from; y <= to; y++) {
      clearGeneratedBlockAt(x, y, z);
    }
  }

  const {
    applySettlementColumn,
    applyPointOfInterestColumn,
    applyShipwreckColumn,
    applySkyFeatureColumn,
    getChunkStructureGenerationProfiles,
    getSettlementSnapshotAt,
    getPointOfInterestSnapshotAt,
    getShipwreckSnapshotAt,
    getSkyFeatureSnapshotAt
  } = createStructureOps({
    CHUNK_SIZE,
    SETTLEMENT_CELL_SIZE,
    SETTLEMENT_BASE_CHANCE,
    POI_CELL_SIZE,
    POI_BASE_CHANCE,
    SHIPWRECK_CELL_SIZE,
    SHIPWRECK_BASE_CHANCE,
    SKY_FEATURE_CELL_SIZE,
    SKY_FEATURE_BASE_CHANCE,
    structureScale: Number(WORLD_GEN_PROFILE.structureScale) || 1,
    WORLD_MIN_Y,
    WATER_LEVEL,
    settlementCache,
    poiCache,
    shipwreckCache,
    skyFeatureCache,
    hash2i,
    hash3i,
    valueNoise2,
    valueNoise3,
    setBoundedCache,
    clamp,
    smooth,
    lerp,
    columnNoise,
    chooseStoneType,
    worldToChunk,
    chunkKey,
    worldToBlock,
    getDimensionAtCoord,
    getBlockIdAt,
    canUseNaturalAt,
    shapeColumnSurface,
    clearColumnAbove,
    setGeneratedBlockAt,
    clearGeneratedBlockAt,
    createNaturalBlock,
    createBlock,
    sealFluidWithBarrier,
    convertAdjacentWaterToGlass
  });

  function getSaplingGrowthTargetSeconds(x, y, z) {
    const rolled = hash3i(x, y, z, 8811);
    return SAPLING_GROWTH_MIN_SECONDS + (rolled * (SAPLING_GROWTH_MAX_SECONDS - SAPLING_GROWTH_MIN_SECONDS));
  }

  function resyncNearbySaplings(playerPos) {
    if (!playerPos) return;
    const originX = worldToBlock(playerPos.x);
    const originY = worldToBlock(playerPos.y);
    const originZ = worldToBlock(playerPos.z);
    const radius = SAPLING_RESYNC_RADIUS;
    const radiusSq = radius * radius;
    const minY = originY - SAPLING_RESYNC_Y_DOWN;
    const maxY = originY + SAPLING_RESYNC_Y_UP;

    for (let dz = -radius; dz <= radius; dz++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if ((dx * dx) + (dz * dz) > radiusSq) continue;
        const x = originX + dx;
        const z = originZ + dz;
        const ck = chunkKey(worldToChunk(x), worldToChunk(z));
        if (!loadedChunks.has(ck)) continue;
        for (let y = minY; y <= maxY; y++) {
          if (getBlockIdAt(x, y, z) !== SAPLING_BLOCK_ID) continue;
          saplingKeys.add(blockKey(x, y, z));
        }
      }
    }
  }

  function pickLeafVariantByTheme(x, y, z, theme = 'plains', seed = 877) {
    const pool = LEAF_VARIANT_POOL_BY_THEME[theme] || LEAF_VARIANT_POOL_BY_THEME.plains || [0];
    const safePool = Array.isArray(pool) && pool.length > 0 ? pool : [0];
    const idx = Math.min(
      safePool.length - 1,
      Math.max(0, Math.floor(hash3i(x, y, z, seed) * safePool.length))
    );
    return safePool[idx] ?? 0;
  }

  function chooseWeighted(entries, roll = 0) {
    if (!Array.isArray(entries) || entries.length <= 0) return null;
    let total = 0;
    for (const entry of entries) total += Math.max(0, Number(entry?.weight) || 0);
    if (total <= 0) return entries[0];

    let cursor = clamp(Number(roll) || 0, 0, 0.999999) * total;
    for (const entry of entries) {
      const weight = Math.max(0, Number(entry?.weight) || 0);
      if (cursor <= weight) return entry;
      cursor -= weight;
    }
    return entries[entries.length - 1];
  }

  function hasNearbyTreeTrunk(x, y, z, radius = 2) {
    const r = Math.max(1, Math.floor(Number(radius) || 2));
    const radiusSq = r * r;
    for (let dz = -r; dz <= r; dz++) {
      for (let dx = -r; dx <= r; dx++) {
        if (dx === 0 && dz === 0) continue;
        if ((dx * dx) + (dz * dz) > radiusSq) continue;
        for (let dy = 0; dy <= 4; dy++) {
          if (isTreeLogBlockId(getBlockIdAt(x + dx, y + dy, z + dz))) return true;
        }
      }
    }
    return false;
  }

  function buildSaplingTreeLayout(x, y, z, opts = {}) {
    const trunkHeight = Math.max(3, Math.floor(Number(opts?.trunkHeight) || 4));
    const compact = !!opts?.compact;
    const trunkBlockId = Math.floor(Number(opts?.trunkBlockId) || 3);
    const trunkRadius = compact ? 0 : Math.max(0, Math.floor(Number(opts?.trunkRadius) || 0));
    const leafTheme = typeof opts?.leafTheme === 'string' ? opts.leafTheme : 'plains';
    const treeSpecies = typeof opts?.treeSpecies === 'string' ? opts.treeSpecies : '';
    const canopyY = y + trunkHeight - 1;
    const leafVariant = pickLeafVariantByTheme(x, y, z, leafTheme, 877);
    const treeMeta = {
      treeRootX: x,
      treeRootY: y,
      treeRootZ: z,
      treeTrunkTopY: canopyY,
      treeTrunkRadius: trunkRadius,
      treeTrunkBlockId: trunkBlockId,
      treeSpecies
    };
    const leafMeta = { leafVariant, leafDecayEligible: true, ...treeMeta };
    const placements = [];
    const placementKeys = new Set();
    const pushPlacement = (px, py, pz, id, opts = null) => {
      const key = blockKey(px, py, pz);
      if (placementKeys.has(key)) return;
      placementKeys.add(key);
      placements.push({ x: px, y: py, z: pz, id, opts });
    };

    for (let dy = 0; dy < trunkHeight; dy++) {
      for (let tx = -trunkRadius; tx <= trunkRadius; tx++) {
        for (let tz = -trunkRadius; tz <= trunkRadius; tz++) {
          const isCorner = trunkRadius > 0 && Math.abs(tx) === trunkRadius && Math.abs(tz) === trunkRadius;
          if (isCorner && dy > Math.floor(trunkHeight * 0.6) && hash3i(x + tx, y + dy, z + tz, 9079) < 0.52) continue;
          pushPlacement(x + tx, y + dy, z + tz, trunkBlockId, treeMeta);
        }
      }
    }

    if (compact) {
      for (let lx = -1; lx <= 1; lx++) {
        for (let lz = -1; lz <= 1; lz++) {
          const isCorner = Math.abs(lx) === 1 && Math.abs(lz) === 1;
          if (isCorner && hash3i(x + lx, canopyY, z + lz, 9131) < 0.42) continue;
          pushPlacement(x + lx, canopyY, z + lz, 4, leafMeta);
          if (!isCorner) pushPlacement(x + lx, canopyY + 1, z + lz, 4, leafMeta);
        }
      }
      pushPlacement(x, canopyY + 2, z, 4, leafMeta);
      return placements;
    }

    for (let lx = -1; lx <= 1; lx++) {
      for (let lz = -1; lz <= 1; lz++) {
        for (let ly = -1; ly <= 1; ly++) {
          const dist = Math.abs(lx) + Math.abs(lz) + Math.abs(ly) * 0.9;
          if (dist > 2.2) continue;
          pushPlacement(x + lx, canopyY + ly, z + lz, 4, leafMeta);
        }
      }
    }
    pushPlacement(x, canopyY + 2, z, 4, leafMeta);
    return placements;
  }

  function resolveSaplingTreeLayout(x, y, z) {
    const info = getDimensionAtCoord(x, z) === 'overworld'
      ? (getPrefetchedColumnInfoAt(x, z, 'overworld') || columnNoise(x, z))
      : { biome: 'mountain', humidity: 0.42, terrain: 'plains', height: y };
    const species = resolveTreeSpeciesProfile(x, z, info.biome, info.humidity, info.terrain || 'plains', info.height || y);
    const minHeight = Math.max(3, Math.floor(Number(species?.minHeight) || 4));
    const maxHeight = Math.max(minHeight, Math.floor(Number(species?.maxHeight) || minHeight + 1));
    const trunkHeight = minHeight + Math.floor(hash3i(x, y, z, 611) * (maxHeight - minHeight + 1));
    const trunkBlockId = TREE_LOG_BLOCK_ID_BY_SPECIES[species?.id] || 3;
    return buildSaplingTreeLayout(x, y, z, {
      trunkHeight,
      compact: false,
      trunkBlockId,
      trunkRadius: Number(species?.trunkRadius) || 0,
      leafTheme: species?.leafTheme || 'plains',
      treeSpecies: species?.id || ''
    });
  }

  function resolveCompactSaplingTreeLayout(x, y, z) {
    const info = getDimensionAtCoord(x, z) === 'overworld'
      ? (getPrefetchedColumnInfoAt(x, z, 'overworld') || columnNoise(x, z))
      : { biome: 'mountain', humidity: 0.42, terrain: 'plains', height: y };
    const species = resolveTreeSpeciesProfile(x, z, info.biome, info.humidity, info.terrain || 'plains', info.height || y);
    const trunkBlockId = TREE_LOG_BLOCK_ID_BY_SPECIES[species?.id] || 3;
    return buildSaplingTreeLayout(x, y, z, {
      trunkHeight: 3,
      compact: true,
      trunkBlockId,
      leafTheme: species?.leafTheme || 'plains',
      treeSpecies: species?.id || ''
    });
  }

  function canSaplingGrowIntoLayout(saplingX, saplingY, saplingZ, placements) {
    for (const placement of placements) {
      const key = blockKey(placement.x, placement.y, placement.z);
      const existing = getBlockRecordAtKey(key);
      if (!existing) continue;

      const isSaplingRoot = placement.x === saplingX && placement.y === saplingY && placement.z === saplingZ;
      if (isSaplingRoot && existing.id === SAPLING_BLOCK_ID) continue;
      if (!existing.replaceable) return false;
    }
    return true;
  }

  function tryGrowSaplingAt(x, y, z) {
    const gx = worldToBlock(x);
    const gy = worldToBlock(y);
    const gz = worldToBlock(z);
    if (getBlockIdAt(gx, gy, gz) !== SAPLING_BLOCK_ID) return false;
    if (!isTreeSoilBlockId(getBlockIdAt(gx, gy - 1, gz))) return false;
    if (isWaterAt(gx, gy, gz) || isLavaAt(gx, gy, gz)) return false;

    const layouts = [
      resolveSaplingTreeLayout(gx, gy, gz),
      resolveCompactSaplingTreeLayout(gx, gy, gz)
    ];
    for (const placements of layouts) {
      if (!canSaplingGrowIntoLayout(gx, gy, gz, placements)) continue;
      const placed = [];
      let failed = false;

      for (const placement of placements) {
        const ck = chunkKey(worldToChunk(placement.x), worldToChunk(placement.z));
        if (!loadedChunks.has(ck)) {
          loadChunk(worldToChunk(placement.x), worldToChunk(placement.z), { immediate: true });
        }
        const block = createBlock(placement.x, placement.y, placement.z, placement.id, {
          generated: false,
          chunkKey: ck,
          allowReplace: true,
          ...(placement.opts || {})
        });
        if (!block) {
          failed = true;
          break;
        }
        registerManualPlacement(placement.x, placement.y, placement.z, placement.id, placement.opts);
        placed.push(placement);
      }

      if (failed) {
        for (let i = placed.length - 1; i >= 0; i--) {
          const prev = placed[i];
          const prevMesh = getBlockAt(prev.x, prev.y, prev.z);
          if (prevMesh) removeBlock(prevMesh, { trackModification: false, skipOcclusionRefresh: true });
          unregisterManualPlacement(blockKey(prev.x, prev.y, prev.z));
        }
        continue;
      }

      for (const placement of placed) {
        refreshOcclusionAround(placement.x, placement.y, placement.z);
      }
      settleGravityAround(gx, gy - 1, gz, 1);
      return true;
    }

    return false;
  }

  function processSaplingGrowthBatch(tick = SAPLING_GROWTH_INTERVAL) {
    const candidates = [];
    const candidateKeySet = new Set();
    for (const key of saplingKeys.values()) {
      const pos = parseBlockKey(key);
      if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y) || !Number.isFinite(pos.z)) continue;
      const ck = chunkKey(worldToChunk(pos.x), worldToChunk(pos.z));
      if (!loadedChunks.has(ck)) continue;
      if (getBlockIdAt(pos.x, pos.y, pos.z) !== SAPLING_BLOCK_ID) {
        saplingKeys.delete(key);
        saplingGrowthProgress.delete(key);
        continue;
      }
      candidates.push(pos);
      candidateKeySet.add(blockKey(pos.x, pos.y, pos.z));
    }

    for (const key of Array.from(saplingGrowthProgress.keys())) {
      if (!candidateKeySet.has(key)) saplingGrowthProgress.delete(key);
    }

    if (candidates.length <= 0) {
      saplingGrowthCursor = 0;
      return;
    }

    if (saplingGrowthCursor >= candidates.length) saplingGrowthCursor = 0;
    const batch = Math.min(SAPLING_GROWTH_BATCH, candidates.length);

    for (let i = 0; i < batch; i++) {
      const idx = (saplingGrowthCursor + i) % candidates.length;
      const sapling = candidates[idx];
      const x = worldToBlock(sapling.x);
      const y = worldToBlock(sapling.y);
      const z = worldToBlock(sapling.z);
      const key = blockKey(x, y, z);

      if (getBlockIdAt(x, y, z) !== SAPLING_BLOCK_ID) {
        saplingGrowthProgress.delete(key);
        continue;
      }
      if (!isSolidAt(x, y - 1, z)) {
        saplingGrowthProgress.delete(key);
        continue;
      }

      const hydrationBoost = hasWaterContact(x, y - 1, z) ? 1.12 : 1.0;
      let progress = Number(saplingGrowthProgress.get(key) || 0);
      progress += Math.max(0.05, Number(tick) || SAPLING_GROWTH_INTERVAL) * hydrationBoost * (0.85 + Math.random() * 0.34);

      const targetSeconds = getSaplingGrowthTargetSeconds(x, y, z);
      if (progress >= targetSeconds) {
        const grown = tryGrowSaplingAt(x, y, z);
        if (grown) {
          saplingGrowthProgress.delete(key);
          continue;
        }
        progress = Math.min(targetSeconds - 1, targetSeconds * 0.68);
      }

      saplingGrowthProgress.set(key, progress);
    }

    saplingGrowthCursor = (saplingGrowthCursor + batch) % candidates.length;
  }

  function getCactusGrowthTargetSeconds(x, y, z) {
    const rolled = hash3i(x, y, z, 8923);
    return CACTUS_GROWTH_MIN_SECONDS + rolled * (CACTUS_GROWTH_MAX_SECONDS - CACTUS_GROWTH_MIN_SECONDS);
  }

  function isCactusRootAt(x, y, z) {
    return getBlockIdAt(x, y, z) === CACTUS_BLOCK_ID && getBlockIdAt(x, y - 1, z) !== CACTUS_BLOCK_ID;
  }

  function getCactusColumnHeight(x, y, z) {
    let height = 0;
    while (height < (CACTUS_MAX_HEIGHT + 4) && getBlockIdAt(x, y + height, z) === CACTUS_BLOCK_ID) {
      height += 1;
    }
    return height;
  }

  function canCactusGrowInto(x, y, z) {
    if (getBlockIdAt(x, y, z) === CACTUS_BLOCK_ID) return false;
    if (!canPlaceAt(x, y, z)) return false;
    if (isWaterAt(x, y, z) || isLavaAt(x, y, z)) return false;

    const neighbors = [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1]
    ];
    for (const [dx, dz] of neighbors) {
      const neighborId = getBlockIdAt(x + dx, y, z + dz);
      if (neighborId === null || neighborId === CACTUS_BLOCK_ID) continue;
      if (blockData[neighborId]?.solid) return false;
    }
    return true;
  }

  function tryGrowCactusAt(rootX, rootY, rootZ) {
    if (!isCactusRootAt(rootX, rootY, rootZ)) return false;
    if (!isSolidAt(rootX, rootY - 1, rootZ)) return false;

    const height = getCactusColumnHeight(rootX, rootY, rootZ);
    if (height <= 0 || height >= CACTUS_MAX_HEIGHT) return false;

    const topY = rootY + height - 1;
    const nextY = topY + 1;
    if (!canCactusGrowInto(rootX, nextY, rootZ)) return false;

    const rootBlock = getBlockAt(rootX, rootY, rootZ);
    const created = createBlock(rootX, nextY, rootZ, CACTUS_BLOCK_ID, {
      generated: !!rootBlock?.userData?.generated,
      chunkKey: chunkKey(worldToChunk(rootX), worldToChunk(rootZ)),
      allowReplace: true
    });
    return !!created;
  }

  function consumeTrashNearCactusRoot(rootX, rootY, rootZ, height = 1) {
    let consumed = 0;
    const radiusSq = CACTUS_TRASH_RADIUS * CACTUS_TRASH_RADIUS;
    const minY = rootY - 0.75;
    const maxY = rootY + Math.max(1, height) + CACTUS_TRASH_VERTICAL_MARGIN;

    for (let i = drops.length - 1; i >= 0; i--) {
      if (consumed >= CACTUS_MAX_TRASH_PER_TICK) break;
      const drop = drops[i];
      const userData = drop?.userData || null;
      if (!userData || userData.kind === 'xp') continue;
      if (userData.source !== PLAYER_DISCARD_DROP_SOURCE) continue;
      if (userData.mode === 'magnet') continue;
      if (drop.position.y < minY || drop.position.y > maxY) continue;

      const dx = drop.position.x - rootX;
      const dz = drop.position.z - rootZ;
      if (((dx * dx) + (dz * dz)) > radiusSq) continue;

      if (onCactusConsumeDrop) {
        try {
          onCactusConsumeDrop({
            x: drop.position.x,
            y: drop.position.y,
            z: drop.position.z,
            rootX,
            rootY,
            rootZ,
            height: Math.max(1, height),
            itemId: userData.dropId,
            kind: userData.kind
          });
        } catch {
          // Ignore gameplay VFX callback failures to keep world simulation stable.
        }
      }
      recycleDropMesh(drop);
      drops.splice(i, 1);
      consumed += 1;
    }

    return consumed;
  }

  function processCactusGrowthBatch(tick = CACTUS_GROWTH_INTERVAL) {
    const candidates = [];
    const candidateKeySet = new Set();

    for (const key of cactusKeys.values()) {
      const pos = parseBlockKey(key);
      if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y) || !Number.isFinite(pos.z)) continue;
      const ck = chunkKey(worldToChunk(pos.x), worldToChunk(pos.z));
      if (!loadedChunks.has(ck)) continue;
      if (getBlockIdAt(pos.x, pos.y, pos.z) !== CACTUS_BLOCK_ID) {
        cactusKeys.delete(key);
        cactusGrowthProgress.delete(key);
        cactusTrashProgress.delete(key);
        continue;
      }
      if (!isCactusRootAt(pos.x, pos.y, pos.z)) continue;
      candidates.push(pos);
      candidateKeySet.add(blockKey(pos.x, pos.y, pos.z));
    }

    for (const key of Array.from(cactusGrowthProgress.keys())) {
      if (!candidateKeySet.has(key)) cactusGrowthProgress.delete(key);
    }
    for (const key of Array.from(cactusTrashProgress.keys())) {
      if (!candidateKeySet.has(key)) cactusTrashProgress.delete(key);
    }

    if (candidates.length <= 0) {
      cactusGrowthCursor = 0;
      return;
    }

    if (cactusGrowthCursor >= candidates.length) cactusGrowthCursor = 0;
    const batch = Math.min(CACTUS_GROWTH_BATCH, candidates.length);

    for (let i = 0; i < batch; i++) {
      const idx = (cactusGrowthCursor + i) % candidates.length;
      const cactus = candidates[idx];
      const x = worldToBlock(cactus.x);
      const y = worldToBlock(cactus.y);
      const z = worldToBlock(cactus.z);
      const key = blockKey(x, y, z);

      if (!isCactusRootAt(x, y, z)) {
        cactusGrowthProgress.delete(key);
        cactusTrashProgress.delete(key);
        continue;
      }

      const currentHeight = getCactusColumnHeight(x, y, z);
      let trashCount = Number(cactusTrashProgress.get(key) || 0);
      trashCount += consumeTrashNearCactusRoot(x, y, z, currentHeight);

      let grewFromTrash = false;
      while (trashCount >= CACTUS_TRASH_TO_GROW) {
        if (!tryGrowCactusAt(x, y, z)) break;
        trashCount -= CACTUS_TRASH_TO_GROW;
        grewFromTrash = true;
      }

      const grownHeight = getCactusColumnHeight(x, y, z);
      if (grownHeight >= CACTUS_MAX_HEIGHT) {
        cactusGrowthProgress.delete(key);
        cactusTrashProgress.delete(key);
        continue;
      }

      if (grewFromTrash) {
        cactusGrowthProgress.set(key, 0);
        cactusTrashProgress.set(key, Math.min(trashCount, CACTUS_TRASH_TO_GROW - 1));
        continue;
      }

      let progress = Number(cactusGrowthProgress.get(key) || 0);
      progress += Math.max(0.05, Number(tick) || CACTUS_GROWTH_INTERVAL) * (0.78 + Math.random() * 0.28);
      const targetSeconds = getCactusGrowthTargetSeconds(x, y, z);
      if (progress >= targetSeconds) {
        const grown = tryGrowCactusAt(x, y, z);
        if (grown) progress = 0;
        else progress = Math.min(targetSeconds - 1, targetSeconds * 0.84);
      }

      cactusGrowthProgress.set(key, progress);
      cactusTrashProgress.set(key, Math.min(trashCount, CACTUS_TRASH_TO_GROW - 1));
    }

    cactusGrowthCursor = (cactusGrowthCursor + batch) % candidates.length;
  }

  function getTreeSpawnChanceForColumn(x, z, biome, humidity, terrain, surfaceY) {
    let chance = 0.06;
    if (terrain === 'plains') chance = 0.084;
    else if (terrain === 'hill') chance = 0.073;
    else if (terrain === 'plateau') chance = 0.064;
    else if (terrain === 'canyon') chance = 0.046;
    else if (terrain === 'ravine') chance = 0.051;
    else if (terrain === 'volcano') chance = 0.031;
    else if (terrain === 'mountain') chance = 0.054;

    if (biome === 'forest') chance += 0.058;
    else if (biome === 'lowland') chance += 0.022;
    else if (biome === 'wetland') chance += 0.012;
    else if (biome === 'snow') chance -= 0.016;
    else if (biome === 'desert') chance -= 0.028;

    const humidityCenter = clamp((Number(humidity) || 0.5) - 0.5, -0.5, 0.5);
    chance += humidityCenter * 0.09;

    if (surfaceY > WATER_LEVEL + 26) chance -= 0.02;
    if (surfaceY > WATER_LEVEL + 40) chance -= 0.02;

    const distSq = (x * x) + (z * z);
    if (distSq <= (72 * 72)) chance += 0.065;
    else if (distSq <= (128 * 128)) chance += 0.035;

    return clamp(chance * TREE_SPAWN_DENSITY_SCALE, 0.008, 0.18);
  }

  function resolveTreeSpeciesProfile(x, z, biome, humidity, terrain = 'plains', surfaceY = WATER_LEVEL + 2) {
    const h = clamp(Number(humidity) || 0.5, 0, 1);
    const speciesWeights = [];
    const pushSpeciesWeight = (id, weight) => {
      if (!TREE_SPECIES_PROFILES[id]) return;
      const w = Number(weight) || 0;
      if (w <= 0) return;
      speciesWeights.push({ id, weight: w });
    };

    if (terrain === 'plains') {
      pushSpeciesWeight('camphor', 1.0);
      pushSpeciesWeight('banyan', 0.42 + h * 0.6);
      pushSpeciesWeight('nanmu', 0.78 + h * 0.34);
    } else if (terrain === 'hill') {
      pushSpeciesWeight('camphor', 0.66);
      pushSpeciesWeight('nanmu', 0.95);
      pushSpeciesWeight('birch', 0.76);
      pushSpeciesWeight('maple', 0.62);
    } else if (terrain === 'plateau') {
      pushSpeciesWeight('birch', 0.98);
      pushSpeciesWeight('cypress', 0.74);
      pushSpeciesWeight('maple', 0.84);
    } else if (terrain === 'canyon' || terrain === 'ravine') {
      pushSpeciesWeight('juniper', 1.05);
      pushSpeciesWeight('camphor', 0.42);
      if (h > 0.56) pushSpeciesWeight('banyan', 0.4);
    } else if (terrain === 'volcano') {
      pushSpeciesWeight('juniper', 1.2);
      pushSpeciesWeight('cypress', 0.45);
      pushSpeciesWeight('fir', 0.35);
    } else if (terrain === 'mountain') {
      pushSpeciesWeight('conifer', 0.95);
      pushSpeciesWeight('fir', 1.0);
      pushSpeciesWeight('juniper', 0.65);
    } else {
      pushSpeciesWeight('camphor', 0.9);
      pushSpeciesWeight('nanmu', 0.7);
    }

    if (biome === 'forest' && terrain !== 'mountain' && terrain !== 'volcano') {
      pushSpeciesWeight('banyan', 0.55);
      pushSpeciesWeight('nanmu', 0.35);
      pushSpeciesWeight('camphor', 0.2);
    }

    const highMountain = biome === 'snow' || surfaceY > WATER_LEVEL + 28;
    if (highMountain) {
      pushSpeciesWeight('conifer', 0.82);
      pushSpeciesWeight('fir', 0.95);
      pushSpeciesWeight('juniper', 0.5);
    }

    if (biome === 'desert') {
      pushSpeciesWeight('juniper', 0.7);
      pushSpeciesWeight('camphor', 0.26);
    }

    if (speciesWeights.length <= 0) return TREE_SPECIES_PROFILES.camphor;
    const picked = chooseWeighted(speciesWeights, hash2i(x, z, 5897));
    return TREE_SPECIES_PROFILES[picked?.id] || TREE_SPECIES_PROFILES.camphor;
  }

  function placeRoundCanopy(cx, cy, cz, radius, halfHeight, leafVariant, ck, treeMeta, { flatten = false, sparse = false } = {}) {
    const r = Math.max(1, Math.floor(Number(radius) || 2));
    const hh = Math.max(1, Math.floor(Number(halfHeight) || 2));
    for (let ly = -hh; ly <= hh; ly++) {
      const verticalBias = flatten ? 1.15 : 0.72;
      const layerRadius = r - Math.max(0, Math.abs(ly) - 1) * verticalBias;
      if (layerRadius <= 0) continue;
      for (let lx = -r; lx <= r; lx++) {
        for (let lz = -r; lz <= r; lz++) {
          const dist = Math.sqrt((lx * lx) + (lz * lz) + (ly * ly * (flatten ? 1.65 : 0.85)));
          if (dist > (layerRadius + 0.26)) continue;
          if (sparse && dist > (layerRadius - 0.2) && hash3i(cx + lx, cy + ly, cz + lz, 9311) < 0.34) continue;
          createNaturalBlock(cx + lx, cy + ly, cz + lz, 4, ck, { leafVariant, ...treeMeta });
        }
      }
    }
  }

  function placeConiferCanopy(cx, cy, cz, radius, height, leafVariant, ck, treeMeta, { spire = false, sparse = false } = {}) {
    const h = Math.max(3, Math.floor(Number(height) || 6));
    const r = Math.max(1, Math.floor(Number(radius) || 2));
    const baseY = cy - Math.max(2, Math.floor(h * 0.58));
    for (let i = 0; i < h; i++) {
      const y = baseY + i;
      const t = i / Math.max(1, h - 1);
      const radiusScale = spire ? 1.08 : 1.0;
      const layerRadius = Math.max(0, Math.round((1 - t * radiusScale) * r));
      if (layerRadius <= 0) {
        createNaturalBlock(cx, y, cz, 4, ck, { leafVariant, ...treeMeta });
        continue;
      }
      for (let lx = -layerRadius; lx <= layerRadius; lx++) {
        for (let lz = -layerRadius; lz <= layerRadius; lz++) {
          const edge = Math.abs(lx) + Math.abs(lz);
          if (edge > layerRadius + 0.45) continue;
          if (sparse && edge >= layerRadius && hash3i(cx + lx, y, cz + lz, 9337) < 0.36) continue;
          createNaturalBlock(cx + lx, y, cz + lz, 4, ck, { leafVariant, ...treeMeta });
        }
      }
    }
    createNaturalBlock(cx, cy + 2, cz, 4, ck, { leafVariant, ...treeMeta });
  }

  function placeBanyanAerialRoots(cx, cy, cz, surfaceY, ck, treeMeta, trunkBlockId = 3) {
    const dirs = [
      [2, 0], [-2, 0], [0, 2], [0, -2],
      [2, 2], [2, -2], [-2, 2], [-2, -2]
    ];
    for (const [dx, dz] of dirs) {
      if (hash3i(cx + dx, cy, cz + dz, 9221) < 0.48) continue;
      const rootLen = 1 + Math.floor(hash3i(cx + dx, cy - 1, cz + dz, 9239) * 3);
      for (let i = 0; i < rootLen; i++) {
        const ry = cy - 1 - i;
        if (ry <= surfaceY + 1) break;
        if (getBlockIdAt(cx + dx, ry, cz + dz) !== null) break;
        createNaturalBlock(cx + dx, ry, cz + dz, trunkBlockId, ck, treeMeta);
        const belowId = getBlockIdAt(cx + dx, ry - 1, cz + dz);
        if (belowId !== null && blockData[belowId]?.solid) break;
      }
    }
  }

  function maybeGenerateTree(x, surfaceY, z, ck, biome, humidity, terrain = 'plains') {
    if (surfaceY <= WATER_LEVEL + 1) return;
    if (getBlockIdAt(x, surfaceY + 1, z) !== null) return;

    const topId = getBlockIdAt(x, surfaceY, z);
    if (!isTreeSoilBlockId(topId)) return;

    const spawnChance = getTreeSpawnChanceForColumn(x, z, biome, humidity, terrain, surfaceY);
    if (hash2i(x, z, 515) > spawnChance) return;

    const species = resolveTreeSpeciesProfile(x, z, biome, humidity, terrain, surfaceY);
    if (!species) return;
    if (hasNearbyTreeTrunk(x, surfaceY + 1, z, species.spacingRadius)) return;

    const minHeight = Math.max(3, Math.floor(Number(species.minHeight) || 4));
    const maxHeight = Math.max(minHeight, Math.floor(Number(species.maxHeight) || minHeight + 1));
    const trunkHeight = minHeight + Math.floor(hash2i(x, z, 611) * (maxHeight - minHeight + 1));
    const trunkRadius = Math.max(0, Math.floor(Number(species.trunkRadius) || 0));
    const trunkBlockId = TREE_LOG_BLOCK_ID_BY_SPECIES[species.id] || 3;
    const canopyY = surfaceY + trunkHeight;
    const treeMeta = {
      treeRootX: x,
      treeRootY: surfaceY + 1,
      treeRootZ: z,
      treeTrunkTopY: canopyY,
      treeSpecies: species.id,
      treeTrunkRadius: trunkRadius,
      treeTrunkBlockId: trunkBlockId
    };
    const leafVariant = pickLeafVariantByTheme(
      x,
      canopyY,
      z,
      species.leafTheme || 'plains',
      877 + (Math.abs(species.id?.charCodeAt?.(0) || 0) % 97)
    );

    let topX = x;
    let topZ = z;
    if (trunkRadius > 0) {
      for (let y = 1; y <= trunkHeight; y++) {
        for (let tx = -trunkRadius; tx <= trunkRadius; tx++) {
          for (let tz = -trunkRadius; tz <= trunkRadius; tz++) {
            const isCorner = Math.abs(tx) === trunkRadius && Math.abs(tz) === trunkRadius;
            if (isCorner && y > Math.floor(trunkHeight * 0.62) && hash3i(x + tx, surfaceY + y, z + tz, 6143) < 0.5) continue;
            createNaturalBlock(x + tx, surfaceY + y, z + tz, trunkBlockId, ck, treeMeta);
          }
        }
      }
    } else if (species.leanTrunk) {
      let shiftX = 0;
      let shiftZ = 0;
      for (let y = 1; y <= trunkHeight; y++) {
        if (y > 2 && hash3i(x, surfaceY + y, z, 6191) > 0.72) {
          const dirRoll = hash3i(x + y, surfaceY + y, z - y, 6203);
          if (dirRoll < 0.25) shiftX = Math.max(-1, shiftX - 1);
          else if (dirRoll < 0.5) shiftX = Math.min(1, shiftX + 1);
          else if (dirRoll < 0.75) shiftZ = Math.max(-1, shiftZ - 1);
          else shiftZ = Math.min(1, shiftZ + 1);
        }
        topX = x + shiftX;
        topZ = z + shiftZ;
        createNaturalBlock(topX, surfaceY + y, topZ, trunkBlockId, ck, treeMeta);
      }
    } else {
      for (let y = 1; y <= trunkHeight; y++) {
        createNaturalBlock(x, surfaceY + y, z, trunkBlockId, ck, treeMeta);
      }
    }

    const canopyStyle = species.canopyStyle || 'round';
    if (canopyStyle === 'conifer') {
      placeConiferCanopy(
        topX,
        canopyY,
        topZ,
        species.canopyRadius,
        species.canopyHeight,
        leafVariant,
        ck,
        treeMeta,
        { spire: false, sparse: false }
      );
    } else if (canopyStyle === 'spire') {
      placeConiferCanopy(
        topX,
        canopyY,
        topZ,
        species.canopyRadius,
        species.canopyHeight,
        leafVariant,
        ck,
        treeMeta,
        { spire: true, sparse: false }
      );
    } else if (canopyStyle === 'sparse') {
      placeRoundCanopy(
        topX,
        canopyY,
        topZ,
        species.canopyRadius,
        species.canopyHeight,
        leafVariant,
        ck,
        treeMeta,
        { flatten: false, sparse: true }
      );
      createNaturalBlock(topX, canopyY + 1, topZ, 4, ck, { leafVariant, ...treeMeta });
    } else if (canopyStyle === 'spread') {
      placeRoundCanopy(
        topX,
        canopyY,
        topZ,
        species.canopyRadius,
        species.canopyHeight,
        leafVariant,
        ck,
        treeMeta,
        { flatten: true, sparse: false }
      );
      createNaturalBlock(topX, canopyY + 2, topZ, 4, ck, { leafVariant, ...treeMeta });
      if (species.aerialRoots) placeBanyanAerialRoots(topX, canopyY, topZ, surfaceY, ck, treeMeta, trunkBlockId);
    } else if (canopyStyle === 'column') {
      placeRoundCanopy(
        topX,
        canopyY,
        topZ,
        species.canopyRadius,
        species.canopyHeight,
        leafVariant,
        ck,
        treeMeta,
        { flatten: false, sparse: false }
      );
      for (let dy = 1; dy <= 2; dy++) {
        createNaturalBlock(topX, canopyY + dy, topZ, 4, ck, { leafVariant, ...treeMeta });
      }
    } else if (canopyStyle === 'light_round') {
      placeRoundCanopy(
        topX,
        canopyY,
        topZ,
        species.canopyRadius,
        species.canopyHeight,
        leafVariant,
        ck,
        treeMeta,
        { flatten: false, sparse: true }
      );
      createNaturalBlock(topX, canopyY + 1, topZ, 4, ck, { leafVariant, ...treeMeta });
    } else {
      placeRoundCanopy(
        topX,
        canopyY,
        topZ,
        species.canopyRadius,
        species.canopyHeight,
        leafVariant,
        ck,
        treeMeta,
        { flatten: false, sparse: false }
      );
      createNaturalBlock(topX, canopyY + 2, topZ, 4, ck, { leafVariant, ...treeMeta });
    }
  }

  function maybeGenerateForage(x, surfaceY, z, ck, biome, humidity, terrain = 'plains') {
    if (terrain === 'canyon' || terrain === 'ravine') return;
    if (terrain === 'volcano') {
      if (surfaceY > WATER_LEVEL + 2 && getBlockIdAt(x, surfaceY + 1, z) == null && hash2i(x, z, 6501) > 0.975) {
        createNaturalBlock(x, surfaceY + 1, z, 76, ck);
      }
      return;
    }

    if (biome === 'ocean' || biome === 'wetland') {
      if (surfaceY < WATER_LEVEL - 1 && getBlockIdAt(x, surfaceY + 1, z) === 7) {
        const depth = Math.max(1, WATER_LEVEL - surfaceY);
        const baseChance = biome === 'wetland' ? KELP_WETLAND_CHANCE : KELP_OCEAN_CHANCE;
        const humidityBoost = biome === 'wetland'
          ? 0.04
          : clamp((humidity - 0.55) * 0.12, -0.04, 0.03);
        const depthPenalty = depth >= 10 ? 0.12 : depth >= 7 ? 0.07 : 0;
        const kelpChance = clamp(baseChance + humidityBoost - depthPenalty, 0.08, 0.36);

        if (hash2i(x, z, 6503) < kelpChance) {
          const maxHeight = depth >= 8 ? KELP_MAX_HEIGHT_DEEP : KELP_MAX_HEIGHT_SHALLOW;
          const kelpHeight = 1 + Math.floor(hash2i(x, z, 6509) * maxHeight);
          for (let i = 1; i <= kelpHeight; i++) {
            if (surfaceY + i >= WATER_LEVEL) break;
            createNaturalBlock(x, surfaceY + i, z, 73, ck);
          }
        }
      }
      return;
    }

    if (surfaceY <= WATER_LEVEL + 1) return;
    if (getBlockIdAt(x, surfaceY + 1, z) !== null) return;

    const topId = getBlockIdAt(x, surfaceY, z);
    const validTop = topId === 1 || topId === 2 || topId === 26 || topId === 27 || topId === 49 || topId === 70;
    if (!validTop) return;

    if (biome === 'desert' && topId === 26) {
      if (hash2i(x, z, 6512) > 0.96) {
        const cactusHeight = 1 + Math.floor(hash2i(x, z, 6517) * 4);
        for (let i = 1; i <= cactusHeight; i++) {
          createNaturalBlock(x, surfaceY + i, z, 72, ck);
        }
      }
      return;
    }

    const regionField = (valueNoise2(x, z, 0.014, 6541) + 1) * 0.5;
    const clusterField = (valueNoise2(x + 31, z - 17, 0.068, 6557) + 1) * 0.5;
    const roll = hash2i(x, z, 6511);
    if (biome === 'forest') {
      const inBerryPatch = regionField > 0.76 && clusterField > 0.58;
      if (inBerryPatch && roll > ((0.968 - humidity * 0.012) - FOREST_BERRY_ROLL_BONUS)) {
        const bushId = hash2i(x, z, 6521) > 0.42 ? 66 : 67;
        createNaturalBlock(x, surfaceY + 1, z, bushId, ck);
        return;
      }
      const inWheatPatch = regionField > 0.87 && clusterField > 0.66;
      if (inWheatPatch && roll > 0.992 && (topId === 1 || topId === 2 || topId === 49)) {
        setGeneratedBlockAt(x, surfaceY, z, 49, ck);
        setGeneratedBlockAt(x, surfaceY + 1, z, 53, ck);
        return;
      }
      const aloePatch = regionField > 0.82 && clusterField > 0.66;
      if (aloePatch && roll > 0.997) {
        createNaturalBlock(x, surfaceY + 1, z, 76, ck);
      }
      return;
    }

    if (biome === 'wetland') {
      const inBerryPatch = regionField > 0.72 && clusterField > 0.56;
      if (inBerryPatch && roll > ((0.969 - humidity * 0.01) - WETLAND_BLUEBERRY_ROLL_BONUS)) {
        createNaturalBlock(x, surfaceY + 1, z, 67, ck);
      } else if (regionField > 0.86 && clusterField > 0.64 && roll > (0.992 - WETLAND_BERRY_ROLL_BONUS)) {
        createNaturalBlock(x, surfaceY + 1, z, 66, ck);
      }
      return;
    }

    if (biome === 'lowland') {
      const inFieldPatch = regionField > 0.78 && clusterField > 0.62;
      if (inFieldPatch && roll > 0.991) {
        createNaturalBlock(x, surfaceY + 1, z, 68, ck);
        return;
      }
      if (inFieldPatch && roll > 0.976) {
        if (topId !== 1 && topId !== 2 && topId !== 49) return;
        setGeneratedBlockAt(x, surfaceY, z, 49, ck);
        setGeneratedBlockAt(x, surfaceY + 1, z, 53, ck);
        return;
      }
      if (regionField > 0.86 && clusterField > 0.7 && roll > (0.994 - LOWLAND_BERRY_ROLL_BONUS)) {
        const bushId = hash2i(x, z, 6533) > 0.58 ? 66 : 67;
        createNaturalBlock(x, surfaceY + 1, z, bushId, ck);
        return;
      }
      const aloePatch = regionField > 0.84 && clusterField > 0.64;
      if (aloePatch && roll > 0.9975) {
        createNaturalBlock(x, surfaceY + 1, z, 76, ck);
      }
    }
  }

  function generateNetherColumn(x, z, ck, opts = {}) {
    const {
      minDepth = 0,
      maxDepth = FULL_COLUMN_DEPTH,
      decorate = true
    } = opts;
    const info = getPrefetchedColumnInfoAt(x, z, 'nether') || netherColumnNoise(x, z);
    const surfaceY = info.height;

    const loDepth = Math.max(0, Math.floor(minDepth));
    const hiDepth = Math.max(loDepth, Math.floor(maxDepth));
    const columnTop = surfaceY - loDepth;
    const columnBottom = Math.max(WORLD_MIN_Y, surfaceY - hiDepth);

    const soulField = info.soul > 0.62;
    const floorTopId = soulField ? 41 : 40;
    const fillId = soulField && info.soul > 0.7 ? 41 : 40;

    for (let y = columnBottom; y <= columnTop; y++) {
      if (!canUseNaturalAt(x, y, z)) continue;
      if (y < surfaceY - 1 && shouldCarveNetherCave(x, y, z, surfaceY)) continue;

      let id = fillId;
      if (y === surfaceY) id = floorTopId;
      else if (y <= surfaceY - 10 && valueNoise3(toLocalX(x, 'nether'), y, z, 0.13, 3121) > 0.57) id = 42;

      createNaturalBlock(x, y, z, id, ck);
    }

    if (!decorate) return;

    if (valueNoise3(toLocalX(x, 'nether'), surfaceY + 3, z, 0.11, 3151) > 0.7) {
      createNaturalBlock(x, surfaceY + 1, z, 38, ck);
    }
  }

  function generateEndColumn(x, z, ck, opts = {}) {
    const {
      minDepth = 0,
      maxDepth = FULL_COLUMN_DEPTH,
      decorate = true
    } = opts;
    const info = getPrefetchedColumnInfoAt(x, z, 'end') || endColumnNoise(x, z);
    if (!info.hasLand) return;

    const surfaceY = info.height;
    const loDepth = Math.max(0, Math.floor(minDepth));
    const hiDepth = Math.max(loDepth, Math.floor(maxDepth));
    const columnTop = surfaceY - loDepth;
    const islandDepth = 8 + Math.floor(info.crown * 6);
    const columnBottom = Math.max(WORLD_MIN_Y, surfaceY - Math.min(islandDepth, hiDepth));

    for (let y = columnBottom; y <= columnTop; y++) {
      if (!canUseNaturalAt(x, y, z)) continue;
      if (y < surfaceY - 2 && shouldCarveEndVoid(x, y, z, surfaceY)) continue;

      let id = 44;
      if (y === surfaceY && valueNoise3(toLocalX(x, 'end'), y, z, 0.13, 4211) > 0.64) {
        id = 45;
      }
      createNaturalBlock(x, y, z, id, ck);
    }

    if (!decorate) return;

    if (Math.abs(toLocalX(x, 'end')) <= 2 && Math.abs(z) <= 2 && surfaceY >= 6) {
      createNaturalBlock(x, surfaceY + 1, z, 38, ck);
    }
  }

  function generateColumn(x, z, ck, opts = {}) {
    const dimension = getDimensionAtCoord(x, z);
    if (dimension === 'nether') {
      generateNetherColumn(x, z, ck, opts);
      return;
    }
    if (dimension === 'end') {
      generateEndColumn(x, z, ck, opts);
      return;
    }

    const {
      minDepth = 0,
      maxDepth = FULL_COLUMN_DEPTH,
      decorate = true,
      structureProfiles = null
    } = opts;

    const info = getPrefetchedColumnInfoAt(x, z, 'overworld') || columnNoise(x, z);
    const surfaceY = info.height;
    const terrain = info.terrain || 'plains';
    const ruggedTerrain = terrain === 'canyon' || terrain === 'ravine';
    const plateauTerrain = terrain === 'plateau';
    const volcanoTerrain = terrain === 'volcano';
    const hillTerrain = terrain === 'hill';
    const shoreline = surfaceY <= WATER_LEVEL + 2;
    const sandField = (valueNoise2(x, z, 0.06, 2011) + 1) * 0.5;
    const gravelField = (valueNoise2(x, z, 0.08, 2027) + 1) * 0.5;
    const beachLike = shoreline && info.biome !== 'mountain' && !ruggedTerrain && !volcanoTerrain;
    const isDesert = info.biome === 'desert';
    const isSnow = info.biome === 'snow';
    const useSand = isDesert || (beachLike ? sandField > 0.34 : (info.biome === 'lowland' && surfaceY <= 16 && sandField > 0.76));
    const useGravel = !useSand && (beachLike ? gravelField > 0.63 : (gravelField > 0.85 && info.biome !== 'forest'));
    const volcanicTopNoise = (valueNoise2(x + 7, z - 11, 0.09, 8527) + 1) * 0.5;
    const volcanicFillNoise = (valueNoise2(x - 13, z + 5, 0.11, 8539) + 1) * 0.5;
    const topBlock = volcanoTerrain
      ? (volcanicTopNoise > 0.42 ? 75 : 74)
      : useSand
      ? 26
      : useGravel
        ? 27
        : isSnow ? 70
        : (info.biome === 'mountain' && surfaceY > 28) || ruggedTerrain || plateauTerrain
          ? 5
          : (hillTerrain && surfaceY > WATER_LEVEL + 16)
            ? 5
          : 1;
    const fillBlock = volcanoTerrain
      ? (volcanicFillNoise > 0.5 ? 75 : 74)
      : useSand ? 26 : useGravel ? 27 : isSnow ? 2 : (ruggedTerrain ? 5 : 2);
    const dirtDepth = volcanoTerrain
      ? 1
      : info.biome === 'mountain'
      ? 2
      : ruggedTerrain
        ? 1
        : hillTerrain
          ? 2
        : plateauTerrain
          ? 2
          : (useSand || useGravel ? 4 : 3);

    const loDepth = Math.max(0, Math.floor(minDepth));
    const hiDepth = Math.max(loDepth, Math.floor(maxDepth));
    const columnTop = surfaceY - loDepth;
    const columnBottom = Math.max(WORLD_MIN_Y, surfaceY - hiDepth);

    for (let y = columnBottom; y <= columnTop; y++) {
      if (!canUseNaturalAt(x, y, z)) continue;
      if (y < surfaceY - 1 && shouldCarveSurfaceRavine(x, y, z, surfaceY, info)) continue;
      if (y < surfaceY - 2 && shouldCarveCave(x, y, z, surfaceY, info.biome)) continue;

      let id = 5;
      if (y === surfaceY) {
        id = topBlock;
      } else if (y >= surfaceY - dirtDepth) {
        id = fillBlock;
      } else {
        id = chooseStoneType(x, y, z);
      }
      createNaturalBlock(x, y, z, id, ck);
    }

    if (!decorate) return;

    if (surfaceY < WATER_LEVEL) {
      for (let y = surfaceY + 1; y <= WATER_LEVEL; y++) {
        if (!canUseNaturalAt(x, y, z)) continue;
        let waterId = 7;
        if (y === WATER_LEVEL && info.biome === 'snow') waterId = 71;
        createBlock(x, y, z, waterId, { generated: true, chunkKey: ck, allowReplace: true });
      }
    }
    maybeGenerateRavineHazard(x, surfaceY, z, ck, info);
    maybeGenerateVolcanicVent(x, surfaceY, z, ck, info);

    const settlementTouched = applySettlementColumn(x, surfaceY, z, ck, structureProfiles);
    if (!settlementTouched) {
      const poiTouched = applyPointOfInterestColumn(x, surfaceY, z, ck, structureProfiles);
      if (!poiTouched) {
        const shipwreckTouched = applyShipwreckColumn(x, surfaceY, z, ck, structureProfiles);
        if (!shipwreckTouched) {
          maybeGenerateTree(x, surfaceY, z, ck, info.biome, info.humidity, terrain);
          maybeGenerateForage(x, surfaceY, z, ck, info.biome, info.humidity, terrain);
        }
      }
    }
    applySkyFeatureColumn(x, z, ck, structureProfiles);
  }

  function applyManualBlocksInChunk(ck) {
    const set = manualByChunk.get(ck);
    if (!set) return;
    for (const key of set) {
      const entry = manualPlaced.get(key);
      if (!entry) continue;
      createBlock(entry.x, entry.y, entry.z, entry.id, {
        generated: false,
        chunkKey: ck,
        allowReplace: true,
        ...(entry.opts || {})
      });
    }
  }

  function getBufferedStreamCenter(playerPos) {
    const playerCx = worldToChunk(playerPos.x);
    const playerCz = worldToChunk(playerPos.z);
    const localX = playerPos.x - (playerCx * CHUNK_SIZE);
    const localZ = playerPos.z - (playerCz * CHUNK_SIZE);

    let vx = 0;
    let vz = 0;
    if (Number.isFinite(lastStreamSampleX) && Number.isFinite(lastStreamSampleZ)) {
      vx = playerPos.x - lastStreamSampleX;
      vz = playerPos.z - lastStreamSampleZ;
    }
    lastStreamSampleX = playerPos.x;
    lastStreamSampleZ = playerPos.z;

    let cx = playerCx;
    let cz = playerCz;

    if (vx > STREAM_DIR_EPSILON && localX >= CHUNK_SIZE - STREAM_EDGE_MARGIN) {
      cx += 1;
    } else if (vx < -STREAM_DIR_EPSILON && localX <= STREAM_EDGE_MARGIN) {
      cx -= 1;
    }

    if (vz > STREAM_DIR_EPSILON && localZ >= CHUNK_SIZE - STREAM_EDGE_MARGIN) {
      cz += 1;
    } else if (vz < -STREAM_DIR_EPSILON && localZ <= STREAM_EDGE_MARGIN) {
      cz -= 1;
    }

    return {
      cx,
      cz,
      playerCx,
      playerCz
    };
  }

  function generateChunkColumns(cx, cz, ck, startIndex, count, opts = {}) {
    const total = CHUNK_SIZE * CHUNK_SIZE;
    const from = Math.max(0, Math.floor(startIndex));
    const to = Math.min(total, from + Math.max(0, Math.floor(count)));

    let generated = 0;
    for (let i = from; i < to; i++) {
      const localX = i % CHUNK_SIZE;
      const localZ = Math.floor(i / CHUNK_SIZE);
      const x = (cx * CHUNK_SIZE) + localX;
      const z = (cz * CHUNK_SIZE) + localZ;
      generateColumn(x, z, ck, opts);
      generated += 1;
    }

    return generated;
  }

  function findChunkBuildTaskIndex(playerPos = null, opts = {}) {
    if (chunkBuildQueue.length <= 0) return -1;

    const allowDeep = opts.allowDeep !== false;
    const prioritizeSurface = opts.prioritizeSurface !== false;
    const preferredRadius = Number.isFinite(opts.preferredRadius)
      ? Math.max(0, Math.floor(opts.preferredRadius))
      : Number.POSITIVE_INFINITY;
    const playerCx = playerPos ? worldToChunk(playerPos.x) : Number.NaN;
    const playerCz = playerPos ? worldToChunk(playerPos.z) : Number.NaN;

    let bestIndex = -1;
    let bestTask = null;
    let bestRingDist = Number.POSITIVE_INFINITY;
    let bestInRadius = false;

    for (let i = 0; i < chunkBuildQueue.length; i++) {
      const task = chunkBuildQueue[i];
      if (!task) continue;
      if (!allowDeep && task.mode === 'deep') continue;

      const ringDist = Number.isFinite(playerCx) && Number.isFinite(playerCz)
        ? Math.max(Math.abs(task.cx - playerCx), Math.abs(task.cz - playerCz))
        : Number.POSITIVE_INFINITY;
      const inRadius = ringDist <= preferredRadius;

      if (!bestTask) {
        bestTask = task;
        bestIndex = i;
        bestRingDist = ringDist;
        bestInRadius = inRadius;
        continue;
      }

      const taskHighPriority = task.priority === 'high';
      const bestHighPriority = bestTask.priority === 'high';
      if (taskHighPriority !== bestHighPriority) {
        if (taskHighPriority) {
          bestTask = task;
          bestIndex = i;
          bestRingDist = ringDist;
          bestInRadius = inRadius;
        }
        continue;
      }

      if (prioritizeSurface) {
        const taskIsSurface = task.mode === 'surface';
        const bestIsSurface = bestTask.mode === 'surface';
        if (taskIsSurface !== bestIsSurface) {
          if (taskIsSurface) {
            bestTask = task;
            bestIndex = i;
            bestRingDist = ringDist;
            bestInRadius = inRadius;
          }
          continue;
        }
      }

      if (inRadius !== bestInRadius) {
        if (inRadius) {
          bestTask = task;
          bestIndex = i;
          bestRingDist = ringDist;
          bestInRadius = inRadius;
        }
        continue;
      }

      if (ringDist !== bestRingDist) {
        if (ringDist < bestRingDist) {
          bestTask = task;
          bestIndex = i;
          bestRingDist = ringDist;
          bestInRadius = inRadius;
        }
        continue;
      }

      if (i < bestIndex) {
        bestTask = task;
        bestIndex = i;
        bestRingDist = ringDist;
        bestInRadius = inRadius;
      }
    }

    return bestIndex;
  }

  function processChunkBuildQueue(maxColumns = CHUNK_BUILD_COLUMNS_PER_FRAME, opts = {}) {
    let budget = Math.max(0, Math.floor(maxColumns));

    while (budget > 0 && chunkBuildQueue.length > 0) {
      const taskIndex = findChunkBuildTaskIndex(opts.playerPos || null, opts);
      if (taskIndex < 0) break;

      const task = chunkBuildQueue[taskIndex];
      const chunk = loadedChunks.get(task.ck);
      if (!chunk) {
        chunkBuildQueue.splice(taskIndex, 1);
        chunkBuildQueuedKeys.delete(task.ck);
        continue;
      }

      if (task.mode === 'surface' && !task.structureProfiles) {
        task.structureProfiles = getChunkStructureGenerationProfiles?.(task.cx, task.cz) || null;
      }

      const generated = generateChunkColumns(task.cx, task.cz, task.ck, task.nextColumn, budget, {
        minDepth: task.minDepth,
        maxDepth: task.maxDepth,
        decorate: task.decorate,
        structureProfiles: task.mode === 'surface' ? task.structureProfiles : null
      });
      if (generated <= 0) {
        chunkBuildQueue.splice(taskIndex, 1);
        chunkBuildQueuedKeys.delete(task.ck);
        continue;
      }

      task.nextColumn += generated;
      budget -= generated;

      if (task.nextColumn >= CHUNK_SIZE * CHUNK_SIZE) {
        if (task.mode === 'surface' && FULL_COLUMN_DEPTH > SURFACE_COLUMN_DEPTH) {
          if (!chunk.ready) {
            applyManualBlocksInChunk(task.ck);
            chunk.ready = true;
          }
          task.structureProfiles = null;
          task.mode = 'deep';
          task.minDepth = SURFACE_COLUMN_DEPTH + 1;
          task.maxDepth = FULL_COLUMN_DEPTH;
          task.decorate = false;
          task.nextColumn = 0;

          chunkBuildQueue.splice(taskIndex, 1);
          chunkBuildQueue.push(task);
          continue;
        }

        if (!chunk.ready) {
          applyManualBlocksInChunk(task.ck);
          chunk.ready = true;
        }
        chunk.detailReady = true;
        queueFluidSeedsForChunkAndReadyNeighbors(task.cx, task.cz);
        enqueueChunkNeighborhoodCull(task.cx, task.cz);
        chunkBuildQueue.splice(taskIndex, 1);
        chunkBuildQueuedKeys.delete(task.ck);
      }
    }
  }

  function loadChunk(cx, cz, opts = {}) {
    const { immediate = false, priority = 'normal' } = opts;
    const ck = chunkKey(cx, cz);
    if (loadedChunks.has(ck)) return;

    const group = new THREE.Group();
    group.matrixAutoUpdate = false;
    group.visible = true;
    scene.add(group);

    loadedChunks.set(ck, {
      cx,
      cz,
      group,
      blockKeys: new Set(),
      detailMeshes: new Set(),
      raycastObjects: new Set(),
      culledCount: 0,
      visible: true,
      visibleRetainUntil: performance.now() * 0.001,
      ready: !!immediate,
      detailReady: !!immediate
    });

    if (immediate) {
      const structureProfiles = getChunkStructureGenerationProfiles?.(cx, cz) || null;
      generateChunkColumns(cx, cz, ck, 0, CHUNK_SIZE * CHUNK_SIZE, {
        minDepth: 0,
        maxDepth: FULL_COLUMN_DEPTH,
        decorate: true,
        structureProfiles
      });
      applyManualBlocksInChunk(ck);
      queueFluidSeedsForChunkAndReadyNeighbors(cx, cz);
      enqueueChunkNeighborhoodCull(cx, cz);
      return;
    }

    if (!chunkBuildQueuedKeys.has(ck)) {
      const task = {
        cx,
        cz,
        ck,
        priority,
        structureProfiles: null,
        nextColumn: 0,
        mode: 'surface',
        minDepth: 0,
        maxDepth: SURFACE_COLUMN_DEPTH,
        decorate: true
      };
      if (priority === 'high') chunkBuildQueue.unshift(task);
      else chunkBuildQueue.push(task);
      chunkBuildQueuedKeys.add(ck);
    }
  }

  function unloadChunk(ck) {
    const chunk = loadedChunks.get(ck);
    if (!chunk) return;

    const keys = Array.from(chunk.blockKeys);
    for (const key of keys) {
      const mesh = solidBlocks.get(key);
      if (mesh) {
        removeBlock(mesh, { trackModification: false, skipOcclusionRefresh: true });
      } else {
        culledBlocks.delete(key);
      }
    }

    if (chunk.group?.parent) {
      chunk.group.parent.remove(chunk.group);
    }

    const instancedBuckets = instancedBlockBucketsByChunk.get(ck);
    if (instancedBuckets) {
      for (const bucket of instancedBuckets.values()) {
        if (!bucket?.mesh) continue;
        chunk.raycastObjects.delete(bucket.mesh);
        removeRaycastObject(bucket.mesh);
        instancedMeshBucket.delete(bucket.mesh);
        bucket.mesh.dispose?.();
        for (const key of bucket.keys) {
          if (!key) continue;
          instancedBlockIndexByKey.delete(key);
          instancedBlockMetaByKey.delete(key);
        }
      }
      instancedBlockBucketsByChunk.delete(ck);
    }

    loadedChunks.delete(ck);
    if (chunkBuildQueuedKeys.has(ck)) {
      chunkBuildQueuedKeys.delete(ck);
      for (let i = chunkBuildQueue.length - 1; i >= 0; i--) {
        if (chunkBuildQueue[i].ck === ck) chunkBuildQueue.splice(i, 1);
      }
    }
    pendingCullChunkKeys.delete(ck);
  }

  function rebuildChunkQueue(cx, cz, playerCx = cx, playerCz = cz) {
    centerChunkX = cx;
    centerChunkZ = cz;
    chunkQueue.length = 0;
    queuedChunkKeys.clear();

    const pending = [];
    for (let dz = -activeViewDistance; dz <= activeViewDistance; dz++) {
      for (let dx = -activeViewDistance; dx <= activeViewDistance; dx++) {
        const qx = cx + dx;
        const qz = cz + dz;
        const ck = chunkKey(qx, qz);
        if (!loadedChunks.has(ck)) {
          const playerDx = qx - playerCx;
          const playerDz = qz - playerCz;
          pending.push({
            cx: qx,
            cz: qz,
            ck,
            playerRingDist: Math.max(Math.abs(playerDx), Math.abs(playerDz)),
            playerDistSq: (playerDx * playerDx) + (playerDz * playerDz),
            streamDistSq: dx * dx + dz * dz
          });
        }
      }
    }

    const unloadKeys = [];
    for (const [ck, chunk] of loadedChunks.entries()) {
      if (Math.abs(chunk.cx - playerCx) > activeUnloadDistance || Math.abs(chunk.cz - playerCz) > activeUnloadDistance) {
        unloadKeys.push(ck);
      }
    }
    for (const ck of unloadKeys) unloadChunk(ck);

    pending.sort((a, b) => {
      if (a.playerRingDist !== b.playerRingDist) return a.playerRingDist - b.playerRingDist;
      if (a.playerDistSq !== b.playerDistSq) return a.playerDistSq - b.playerDistSq;
      return a.streamDistSq - b.streamDistSq;
    });
    for (const item of pending) {
      chunkQueue.push(item);
      queuedChunkKeys.add(item.ck);
    }
  }

  function ensureCriticalChunksAroundPlayer(playerPos, radius = 1) {
    const centerCx = worldToChunk(playerPos.x);
    const centerCz = worldToChunk(playerPos.z);
    let needsCatchup = false;
    const missing = [];

    for (let dz = -radius; dz <= radius; dz++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const cx = centerCx + dx;
        const cz = centerCz + dz;
        const ck = chunkKey(cx, cz);

        if (!loadedChunks.has(ck)) {
          missing.push({ cx, cz, ck, distSq: (dx * dx) + (dz * dz) });
          needsCatchup = true;
          continue;
        }

        const chunk = loadedChunks.get(ck);
        if (!chunk?.ready) needsCatchup = true;
      }
    }

    if (missing.length > 0) {
      missing.sort((a, b) => a.distSq - b.distSq);
      for (const chunk of missing) {
        loadChunk(chunk.cx, chunk.cz, { priority: 'high' });
      }
    }

    return needsCatchup;
  }

  function setChunkVisible(chunk, visible) {
    if (!chunk || chunk.visible === visible) return;
    chunk.visible = visible;
    if (chunk.group) {
      chunk.group.visible = visible;
      return;
    }
    for (const key of chunk.blockKeys) {
      const mesh = solidBlocks.get(key);
      if (!mesh) continue;
      mesh.visible = visible;
    }
  }

  function computeDetailMeshVisibility(mesh, playerPos, forwardX, forwardZ, nowSeconds) {
    const maxDistance = Number(mesh?.userData?.detailCullDistance);
    if (!(maxDistance > 0) || !playerPos) return true;

    const anchorX = Number.isFinite(Number(mesh?.userData?.detailAnchorX))
      ? Number(mesh.userData.detailAnchorX)
      : mesh.position.x;
    const anchorZ = Number.isFinite(Number(mesh?.userData?.detailAnchorZ))
      ? Number(mesh.userData.detailAnchorZ)
      : mesh.position.z;
    const ox = anchorX - playerPos.x;
    const oz = anchorZ - playerPos.z;
    const flatDist = Math.hypot(ox, oz);
    if (flatDist <= DETAIL_VISIBILITY_ALWAYS_DISTANCE) {
      mesh.userData.detailVisibleRetainUntil = nowSeconds + DETAIL_VISIBILITY_RETAIN_SECONDS;
      return true;
    }
    if (flatDist > maxDistance) {
      return (Number(mesh.userData.detailVisibleRetainUntil) || 0) > nowSeconds
        && flatDist <= maxDistance + DETAIL_VISIBILITY_RETAIN_MARGIN;
    }

    const dirLen = Math.hypot(forwardX, forwardZ);
    if (dirLen <= 0.0001 || flatDist <= 0.0001) {
      mesh.userData.detailVisibleRetainUntil = nowSeconds + DETAIL_VISIBILITY_RETAIN_SECONDS;
      return true;
    }

    const alignment = ((ox * forwardX) + (oz * forwardZ)) / flatDist;
    const distRatio = clamp(
      (flatDist - DETAIL_VISIBILITY_ALWAYS_DISTANCE) / Math.max(1, maxDistance - DETAIL_VISIBILITY_ALWAYS_DISTANCE),
      0,
      1
    );
    const minDot = lerp(-0.25, 0.22, distRatio);
    const hysteresis = mesh.visible !== false ? DETAIL_VISIBILITY_HYSTERESIS_DOT : 0;
    if (alignment >= (minDot - hysteresis)) {
      mesh.userData.detailVisibleRetainUntil = nowSeconds + DETAIL_VISIBILITY_RETAIN_SECONDS;
      return true;
    }

    return (Number(mesh.userData.detailVisibleRetainUntil) || 0) > nowSeconds
      && flatDist <= maxDistance + DETAIL_VISIBILITY_RETAIN_MARGIN;
  }

  function updateChunkDetailVisibility(chunk, playerPos, forwardX, forwardZ, nowSeconds) {
    if (!chunk?.detailMeshes || chunk.detailMeshes.size === 0) return;
    const chunkVisible = chunk.visible !== false;
    for (const mesh of chunk.detailMeshes) {
      if (!mesh?.isMesh) continue;
      mesh.visible = chunkVisible && computeDetailMeshVisibility(
        mesh,
        playerPos,
        forwardX,
        forwardZ,
        nowSeconds
      );
    }
  }

  function computeChunkDirectionalVisibility(chunk, playerPos, forwardX, forwardZ, viewDistanceLimit, nowSeconds) {
    const centerX = (chunk.cx * CHUNK_SIZE) + CHUNK_SIZE * 0.5;
    const centerZ = (chunk.cz * CHUNK_SIZE) + CHUNK_SIZE * 0.5;
    const ox = centerX - playerPos.x;
    const oz = centerZ - playerPos.z;
    const flatDist = Math.hypot(ox, oz);
    if (flatDist > viewDistanceLimit) return false;

    const playerCx = worldToChunk(playerPos.x);
    const playerCz = worldToChunk(playerPos.z);
    if (
      Math.abs(chunk.cx - playerCx) <= CHUNK_VISIBILITY_ALWAYS_RING
      && Math.abs(chunk.cz - playerCz) <= CHUNK_VISIBILITY_ALWAYS_RING
    ) {
      chunk.visibleRetainUntil = nowSeconds + CHUNK_VISIBILITY_RETAIN_SECONDS;
      return true;
    }

    if (flatDist <= CHUNK_VISIBILITY_NEAR_DISTANCE) {
      chunk.visibleRetainUntil = nowSeconds + CHUNK_VISIBILITY_RETAIN_SECONDS;
      return true;
    }

    const dirLen = Math.hypot(forwardX, forwardZ);
    if (dirLen <= 0.0001 || flatDist <= 0.0001) {
      chunk.visibleRetainUntil = nowSeconds + CHUNK_VISIBILITY_RETAIN_SECONDS;
      return true;
    }

    const alignment = ((ox * forwardX) + (oz * forwardZ)) / flatDist;
    const distRatio = clamp(
      (flatDist - CHUNK_VISIBILITY_NEAR_DISTANCE) / Math.max(1, viewDistanceLimit - CHUNK_VISIBILITY_NEAR_DISTANCE),
      0,
      1
    );
    const minDot = lerp(-0.7, 0.2, distRatio);
    const hysteresis = chunk.visible !== false ? CHUNK_VISIBILITY_HYSTERESIS_DOT : 0;
    if (alignment >= (minDot - hysteresis)) {
      chunk.visibleRetainUntil = nowSeconds + CHUNK_VISIBILITY_RETAIN_SECONDS;
      return true;
    }

    return (Number(chunk.visibleRetainUntil) || 0) > nowSeconds
      && flatDist <= Math.max(CHUNK_VISIBILITY_NEAR_DISTANCE, viewDistanceLimit - CHUNK_VISIBILITY_RETAIN_MARGIN);
  }

  function updateChunkVisibility(playerPos, viewDir = null, delta = 0.016) {
    if (!playerPos) return;
    const dirXRaw = Number(viewDir?.x) || 0;
    const dirZRaw = Number(viewDir?.z) || 0;
    const dirLen = Math.hypot(dirXRaw, dirZRaw);
    const forwardX = dirLen > 0.0001 ? dirXRaw / dirLen : 0;
    const forwardZ = dirLen > 0.0001 ? dirZRaw / dirLen : 1;

    const moved = Number.isFinite(lastVisibilitySampleX)
      ? Math.hypot(playerPos.x - lastVisibilitySampleX, playerPos.z - lastVisibilitySampleZ)
      : Infinity;
    const turnDelta = Number.isFinite(lastVisibilityForwardX)
      ? Math.abs((forwardX * lastVisibilityForwardX) + (forwardZ * lastVisibilityForwardZ) - 1)
      : Infinity;

    chunkVisibilityTimer = Math.max(0, chunkVisibilityTimer - Math.max(0, delta));
    if (chunkVisibilityTimer > 0 && moved < 1.15 && turnDelta < 0.035) return;
    chunkVisibilityTimer = moved > 4.0 || turnDelta > 0.16 ? 0.04 : 0.14;

    lastVisibilitySampleX = playerPos.x;
    lastVisibilitySampleZ = playerPos.z;
    lastVisibilityForwardX = forwardX;
    lastVisibilityForwardZ = forwardZ;

    const viewDistanceLimit = (activeViewDistance + 0.4) * CHUNK_SIZE;
    const nowSeconds = performance.now() * 0.001;
    for (const chunk of loadedChunks.values()) {
      const visible = computeChunkDirectionalVisibility(
        chunk,
        playerPos,
        forwardX,
        forwardZ,
        viewDistanceLimit,
        nowSeconds
      );
      setChunkVisible(chunk, visible);
      updateChunkDetailVisibility(chunk, playerPos, forwardX, forwardZ, nowSeconds);
    }
  }

  function updateStreaming(playerPos, delta = 0.016, buildBudget = CHUNK_BUILD_COLUMNS_PER_FRAME, opts = {}) {
    const targetViewDistance = normalizeViewDistance(opts?.viewDistance);
    const viewDistanceChanged = targetViewDistance !== activeViewDistance;
    if (viewDistanceChanged) {
      activeViewDistance = targetViewDistance;
      activeUnloadDistance = activeViewDistance + 1;
    }
    updatePrefetchTuningForViewDistance(activeViewDistance);

    const criticalNeedsCatchup = ensureCriticalChunksAroundPlayer(playerPos, CRITICAL_CHUNK_RADIUS);

    const streamCenter = getBufferedStreamCenter(playerPos);
    if (viewDistanceChanged || streamCenter.cx !== centerChunkX || streamCenter.cz !== centerChunkZ) {
      if (columnPrefetch?.prefetchNeighborhood) {
        columnPrefetch.prefetchNeighborhood(streamCenter.cx, streamCenter.cz, activeViewDistance + 1);
      }
      rebuildChunkQueue(streamCenter.cx, streamCenter.cz, streamCenter.playerCx, streamCenter.playerCz);
    }

    const chunkLoadsThisFrame = MAX_CHUNKS_PER_FRAME
      + Math.max(0, activeViewDistance - VIEW_DISTANCE)
      + (criticalNeedsCatchup ? 1 : 0);
    for (let i = 0; i < chunkLoadsThisFrame && chunkQueue.length > 0; i++) {
      const next = chunkQueue.shift();
      queuedChunkKeys.delete(next.ck);
      loadChunk(next.cx, next.cz);
    }

    const effectiveBudget = Math.max(4, Math.floor(Number.isFinite(buildBudget) ? buildBudget : CHUNK_BUILD_COLUMNS_PER_FRAME));
    const boostedBudget = criticalNeedsCatchup
      ? Math.min(
        Math.max(
          effectiveBudget + CRITICAL_BUILD_BUDGET_MIN_EXTRA,
          Math.floor(effectiveBudget * CRITICAL_BUILD_BUDGET_SCALE)
        ),
        effectiveBudget + CRITICAL_BUILD_BUDGET_MAX_EXTRA
      )
      : effectiveBudget;
    processChunkBuildQueue(boostedBudget, {
      playerPos,
      preferredRadius: criticalNeedsCatchup ? CRITICAL_CHUNK_RADIUS : STREAM_SURFACE_PRIORITY_RADIUS,
      prioritizeSurface: true,
      allowDeep: !criticalNeedsCatchup
    });
    const cullBudget = criticalNeedsCatchup
      ? Math.max(1, Math.floor(effectiveBudget * 0.08))
      : Math.max(2, Math.floor(effectiveBudget * 0.16));
    processPendingChunkCulls(cullBudget);

    updateFallingGravityBlocks(delta);

    leafDecayTimer += Math.max(0, delta);
    if (leafDecayTimer >= LEAF_DECAY_INTERVAL) {
      let ticks = Math.floor(leafDecayTimer / LEAF_DECAY_INTERVAL);
      leafDecayTimer -= ticks * LEAF_DECAY_INTERVAL;
      ticks = Math.min(ticks, 2);
      for (let i = 0; i < ticks; i++) processLeafDecayBatch();
    }

    saplingResyncTimer += Math.max(0, delta);
    if (saplingResyncTimer >= SAPLING_RESYNC_INTERVAL) {
      saplingResyncTimer = 0;
      resyncNearbySaplings(playerPos);
    }

    saplingGrowthTimer += Math.max(0, delta);
    if (saplingGrowthTimer >= SAPLING_GROWTH_INTERVAL) {
      let ticks = Math.floor(saplingGrowthTimer / SAPLING_GROWTH_INTERVAL);
      saplingGrowthTimer -= ticks * SAPLING_GROWTH_INTERVAL;
      ticks = Math.min(ticks, 3);
      for (let i = 0; i < ticks; i++) processSaplingGrowthBatch(SAPLING_GROWTH_INTERVAL);
    }
  }

  function generateTerrain(startPos = { x: 0, z: 0 }) {
    const cx = worldToChunk(startPos.x);
    const cz = worldToChunk(startPos.z);

    updatePrefetchTuningForViewDistance(activeViewDistance);
    if (columnPrefetch?.clear) columnPrefetch.clear();
    if (columnPrefetch?.prefetchNeighborhood) {
      columnPrefetch.prefetchNeighborhood(cx, cz, Math.max(INITIAL_RADIUS + 2, activeViewDistance + 1));
    }

    lastStreamSampleX = startPos.x;
    lastStreamSampleZ = startPos.z;

    for (let dz = -INITIAL_RADIUS; dz <= INITIAL_RADIUS; dz++) {
      for (let dx = -INITIAL_RADIUS; dx <= INITIAL_RADIUS; dx++) {
        loadChunk(cx + dx, cz + dz, { immediate: true });
      }
    }
    rebuildChunkQueue(cx, cz, cx, cz);
  }

  function getSurfaceHeight(x, z) {
    const gx = worldToBlock(x);
    const gz = worldToBlock(z);
    const dimension = getDimensionAtCoord(gx, gz);
    if (dimension === 'nether') {
      const info = getPrefetchedColumnInfoAt(gx, gz, 'nether') || netherColumnNoise(gx, gz);
      return info.height;
    }
    if (dimension === 'end') {
      const info = getPrefetchedColumnInfoAt(gx, gz, 'end') || endColumnNoise(gx, gz);
      return info.height;
    }
    const info = getPrefetchedColumnInfoAt(gx, gz, 'overworld') || columnNoise(gx, gz);
    return info.height;
  }

  function getEcosystemSnapshotAt(x, z) {
    const gx = worldToBlock(x);
    const gz = worldToBlock(z);
    if (getDimensionAtCoord(gx, gz) !== 'overworld') return null;

    const info = getPrefetchedColumnInfoAt(gx, gz, 'overworld') || columnNoise(gx, gz);
    const settlement = getSettlementSnapshotAt(gx, gz);
    const poi = getPointOfInterestSnapshotAt(gx, gz);
    const shipwreck = getShipwreckSnapshotAt(gx, gz);
    const skyFeature = getSkyFeatureSnapshotAt(gx, gz);

    return {
      biome: info.biome,
      terrain: info.terrain || 'plains',
      humidity: info.humidity,
      surfaceY: info.height,
      settlement,
      poi,
      shipwreck,
      skyFeature,
      threat: Math.max(0, poi?.threat || 0, shipwreck ? 1 : 0, skyFeature?.type === 'sky_city' ? 1 : 0)
    };
  }

  function hasRaycastObject(mesh) {
    if (objectSet.has(mesh)) return true;
    if (!isInstancedBlockProxy(mesh)) return false;
    const key = mesh.userData?.blockKey;
    if (!key) return false;
    const mapped = solidBlocks.get(key);
    if (mapped === mesh) return true;
    if (mapped?.userData?.instancedProxy && mapped.userData.blockKey === key) return true;
    if (!resolveInstancedSlotByKey(key)) return false;
    solidBlocks.set(key, mesh);
    culledBlocks.delete(key);
    const chunk = mesh.userData?.chunkKey ? loadedChunks.get(mesh.userData.chunkKey) : null;
    if (chunk) chunk.blockKeys.add(key);
    return true;
  }

  function repairChunkRaycastObjects(chunk, now = 0) {
    if (!chunk?.group || !chunk?.raycastObjects) return;
    const lastRepairAt = Number(chunk.raycastRepairAt) || 0;
    if ((now - lastRepairAt) < CHUNK_RAYCAST_REPAIR_COOLDOWN_MS) return;
    chunk.raycastRepairAt = now;

    for (const child of chunk.group.children) {
      if (!child?.isMesh) continue;
      const userData = child.userData || {};
      const isInstancedBucket = !!userData.instancedBlockBucket;
      const isBlockMesh = userData.id !== undefined && userData.raycast !== false;
      if (!isInstancedBucket && !isBlockMesh) continue;
      if (!chunk.raycastObjects.has(child)) chunk.raycastObjects.add(child);
      if ((isInstancedBucket || userData.raycast !== false) && !objectSet.has(child)) {
        addRaycastObject(child);
      }
    }
  }

  function getRaycastObjectsAround(position, maxDist = 6) {
    if (!position) return objects;

    raycastScratch.length = 0;
    const now = performance.now();
    const castRange = Math.max(0.5, maxDist) + 1.15;
    const castRangeSq = castRange * castRange;
    const verticalLimit = Math.max(4, maxDist * 1.25 + 1.0);
    const radiusChunks = Math.max(1, Math.ceil(castRange / CHUNK_SIZE));
    const cx = worldToChunk(position.x);
    const cz = worldToChunk(position.z);

    for (let dz = -radiusChunks; dz <= radiusChunks; dz++) {
      for (let dx = -radiusChunks; dx <= radiusChunks; dx++) {
        const chunk = loadedChunks.get(chunkKey(cx + dx, cz + dz));
        if (!chunk) continue;
        repairChunkRaycastObjects(chunk, now);
        for (const mesh of chunk.raycastObjects) {
          if (mesh?.userData?.instancedBlockBucket) {
            raycastScratch.push(mesh);
            continue;
          }
          const dy = mesh.position.y - position.y;
          if (Math.abs(dy) > verticalLimit) continue;
          const ox = mesh.position.x - position.x;
          const oz = mesh.position.z - position.z;
          if ((ox * ox) + (dy * dy) + (oz * oz) > castRangeSq) continue;
          raycastScratch.push(mesh);
        }
      }
    }

    const nearbyDynamicObjects = getDynamicRaycastObjectsAround(position, maxDist);
    for (const mesh of nearbyDynamicObjects) raycastScratch.push(mesh);

    return raycastScratch;
  }

  function getDynamicRaycastObjectsAround(position, maxDist = 6) {
    dynamicRaycastScratch.length = 0;
    if (!position) {
      for (const mesh of dynamicRaycastObjects) dynamicRaycastScratch.push(mesh);
      return dynamicRaycastScratch;
    }
    const castRange = Math.max(0.05, maxDist) + 1.15;
    const castRangeSq = castRange * castRange;
    const verticalLimit = Math.max(4, maxDist * 1.25 + 1.0);

    for (const mesh of dynamicRaycastObjects) {
      if (!mesh) continue;
      const dy = mesh.position.y - position.y;
      if (Math.abs(dy) > verticalLimit) continue;
      const ox = mesh.position.x - position.x;
      const oz = mesh.position.z - position.z;
      if ((ox * ox) + (dy * dy) + (oz * oz) > castRangeSq) continue;
      dynamicRaycastScratch.push(mesh);
    }

    return dynamicRaycastScratch;
  }

  function getPerformanceSnapshot() {
    let visibleChunks = 0;
    let instancedBuckets = 0;
    let manualSaplings = 0;
    let visibleFenceMeshes = 0;
    let totalFenceMeshes = 0;
    let visibleLeafBuckets = 0;
    let totalLeafBuckets = 0;
    let visibleLeafInstances = 0;
    let totalLeafInstances = 0;
    let activeDropUnits = 0;
    let sleepingDrops = 0;
    for (const drop of drops) {
      const userData = drop?.userData || null;
      activeDropUnits += Math.max(1, Math.floor(Number(userData?.stackCount) || 1));
      if (userData?.sleeping) sleepingDrops += 1;
    }
    for (const chunk of loadedChunks.values()) {
      const chunkVisible = chunk?.visible !== false;
      if (chunkVisible) visibleChunks += 1;
      if (chunk?.detailMeshes?.size) {
        for (const mesh of chunk.detailMeshes) {
          const detailTypeId = Number(mesh?.userData?.baseTypeId ?? mesh?.userData?.id);
          if (detailTypeId !== 20) continue;
          const fenceCount = Math.max(1, Number(mesh?.count) || 1);
          totalFenceMeshes += fenceCount;
          if (chunkVisible && mesh.visible !== false) visibleFenceMeshes += fenceCount;
        }
      }
    }
    for (const buckets of instancedBlockBucketsByChunk.values()) {
      instancedBuckets += buckets.size;
      for (const bucket of buckets.values()) {
        const leafTypeId = Number(bucket?.mesh?.userData?.baseTypeId ?? parseInstancedTypeId(bucket?.typeId));
        if (leafTypeId !== 4) continue;
        totalLeafBuckets += 1;
        totalLeafInstances += bucket.count || 0;
        const chunkVisible = loadedChunks.get(bucket.ck)?.visible !== false;
        if (chunkVisible && bucket.mesh?.visible !== false) {
          visibleLeafBuckets += 1;
          visibleLeafInstances += bucket.count || 0;
        }
      }
    }
    for (const entry of manualPlaced.values()) {
      if (Number(entry?.id) === SAPLING_BLOCK_ID) manualSaplings += 1;
    }
    return {
      loadedChunks: loadedChunks.size,
      visibleChunks,
      streamViewDistance: activeViewDistance,
      chunkBuildQueued: chunkBuildQueue.length,
      activeDrops: drops.length,
      activeDropUnits,
      sleepingDrops,
      pooledDrops: dropPool.length,
      raycastObjects: objects.length,
      instancedBlocks: instancedBlockIndexByKey.size,
      instancedBuckets,
      dirtyWaterCells: fluidState.dirtyWaterSet.size,
      dirtyLavaCells: fluidState.dirtyLavaSet.size,
      pendingWaterSeedCells: fluidState.getPendingWaterSeedCount(),
      pendingLavaSeedCells: fluidState.getPendingLavaSeedCount(),
      fluidTier: fluidState.runtimeFluidPerformanceTier,
      fluidWaterSeedBudget: fluidState.runtimeWaterSeedBudget,
      fluidLavaSeedBudget: fluidState.runtimeLavaSeedBudget,
      fluidWaterDirtyBudget: fluidState.runtimeWaterDirtyBudget,
      fluidLavaDirtyBudget: fluidState.runtimeLavaDirtyBudget,
      fluidWaterVisualBudget: fluidState.runtimeWaterVisualBudget,
      fluidWaterVisualProcessed: fluidState.runtimeWaterVisualProcessed,
      fluidWaterVisualDeferred: fluidState.runtimeWaterVisualDeferred,
      waterAnimatingMeshes: animatingWaterMeshes.size,
      fluidFocusRadius: fluidState.runtimeFluidFocusRadius,
      culledBlocks: culledBlocks.size,
      visibleFenceMeshes,
      totalFenceMeshes,
      visibleLeafBuckets,
      totalLeafBuckets,
      visibleLeafInstances,
      totalLeafInstances,
      manualSaplings,
      saplingsGrowing: saplingGrowthProgress.size,
      activePointLights: activeLightMeshes.length,
      deferredPointLights: deferredLightMeshes.length,
      prefetch: columnPrefetch?.getStats?.() || null
    };
  }

  return {
    blockData,
    objects,
    hasRaycastObject,
    registerDynamicRaycastObject,
    unregisterDynamicRaycastObject,
    resolveRaycastHit: resolveRaycastHitObject,
    getRaycastObjectsAround,
    getDynamicRaycastObjectsAround,
    getBlockAt,
    getRenderableBlockAt,
    getBlockIdAt,
    getWorldMinY() {
      return WORLD_MIN_Y;
    },
    isSolidAt,
    isSolidForCollisionAt,
    isWaterAt,
    isLavaAt,
    canSupportTorchAt: hasTorchSupport,
    canPlaceAt,
    createBlock: placeBlock,
    removeBlock,
    igniteAt,
    spawnDrop,
    updateDrops,
    updateFire,
    updateCacti,
    updateWaterFlow,
    growSaplingAt(x, y, z) {
      return tryGrowSaplingAt(x, y, z);
    },
    settleGravityAround,
    exportManualState,
    importManualState,
    reloadTerrain,
    updateStreaming,
    updateChunkVisibility,
    refreshLightBudget,
    getPerformanceSnapshot,
    generateTerrain,
    getSurfaceHeight,
    getSettlementAt: getSettlementSnapshotAt,
    getPointOfInterestAt: getPointOfInterestSnapshotAt,
    getShipwreckAt: getShipwreckSnapshotAt,
    getSkyFeatureAt: getSkyFeatureSnapshotAt,
    getEcosystemAt: getEcosystemSnapshotAt,
    getDimensionAt(x, z) {
      return getDimensionAtCoord(worldToBlock(x), worldToBlock(z));
    },
    toDimensionX,
    toLocalX,
    getNetherXOffset() {
      return NETHER_X_OFFSET;
    },
    getEndXOffset() {
      return END_X_OFFSET;
    },
    getWaterLevel() {
      return WATER_LEVEL;
    },
    getWorldSeed() {
      return WORLD_SEED;
    },
    getTextureStyle() {
      return activeTextureStyle;
    },
    getTextureStyleOptions() {
      return [...TEXTURE_STYLE_OPTIONS];
    },
    setTextureStyle(styleId) {
      return setTextureStyleInternal(styleId);
    },
    getSaplingGrowthRangeSeconds() {
      return {
        min: SAPLING_GROWTH_MIN_SECONDS,
        max: SAPLING_GROWTH_MAX_SECONDS
      };
    }
  };
}
